<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template>
	<div v-if="initialized && cfdocument.identifier" @click="framework_viewer_clicked">
		<k-live-region :message="a11y_status_message" />
		<vue-draggable-resizable :drag-handle="'.k-case-tree-title'" :resizable="!maximized" :draggable="!maximized" :class-name="class_name" @resizing="tree_resized" :z="maximized ? 10 : 100" :w="viewer_width" :h="viewer_height" :active="!maximized" :preventDeactivation="true" :handles="['br']" class-name-handle="k-resizable-handle" :enable-native-drag="true">
			<div class="k-case-tree-top" :data-case-tree-top-identifier="lsdoc_identifier" :style="case_tree_top_style">
				<div class="k-case-tree-top-inner">
					<div class="k-case-tree-title">
						<k-btn v-if="!embedded_mode && !wiki_mode_view && !($vuetify.breakpoint.xsOnly && show_search_bar)" icon :color="U.get_contrast_color(U.framework_color(framework_record.json.CFDocument.identifier))" :accessible-label="maximized ? 'Collapse framework viewer' : 'Close framework viewer'" @click="hide_tree"><k-icon>fas {{ maximized ? 'fa-arrow-circle-left' : 'fa-times-circle' }}</k-icon></k-btn>
						<FrameworkSwitcher2 
							v-if="!wiki_mode_view && allow_framework_switching && !($vuetify.breakpoint.xsOnly && show_search_bar)"
							btn_size="small"
							:color="U.get_contrast_color(U.framework_color(framework_record.json.CFDocument.identifier))"
							@framework_selected="set_framework_from_switcher" 
						/>
						<div v-show="show_framework_title" class="k-case-tree-title-inner ml-1" :class="doc_title_css" :aria-label="frameworkTitleText" v-html="case_tree_title" :style="{ color: U.get_contrast_color(U.framework_color(framework_record.json.CFDocument.identifier)) }"></div>
						<k-tooltip top><template v-slot:activator="{on}"><k-icon v-on="on" v-if="is_private" small class="ml-2"  color="#fff">fas fa-user-secret</k-icon></template><div class="text-center">This framework is PRIVATE:<br>it will only be visible to explicitly-authorized users.</div></k-tooltip>
						<k-menu right v-model="sandbox_menu_showing" accessible-popover accessible-label="Sandbox framework details" content-id="framework-viewer-sandbox-panel"><template v-slot:activator="{ on, attrs }"><k-btn v-if="is_sandbox" v-show="show_framework_title" v-on="on" v-bind="attrs" fab x-small color="#fff" class="ml-2" :accessible-label="`Show sandbox details for ${frameworkTitleText}`"><k-icon small>fas fa-umbrella-beach</k-icon></k-btn></template>
							<k-list dense>
								<k-list-item><k-list-item-icon><k-icon class="mr-2">fas fa-umbrella-beach</k-icon></k-list-item-icon><k-list-item-title>This is a “sandbox” copy of the original framework<br />created at {{ sandbox_creation_date }}</k-list-item-title></k-list-item>
								<k-list-item @click="open_original_of_sandbox"><k-list-item-title class="text-right">Switch to the original framework <k-icon class="ml-2">fas fa-circle-arrow-right</k-icon></k-list-item-title></k-list-item>
								<k-list-item
									v-if="user_can_admin && editing_enabled"
									@click="show_apply_sandbox = true"
								>
									<k-list-item-title class="text-right">
										Apply sandbox changes to the original framework
										<k-icon class="ml-2">fas fa-bolt</k-icon>
									</k-list-item-title>
								</k-list-item>
								<k-list-item v-if="user_can_admin && !editing_enabled">
									<k-list-item-title
										class="text-right mt-3"
										style="font-size: 14px"
									>
										(To apply sandbox changes to the original framework,<br />you must first turn on editing mode)
									</k-list-item-title>
								</k-list-item>
							</k-list>
						</k-menu>
						<k-menu right v-model="derivative_menu_showing" accessible-popover accessible-label="Derivative framework details" content-id="framework-viewer-derivative-panel"><template v-slot:activator="{ on, attrs }"><k-btn v-if="is_derivative && user_can_edit" v-show="show_framework_title" v-on="on" v-bind="attrs" fab x-small color="#fff" class="ml-2" :accessible-label="`Show derivative details for ${frameworkTitleText}`"><k-icon small>fas fa-arrow-up-from-bracket</k-icon></k-btn></template>
							<k-list dense>
								<k-list-item><k-list-item-icon><k-icon class="mr-2">fas fa-arrow-up-from-bracket</k-icon></k-list-item-icon><k-list-item-title>This is a “derivative” copy of another framework</k-list-item-title></k-list-item>
								<k-list-item @click="open_original_of_derivative"><k-list-item-title class="text-right">Switch to the original framework <k-icon class="ml-2">fas fa-circle-arrow-right</k-icon></k-list-item-title></k-list-item>
							</k-list>
						</k-menu>
					</div>
					<k-spacer />
					<div v-if="show_rosetta_toggle">
						<k-btn small color="#fff" outlined class="mt-1 ml-2" @click="go_to_rosetta"><k-icon small class="mr-2">fas fa-cubes</k-icon>Rosetta</k-btn>
					</div>
					<k-btn v-if="(crosswalk_right_identifier && editing_enabled) || showing_crosswalk" @click="showing_crosswalk = !showing_crosswalk" color="white" class="mx-2" style="margin-top: 9px" x-small>{{ showing_crosswalk ? 'Return to Framework' : 'Return to Crosswalk Editor' }}</k-btn>
						<div v-show="show_search_bar" :style="!embedded_mode ? 'margin-left:8px' : ''" class="k-case-tree-search-outer" @click.stop=""> <!-- click.stop is needed here to prevent "ghosting" the search interface when you click near the search bar -->
							<div class="k-case-tree-search-results elevation-2" v-if="active_search_tree_keys.length > 0 && !showing_crosswalk && viewer_mode != 'table'">
							<k-btn small icon color="#fff" accessible-label="Go to previous search result" @click.stop="jump_to_search(-1)"><k-icon small color="#fff">fas fa-angle-left</k-icon></k-btn><!--
						--><k-btn small icon color="#fff" accessible-label="Go to next search result" @click.stop="jump_to_search(1)"><k-icon small color="#fff">fas fa-angle-right</k-icon></k-btn>
						</div>
						<k-hover v-slot:default="{ hover }">
							<k-text-field ref="framework_search_bar" light background-color="#fff" class="mx-1" solo hide-details dense
								accessible-label="Search framework items"
								placeholder="Search"
								v-model="search_terms"
								prepend-inner-icon="fas fa-search"
								prepend-decorative
								autocomplete="new-password"
								@focus="search_bar_focused"
								@blur="search_bar_blurred"
								@keydown="search_field_keydown"
								@click.stop=""
							>
								<template v-slot:append>
									<k-btn v-show="search_terms || $vuetify.breakpoint.xs || $vuetify.breakpoint.sm" icon x-small class="ml-2" :color="hover ? 'primary' : '#777'" style="margin-top: -2px" accessible-label="Clear search" @click.stop="clear_search_terms"><k-icon>fas fa-times-circle</k-icon></k-btn>
									<k-tooltip bottom><template v-slot:activator="{ on }"><k-btn v-on="on" v-show="(hover || search_terms) && !showing_crosswalk && !wiki_mode_view" icon x-small class="ml-2" :color="hover ? 'primary' : '#777'" style="margin-top: -2px" :accessible-label="advanced_search_showing ? 'Run basic search' : 'Run advanced search'" @click.stop="advanced_search_clicked"><k-icon>fas fa-robot</k-icon></k-btn></template>Advanced Search</k-tooltip>
									<k-btn v-show="(hover || search_terms) && !showing_crosswalk && viewer_mode != 'table' && !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm && !wiki_mode_view" icon x-small class="ml-2 k-help-icon-btn" color="light-blue" style="margin-top: -2px" accessible-label="Open search help" @click.stop="U.show_help('search')"><k-icon>fas fa-info-circle</k-icon></k-btn>
								</template>
							</k-text-field>
						</k-hover>
						<div v-if="search_panel_showing && !showing_crosswalk && viewer_mode != 'table'" class="k-case-tree-search-panel" :class="search_panel_css_class" role="region" aria-label="Framework search results" @click.stop="search_panel_to_front">
							<div class="k-case-tree-search-panel-screen"></div>
							<div class="k-case-tree-search-options" v-show="!search_results_panel_showing || $vuetify.breakpoint.smAndUp">
								<div class="my-3 d-flex align-center" style="pointer-events: auto" @click="search_filter_menu_area_clicked">
									<k-menu
										right
										nudge-top="-32"
										v-model="search_filter_menu_open"
										accessible-popover
										accessible-label="Search filters"
										content-id="framework-search-filters-panel"
									><template v-slot:activator="{ on, attrs }"><k-btn v-on="on" v-bind="attrs" x-small color="secondary" class="k-tight-btn ml-1" ref="search_filter_btn" accessible-label="Open search filters" @click.stop="search_filter_menu_clicked">Filters<k-icon small class="ml-1">fas fa-caret-down</k-icon></k-btn></template>
										<k-list width="400">
											<k-list-item><k-list-item-title @click.stop="">
												<div class="d-flex mt-1">
													<div class="grey--text text--darken-2 mt-2 ml-2" style="font-size: 16px"><b>Search filters:</b></div>
													<k-spacer />
													<div style="flex: 0 0 110px">
														<k-select background-color="#fff" v-model="search_grade_low" :items="grades" item-value="index" label="Grade Low" outlined dense hide-details :menu-props="{ top: true, dense: true }"></k-select>
													</div>
													<div class="ml-1" style="flex: 0 0 110px">
														<k-select background-color="#fff" v-model="search_grade_high" :items="grades" item-value="index" label="Grade High" outlined dense hide-details :menu-props="{ top: true, dense: true }"></k-select>
													</div>
												</div>
												<div style="flex: 1 1 auto" class="mt-3 mb-1">
													<k-select multiple background-color="#fff" v-model="search_field_types" :items="search_field_type_options" label="Fields" outlined small-chips deletable-chips hide-details :menu-props="{ offsetY: true, dense: true, closeOnClick: true }"></k-select>
												</div>
												<div style="flex: 1 1 auto" class="mt-3 mb-1">
													<k-select multiple background-color="#fff" v-model="search_item_types" :items="search_item_type_options" label="Item Types" outlined small-chips :deletable-chips="search_item_types[0] != all_item_types_option" hide-details :menu-props="{ offsetY: true, dense: true, closeOnClick: true }"></k-select>
												</div>
												<div style="flex: 1 1 auto" class="mt-3 mb-3 ml-3">
													<k-checkbox :label="'Include Rubrics'" v-model="can_search_rubrics" dense hide-details></k-checkbox>
												</div>
											</k-list-item-title></k-list-item>
										</k-list>
									</k-menu>
									<div class="ml-3" style="font-size: 14px; line-height: 18px" v-html="filter_description"></div>
									<k-spacer />
								</div>
								<div class="mt-1 mb-2 mx-auto" v-if="search_item_for_limit_descriptor">
									<div class="d-flex">
										<k-spacer />
										<k-checkbox class="mt-0 pt-0" v-model="search_limit_to_item" :disabled="search_item_for_limit_descriptor == ''" hide-details>
											<template v-slot:label><div style="font-size: 14px" :class="search_item_for_limit_descriptor ? '' : 'grey--text'">
												Limit search to children of<span v-if="!search_limit_to_item && search_item_for_limit_descriptor"> last</span> selected item<span v-if="search_item_for_limit_descriptor">:</span>
											</div></template>
										</k-checkbox>
										<k-spacer />
									</div>
									<div v-if="search_item_for_limit_descriptor" class="text-center">
										<b class="grey--text text--darken-1">{{ search_item_for_limit_descriptor }}</b>
									</div>
								</div>
							</div>
							<div v-if="search_results_panel_showing" class="k-case-tree-search-results-panel" :class="$vuetify.breakpoint.xsOnly ? 'elevation-4' : ''">
								<k-list dense>
									<k-list-item v-if="search_result_entries.length == 0"><k-spacer />
										<div style="white-space: normal; text-align: center">
											<i v-if="tree_keys_for_advanced_search.length > 0">
												A basic search of these terms returned no matches.<br />
												Adjust your <button type="button" class="k-case-tree-inline-btn" @click.stop="show_search_filters"><b>search filters</b></button> or 
												<nobr>try an <button type="button" class="k-case-tree-inline-btn" @click.stop="advanced_search_clicked"><b>advanced search</b><k-icon color="primary" small class="ml-1" style="margin-top: -3px">fas fa-robot</k-icon></button></nobr>
											</i>
											<i v-else>
												No items matched your search filters. <button type="button" class="k-case-tree-inline-btn" @click.stop="show_search_filters"><b>Adjust the filters</b></button> and try again.
											</i>
										</div>
										<k-spacer />
									</k-list-item>
									<k-list-item v-else class="pb-1">
										<div>
											<k-btn v-visible="search_results_page_index > 0" icon x-small accessible-label="Previous search results page" @click.stop="show_search_results_page('prev')"><k-icon>fas fa-arrow-circle-left</k-icon></k-btn>
										</div>
										<k-spacer />
										<div class="grey--text text--darken-2" :class="wiki_mode_view ? 'mt-6' : ''">
											<k-icon v-if="advanced_search_showing" small style="margin-top: -5px" class="mr-1">fas fa-robot</k-icon>
											<b style="font-size: 14px">Search Results<span v-if="max_search_results_page_index > 0"> (page {{ search_results_page_index + 1 }})</span></b>
										</div>
										<!-- <k-btn small icon class="ml-4" @click.stop="search_panel_to_back"><k-icon style="margin-top:-3px" small>fas fa-compress-alt</k-icon></k-btn> -->
										<k-spacer />
										<div>
											<k-btn v-visible="search_results_page_index < max_search_results_page_index" icon x-small accessible-label="Next search results page" @click.stop="show_search_results_page('next')"><k-icon>fas fa-arrow-circle-right</k-icon></k-btn>
										</div>
									</k-list-item>

									<div v-for="(o, i) in active_search_tree_keys" :key="i" v-show="show_item_in_search_panel(o)">
										<k-tooltip :left="!$vuetify.breakpoint.xsOnly" :nudge-right="$vuetify.breakpoint.xsOnly ? '' : '8'" :bottom="$vuetify.breakpoint.xsOnly" :nudge-top="$vuetify.breakpoint.xsOnly ? '12' : ''" content-class="k-case-tree-search-item-tooltip elevation-1" transition="none" :disabled="wiki_mode_view">
											<template v-slot:activator="{ on }">
												<k-hover v-slot:default="{ hover }"><k-list-item v-on="on" @click.stop="search_result_clicked(o)" :class="(o.tree_key == search_result_highlighted_tree_key) ? 'k-case-tree-advanced-search-last-clicked-result' : ''">
													<div v-show="hover">
														<CopyBtn size="small" :val="search_result_panel_copy_val(o)" color="primary" :label="searchResultCopyLabel(o)" />
													</div>
													<div class="mr-2" v-if="advanced_search_showing">
														<div style="width: 30px; margin-top: -1px">
															<k-progress-linear :value="o.entry.sim_score / 10" color="green" height="10"></k-progress-linear>
														</div>
													</div>
													<div class="d-flex" v-html="search_result_panel_html(o)"></div>
												</k-list-item></k-hover>
											</template>
											<div v-html="search_result_panel_tooltip_html(o)"></div>
										</k-tooltip>
									</div>
									<k-list-item v-if="!advanced_search_showing && tree_keys_for_advanced_search.length > 0 && search_result_entries.length > 0">
										<k-spacer />
										<div class="pt-2 mt-2 px-1" style="white-space: normal; text-align: center; border-top: 1px solid #999">
											<i>Try an <button type="button" class="k-case-tree-inline-btn" @click.stop="advanced_search_clicked"><b>advanced search</b><k-icon color="primary" small class="ml-1 mr-1" style="margin-top: -3px">fas fa-robot</k-icon></button> or <button type="button" class="k-case-tree-inline-btn" @click.stop="show_search_filters"><b>adjust search filters</b></button></i>
										</div>
										<k-spacer />
									</k-list-item>
								</k-list>
							</div>
						</div>
					</div>
					<k-btn v-show="!show_search_bar && !showing_crosswalk && viewer_mode != 'table'" x-small fab color="transparent" class="ml-2" accessible-label="Show framework search" @click.stop="reveal_search_bar"><k-icon color="white">fas fa-search</k-icon></k-btn>

					<k-menu
						:open-on-hover="false"
						:transition="false"
						bottom
						left
						v-model="kebab_menu_showing"
						accessible-popover
						accessible-label="Framework options"
						content-id="framework-viewer-options-panel"
					><template v-slot:activator="{ on, attrs }"><k-btn v-if="!embedded_mode && !(wiki_mode && !user_can_edit)" v-on="on" v-bind="attrs" icon :color="U.get_contrast_color(U.framework_color(framework_record.json.CFDocument.identifier))" class="ml-0" accessible-label="Open framework options"><k-icon>fas fa-ellipsis-v</k-icon></k-btn></template>
						<k-list min-width="250" dense>
							<k-list-item v-show="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm" @click="show_help"><k-list-item-icon><k-icon small color="light-blue accent-4">fas fa-circle-info</k-icon></k-list-item-icon><k-list-item-title class="light-blue--text text--accent-4">{{ $store.state.site_config.app_name }} Help</k-list-item-title></k-list-item>
							<k-list-item><k-list-item-title class="text-center">FRAMEWORK OPTIONS</k-list-item-title></k-list-item>
							<k-divider />

							<!-- <k-list-item @click="toggle_maximize_tree"><k-list-item-icon><k-icon small>fas {{maximized?'fa-compress':'fa-expand'}}</k-icon></k-list-item-icon><k-list-item-title>{{maximized?'Minimize':'Maximize'}} framework viewer</k-list-item-title></k-list-item> -->

							<k-list-item class="mt-1"><k-list-item-title><span class="mr-2">Viewer Mode:</span>
								<k-btn-toggle dense active-class="k-toggle-btn-active-class" class="k-toggle-btn" v-model="viewer_mode" mandatory>
									<k-btn small light value="tree"> Tree </k-btn>
									<k-btn small light value="tiles">Tiles</k-btn>
									<k-btn v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" small light value="table">Table</k-btn>
								</k-btn-toggle>
							</k-list-item-title></k-list-item>

							<!-- <k-list-item v-if="viewer_mode!='table'&&open_node_count>0" @click="collapse_all"><k-list-item-icon><k-icon small style="transform:rotate(-45deg)">fas fa-compress-alt</k-icon></k-list-item-icon><k-list-item-title>Collapse all</k-list-item-title></k-list-item> -->

							<!-- <k-list-item><k-list-item-icon><k-icon small>fas fa-font</k-icon></k-list-item-icon><k-list-item-title>
							Font Size:
							<k-btn icon x-small color="#777" class="ml-1" accessible-label="Decrease framework viewer text size" @click.stop="adjust_font_size(-1)" :disabled="font_size==-2"><k-icon>fas fa-minus-circle</k-icon></k-btn>
							<k-btn icon x-small color="#777" class="ml-1" accessible-label="Increase framework viewer text size" @click.stop="adjust_font_size(1)" :disabled="font_size==2"><k-icon>fas fa-plus-circle</k-icon></k-btn>
						</k-list-item-title></k-list-item> -->

							<k-list-item v-if="editing_enabled" @click="show_update_report('archives')" class="orange--text text--darken-2"><k-list-item-icon><k-icon small color="orange darken-2">fas fa-archive</k-icon></k-list-item-icon><k-list-item-title>Manage this framework's archives</k-list-item-title></k-list-item>

							<k-divider v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" />

							<k-menu
								:transition="false"
								offset-x
								left
								nudge-top="8"
								:open-on-hover="false"
								style="display: block"
								v-model="export_menu_showing"
								accessible-popover
								accessible-label="Export framework and copy framework links"
								content-id="framework-viewer-export-panel"
								:parent-menu-active="kebab_menu_showing"
							>
								<template v-slot:activator="{ on, attrs }"><k-list-item v-on="on" v-bind="attrs" style="cursor: pointer">
									<k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title>Export Framework / Copy Framework Link</k-list-item-title>
									<k-list-item-action class="justify-end"><k-icon small>fas fa-chevron-right</k-icon></k-list-item-action>
								</k-list-item></template>

								<k-list dense>
									<k-list-item v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" @click="export_framework_json"><k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title>Export CASE <b>JSON</b>…</k-list-item-title></k-list-item>
									<k-list-item v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" @click="export_canvas_spreadsheet"><k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title>Export to <b>Canvas</b> spreadsheet format…</k-list-item-title></k-list-item>
									<k-list-item v-if="enable_powerschool_export && !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" @click="export_for_powerschool"><k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title>Export for <b>PowerSchool</b> SIS</k-list-item-title></k-list-item>
									<k-list-item v-if="$store.state.enable_ap_export && signed_in && !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" @click="export_ap_spreadsheet"><k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title>Export to <b>AP spreadsheet</b> format…</k-list-item-title></k-list-item>
									<k-list-item v-if="$store.state.enable_ap_export && signed_in && !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" @click="export_ap_word"><k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title>Export to <b>AP Word</b> format…</k-list-item-title></k-list-item>
									<k-list-item v-if="site_config.enable_nc_ai_export && signed_in && !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" @click="export_nc_ai()"><k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title>Export to <b>NC AI</b> format…</k-list-item-title></k-list-item>
									<k-list-item v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" @click="viewer_mode = 'table'; kebab_menu_showing = false;"><k-list-item-icon><k-icon small>fas fa-file-export</k-icon></k-list-item-icon><k-list-item-title><i>Export framework data in <b>spreadsheet</b> form from the <b>Table View</b></i></k-list-item-title></k-list-item>
									<k-list-item @click="copy_shortcut_url"><k-list-item-icon><k-icon small>fas fa-link</k-icon></k-list-item-icon><k-list-item-title>Copy <b>{{ $store.state.site_config.browsing_interface_title }}</b> link</k-list-item-title></k-list-item>
									<k-list-item @click="copy_api_url"><k-list-item-icon><k-icon small>fas fa-link</k-icon></k-list-item-icon><k-list-item-title>Copy CASE package <b>API</b> URL</k-list-item-title></k-list-item>
								</k-list>
							</k-menu>

							<k-list-item v-if="bp_review_full_reports_available" @click="show_best_practices"><k-list-item-icon><k-icon small class="deep-purple--text text--darken-3">fas fa-certificate</k-icon></k-list-item-icon><k-list-item-title class="deep-purple--text text--darken-3">Best Practices Review</k-list-item-title></k-list-item>
							<k-list-item v-if="user_can_admin" @click="show_manage_framework_user_rights = true"><k-list-item-icon><k-icon small class="red--text text--darken-3">fas fa-users</k-icon></k-list-item-icon><k-list-item-title class="red--text text--darken-3">Manage user rights for framework…</k-list-item-title></k-list-item>

							<k-list-item class="mt-1" v-if="user_can_admin"><k-list-item-title>
								<span class="mr-2">CASE Displayed:</span>
								<k-btn-toggle dense active-class="k-toggle-btn-active-class" class="k-toggle-btn" v-model="case_version_displayed" mandatory>
									<k-btn class="k-nocaps-btn k-tight-btn" small light value="vext">vext</k-btn>
									<k-btn class="k-nocaps-btn k-tight-btn" small light value="v1p1">v1p1</k-btn>
									<k-btn class="k-nocaps-btn k-tight-btn" small light value="v1p0">v1p0</k-btn>
								</k-btn-toggle>
							</k-list-item-title></k-list-item>

							<k-divider v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm && user_can_edit" />

							<k-list-item v-if="user_can_edit && !editing_enabled && wiki_mode" @click="wiki_mode_force_normal_view = !wiki_mode_force_normal_view"><k-list-item-icon><k-icon small>fas fa-book</k-icon></k-list-item-icon><k-list-item-title>{{
								wiki_mode_force_normal_view
									? 'Wiki framework: Show Wiki preview'
									: 'Wiki framework: Show “normal” CASE view'
							}}</k-list-item-title></k-list-item>

							<k-list-item v-if="user_can_edit && !editing_enabled && !viewing_archive" @click="handle_check_out_for_editing"><k-list-item-icon><k-icon small>fas fa-edit</k-icon></k-list-item-icon><k-list-item-title><b>Enable framework editing</b></k-list-item-title></k-list-item>
							<k-list-item v-if="user_can_edit && editing_enabled" @click="check_in_for_editing(true)"><k-list-item-icon><k-icon small>fas fa-edit</k-icon></k-list-item-icon><k-list-item-title><b>Stop editing framework</b></k-list-item-title></k-list-item>
						</k-list>
					</k-menu>
					<!-- <k-btn icon color="#fff" style="margin-right:-2px" @click="toggle_maximize_tree"><k-icon>fas {{maximized?'fa-compress':'fa-expand'}}</k-icon></k-btn> -->
				</div>
				<div v-if="image_src && !is_sandbox" class="k-case-tree-framework-image-wrapper">
					<img :src="image_src" :alt="frameworkImageAlt" />
				</div>
				<div v-if="is_sandbox" class="k-case-tree-framework-image-wrapper">
					<k-icon style="font-size: 180px; line-height: 220px" color="#333">fas fa-umbrella-beach</k-icon>
				</div>
			</div>

			<div class="k-case-tree-main" v-if="cftree.children" v-show="!showing_crosswalk" :style="case_tree_main_style">
				<div class="k-case-tree-inner-wrapper">
					<!-- <div v-if="cftree.children && !has_items" class="mt-4 text-center"><i>This CASE document does not include any items</i></div> -->
					<CASEFVToolbar v-show="viewer_mode == 'tree' && !show_move_fn" :viewer="this" />

					<div v-if="viewer_mode == 'tree' || viewer_mode == 'tiles'" class="k-case-tree-inner-wrapper-2">
						<CASETree ref="main_viewer_tree"
							:framework_record="framework_record"
							:viewer="this"
							:show_checkbox_fn="show_checkbox_fn"
							:show_move_fn="show_move_fn"
							:show_chooser_fn="show_chooser_fn" 
							@tree_scrolled="tree_scrolled" 
						/>
					</div>

					<ItemsTable v-if="item_table_shown" v-show="viewer_mode == 'table' && viewer_table_mode == 'items'"
						:viewer="this"
						:framework_record="framework_record"
						@search_result_clicked="search_result_clicked"
						@switch_table="viewer_table_mode = 'associations'"
						@switch_to_tree="viewer_mode = 'tree'" 
					/>
					<AssociationsTable v-if="assoc_table_shown" v-show="viewer_mode == 'table' && viewer_table_mode == 'associations'"
						:viewer="this"
						:framework_record="framework_record"
						:hide_associations="hide_associations"
						@search_result_clicked="search_result_clicked"
						@switch_table="viewer_table_mode = 'items'"
						@switch_to_tree="viewer_mode = 'tree'" 
					/>
				</div>
			</div>

			<!--CROSSWALK-->
			<CrosswalkEditor v-if="crosswalk_right_identifier" v-show="showing_crosswalk" :crosswalk_lsdoc_identifiers="[lsdoc_identifier, crosswalk_right_identifier]" :viewer="this" />

			<div v-if="viewing_archive || tracking_changes" class="k-case-archive-view-ctl" :class="'k-case-archive-view-ctl-' + view_archive_fn">
				<k-btn v-if="tracking_changes" small icon color="#fff" class="mr-2" accessible-label="Copy archive link" @click="copy_archive_link"><k-icon>fas fa-link</k-icon></k-btn>
				<div style="overflow: hidden; text-overflow: ellipsis" class="mr-2">
					<nobr v-html="view_archive_ctl_text"></nobr>
				</div>
				<k-spacer />
				<k-menu v-if="tracking_changes" left z-index="100" v-model="track_changes_fields_menu_showing" accessible-popover accessible-label="Choose track changes fields" content-id="framework-viewer-track-changes-panel"><template v-slot:activator="{ on, attrs }"><k-btn v-on="on" v-bind="attrs" x-small color="#fff" class="mr-3" accessible-label="Choose which changes to show">Show changes to…</k-btn></template>
					<k-list dense>
						<k-list-item v-for="(mitem, key) in track_changes_field_descriptions" :key="key" @click.stop="set_track_changes_fields(key)"><k-list-item-title><k-icon small color="#555" class="mr-2" style="margin-top: -2px" v-visible="track_changes_fields[key]">fas fa-check-circle</k-icon><span v-html="mitem"></span></k-list-item-title></k-list-item>
					</k-list>
				</k-menu>

				<div class="text-center">
					<nobr>
						<k-btn small text color="white" class="k-tight-btn" @click="show_update_report('')"><k-icon small class="mr-2">fas fa-arrow-circle-left</k-icon>Update Report</k-btn>
						<k-btn small text color="white" class="k-tight-btn" @click="show_archive_table_view"><k-icon small class="mr-2">fas fa-table</k-icon>Show Changes in Table</k-btn>
						<k-btn v-if="viewing_archive" small text color="white" class="k-tight-btn" @click="return_to_current_version"><k-icon small class="mr-2">fas fa-times-circle</k-icon>Done viewing updates</k-btn>
						<k-btn v-if="tracking_changes" small text color="white" class="k-tight-btn" @click="cancel_track_changes"><k-icon small class="mr-2">fas fa-times-circle</k-icon>Done tracking changes</k-btn>
						<k-btn v-visible="!track_changes_legend_showing" icon small color="#fff" accessible-label="Show track changes legend" @click="toggle_track_changes_legend"><k-icon small>fas fa-key</k-icon></k-btn>
					</nobr>
				</div>
			</div>

			<!-- button to switch to tree mode from tile mode -->
			<div v-if="viewer_mode == 'tiles'" class="k-tile-mode-to-tree-mode-btn">
				<k-btn fab x-small color="primary" accessible-label="Return to tree view" @click="cancel_tile_mode"><k-icon>fas fa-tree</k-icon></k-btn>
			</div>
		</vue-draggable-resizable>

		<CASEFVMoreInfo v-if="more_info_type"
			:framework_record="framework_record"
			:object_type="more_info_type"
			:object="more_info_item"
			:show_delta_at_start="more_info_show_delta"
			@show_framework_json="show_framework_json"
			@dialog_cancel="more_info_type = ''"
		/>

		<PrintItems v-if="show_print_dialog"
			:framework_record="framework_record"
			:print_node="print_node"
			@dialog_cancel="show_print_dialog = false"
		/>

		<CASEAssociatedItemTree ref="associated_item_tree" v-if="association_framework_tree_identifier"
			:left_node="association_framework_tree_left_node"
			:associationType="association_framework_tree_associationType"
			:framework_identifier="association_framework_tree_identifier"
			:archive_framework_record="association_framework_tree_associationType == 'archive' ? track_changes_framework_record : null"
			:item_identifier="association_framework_tree_item_identifier"
			:item_tree_key="association_framework_tree_item_tree_key"
			:line_to_identifier="association_framework_tree_item_identifier"
			:line_to_tree_key="association_framework_tree_item_tree_key"
			:line_from_tree_key="association_line_from_tree_key"
			:viewer="this"
			@dialog_cancel="hide_associated_item"
		/>

		<CASEItemTileExpanded ref="expanded_tile"
			:viewer="this"
			:framework_record="framework_record"
		/>

		<DocumentEditor v-if="editing_document && !viewing_archive" 
			:framework_record="framework_record" 
			:viewer="this" 
			@dialog_cancel="cancel_edit_document" 
		/>

		<ItemEditor v-if="edited_node && !viewing_archive"
			:framework_record="framework_record"
			:original_node="edited_node"
			:framework_maximized="maximized"
			:viewer="this" 
			@dialog_cancel="cancel_item_edit" 
			@item_edit_suggestion_saved="item_edit_suggestion_saved" 
			@item_edit_suggestion_applied="item_edit_suggestion_applied" 
			@edit_new_child="edit_new_child" 
			@make_node_open="make_node_open" 
			@make_node_active="make_node_active" 
			@make_node_parents_open="make_node_parents_open" 
		/>

		<BatchEditor ref="batch_editor" v-if="show_checkbox_fn && !viewing_archive"
			:framework_record="framework_record"
			:viewer="this"
			@dialog_cancel="toggle_batch_edit_mode"
		/>

		<MoveEditor ref="move_editor" v-if="show_move_fn && !viewing_archive"
			:framework_record="framework_record"
			:viewer="this"
			@dialog_cancel="toggle_move_mode"
		/>

		<MakeAssociationsInterface ref="make_associations_editor" v-if="show_make_associations_interface && !viewing_archive"
			:framework_record="framework_record" 
			:viewer="this"
			:restore-focus-element="make_associations_restore_focus_element"
			@dialog_cancel="toggle_make_associations"
		/>

		<ReduceFileSize v-if="show_reduce_file_size_dialog"
			:framework_record="framework_record"
			@dialog_cancel="show_reduce_file_size_dialog = false"
		/>

		<ApplySandbox v-if="show_apply_sandbox"
			:framework_record="framework_record"
			@dialog_cancel="show_apply_sandbox = false"
			@hide_tree="hide_tree"
		/>

		<CommentsTable v-if="show_comments_table"
			:viewer="this"
			:framework_record="framework_record"
			:starting_comment_id="comments_table_starting_comment_id"
			@search_result_clicked="search_result_clicked"
			@dialog_cancel="show_comments_table = false"
		/>

		<FrameworkAdminUsers v-if="show_manage_framework_user_rights"
			:lsdoc_identifier="lsdoc_identifier"
			@dialog_cancel="show_manage_framework_user_rights = false"
		/>

		<FrameworkAccessReportList v-if="show_access_detail_dialog"
			:framework_record="framework_record"
			@dialog_cancel="show_access_detail_dialog = false"
		/>

		<ImportConfirm v-if="show_import_confirm"
			:viewer="this"
			@dialog_cancel="show_import_confirm = false"
		/>

		<ProgressionTable v-if="progression_table_data"
			:framework_record="progression_table_record"
			:progression_table_data="progression_table_data"
			@dialog_cancel="progression_table_data = null"
		/>

		<CustomScriptRunner v-if="custom_script_name"
			:custom_script_name="custom_script_name"
			@dialog_cancel="custom_script_name = ''"
		/>

		<SideBySideEditor v-if="side_by_side_editor_head_identifier"
			:viewer="this"
			:head_identifier="side_by_side_editor_head_identifier"
			@dialog_cancel="on_side_by_cancel"
		/>

		<ManageCommentGroups v-if="show_comment_group_manager"
			:framework_record="framework_record"
			@dialog_cancel="show_comment_group_manager = false"
		/>

		<CanvasImport v-if="show_canvas_import_dialog"
			:framework_record="framework_record"
			@dialog_cancel="show_canvas_import_dialog = false"
		/>

		<PowerschoolImport v-if="show_powerschool_import_dialog"
			:framework_record="framework_record"
			@dialog_cancel="show_powerschool_import_dialog = false"
		/>

		<CFRubricModal v-if="rubric_identifier_showing"
			:initiating_cfitem="initiating_rubric_item"
			:viewer="this"
			:rubric_identifier="rubric_identifier_showing"
			:initial_rubric_component_identifier_showing="initial_rubric_component_identifier_showing"
			:framework_record="framework_record"
			@dialog_cancel="rubric_identifier_showing = null; initial_rubric_component_identifier_showing = null; initiating_rubric_item = null;"
		/>

		<BestPracticesReport v-if="best_practices_showing"
			:framework_record="framework_record"
			@dialog_cancel="best_practices_showing = false"
		/>

	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import FrameworkSwitcher2 from '@/components/frameworks/FrameworkSwitcher2'
import CopyBtn from '../utilities/CopyBtn'
import CASEFVMoreInfo from './CASEFVMoreInfo'
import PrintItems from './PrintItems'
import CASETree from './CASETree'
import CASEItemTileExpanded from './CASEItemTileExpanded'
import CASEAssociatedItemTree from './CASEAssociatedItemTree'
import AssociationsTable from './AssociationsTable'
import ItemsTable from './ItemsTable'
import ProgressionTable from './ProgressionTable'
import FrameworkAccessReportList from '../admin/FrameworkAccessReportList'
import CASEFVWikiMixin from './CASEFVWikiMixin'
import CASEFVSearchMixin from './CASEFVSearchMixin'
import CASEFVAssociationsMixin from './CASEFVAssociationsMixin'
import '../../js/search_fns.js'

import DocumentEditor from '../edit/DocumentEditor'
import ItemEditor from '../edit/ItemEditor'
import BatchEditor from '../edit/BatchEditor'
import MoveEditor from '../edit/MoveEditor'
import MakeAssociationsInterface from '../edit/MakeAssociationsInterface'
import ReduceFileSize from '../edit/ReduceFileSize'
import ImportConfirm from '../edit/ImportConfirm'
import ImportItemsSaveMixin from '../edit/ImportItemsSaveMixin'
import ApplySandbox from '../edit/ApplySandbox'
import FrameworkAdminUsers from '../admin/FrameworkAdminUsers'
import CommentsMixin from '../comments/CommentsMixin'
import CommentsTable from '../comments/CommentsTable'
import MirrorMixin from '../../js/MirrorMixin'
import CASEFVToolbar from './CASEFVToolbar'
import ManageCommentGroups from '../comments/ManageCommentGroups'
import APMetadataMixin from '../../js/APMetadataMixin'
import CustomScriptRunner from '../edit/CustomScriptRunner'
import CanvasImport from './CanvasImport'
import PowerschoolImport from './PowerschoolImport'
import NCAIExportMixin from './NCAIExportMixin'

import CrosswalkEditor from '../crosswalks/CrosswalkEditor.vue'
import SideBySideEditor from './SideBySideEditor'
import CFRubricModal from '@/components/rubric/CFRubricModal.vue'

import BestPracticesReport from '../admin/BestPracticesReport'

export default {
	components: {
		FrameworkSwitcher2,
		CASEFVMoreInfo,
		PrintItems,
		CopyBtn,
		CASETree,
		CASEItemTileExpanded,
		CASEAssociatedItemTree,
		AssociationsTable,
		ItemsTable,
		ProgressionTable,
		FrameworkAccessReportList,
		CASEFVToolbar,
		CustomScriptRunner,
		CanvasImport,
		PowerschoolImport,
		// take editor componts out if not using editing capabilities
		DocumentEditor,
		ItemEditor,
		BatchEditor,
		MoveEditor,
		MakeAssociationsInterface,
		ReduceFileSize,
		FrameworkAdminUsers,
		CommentsTable,
		ImportConfirm,
		// Archive actions are a mix of viewing (view, compare) and editing (restore, delete)
		ApplySandbox,
		CrosswalkEditor,
		SideBySideEditor,
		ManageCommentGroups,
		CFRubricModal,
		BestPracticesReport
	},
	mixins: [
		CASEFVWikiMixin,
		CASEFVSearchMixin,
		CommentsMixin,
		ImportItemsSaveMixin,
		MirrorMixin,
		APMetadataMixin,
		CASEFVAssociationsMixin,
		NCAIExportMixin,
	],
	props: {
		lsdoc_identifier: { type: String, required: true },
		starting_lsitem_identifier: {
			type: String,
			required: false,
			default() {
				''
			}
		},
		starting_tree_key: {
			type: String,
			required: false,
			default() {
				''
			}
		},
		minimized_param: {
			type: Boolean,
			required: false,
			default() {
				return false
			}
		},
		imported_search_terms: {
			type: String,
			required: false,
			default() {
				return ''
			}
		}
	},
	data() {
		return {
			initialized: false,
			a11y_status_message: '',
			kebab_menu_showing: false,
			export_menu_showing: false,
			sandbox_menu_showing: false,
			derivative_menu_showing: false,
			track_changes_fields_menu_showing: false,
			edited_node: null, // note that AppShortcutsMixin checks edited_node and editing_document to determine if shortcuts should be used
			editing_document: false,
			current_editor: null,
			show_fw_changes: false,
			more_info_type: '',
			more_info_item: {},
			more_info_show_delta: false,
			stats_mode: false,
			show_chooser_fn: false,
			show_reduce_file_size_dialog: false,
			show_checkbox_fn: false,
			show_move_fn: false,
			move_node_shift_right: null,
			show_move_node_conversion: false,
			show_make_associations_interface: false,
			make_associations_restore_focus_element: null,
			show_manage_framework_user_rights: false,
			show_apply_sandbox: false,
			show_access_detail_dialog: false,
			show_canvas_import_dialog: false,
			show_powerschool_import_dialog: false,
			show_comments_table: false,
			best_practices_showing: false,
			comment_editor: null,
			show_print_dialog: false,
			print_node: null,
			// side_by_side_editor_head_identifier: null,
			rubric_identifier_showing: null,
			initial_rubric_component_identifier_showing: null,
			initiating_rubric_item: null,

			expanded_tile_item: null,
			expanded_tile_framework_record: null,

			show_import_confirm: false,
			new_imported_item_identifiers: [],
			updated_imported_item_identifiers: [],

			view_archive_ctl_text: '',
			view_archive_fn: '',
			editing_prior_to_viewing_archive: null,
			show_compare_to_archive: false,
			archive_date: '',
			archive_note: '',
			archive_item_identifier_to_show: '',
			track_changes_framework_record: null,
			track_changes_field_descriptions: {
				fullStatement: 'Statement/title',
				humanCodingScheme: 'Human-readable code',
				notes: 'Notes',
				supplementalNotes: 'Supplemental information',
				highlight_text_diffs: '<b>HIGHLIGHT TEXT CHANGES</b>',
				loose_comparisons: '<b>IGNORE PUNCTUATION/CAPITALIZATION CHANGES</b>'
				// CFItemType: 'Item type',
				// educationLevel: 'Education level',
			},

			arc_item: {},
			item_move_drag_options: {
				group: 'case_items',
				animation: 200,
				handle: '.k-case-item-move-handle'
			},
			remove_association_confirmed: false,
			association_framework_tree_left_node: null,
			association_framework_tree_associationType: '',
			association_framework_tree_identifier: null,
			association_framework_tree_item_identifier: null,
			association_framework_tree_item_tree_key: null,
			last_clicked_association_identifier: null,
			last_clicked_association_item_tree_key: null,
			association_line_from_tree_key: '',

			previous_starting_tree_key: '',

			progression_table_data: null,
			progression_table_record: null,

			// responsivity
			title_or_search: 'title',

			start_string_message_showing: false,

			custom_script_name: '',

			// manage comment groups
			show_comment_group_manager: false,

			// use these to avoid the overhead of rendering the table modes until requested
			item_table_shown: false,
			assoc_table_shown: false,

			// if set, the table will show only children of the specified node
			table_mode_start_node: null,

			// state used for showing stamp reminder
			last_stamped_framework_timestamp: null,

			focused_tree_key: '',
			tree_card_focus_origin_key: '',
			tree_card_focus_trap_active: false,
			tree_card_last_tab_direction: 1,
		}
	},
	computed: {
		...mapState([
			'user_info',
			'framework_records',
			'start_string',
			'start_identifier',
			'embedded_mode',
			'allow_framework_switching',
			'cureum_standards_tabbed',
			'site_config'
		]),
		...mapGetters([
			'signed_in', 
			'bp_review_full_reports_available'
		]),
		grades() {
			return this.framework_record?.resolved_grades || this.$store.state.grades
		},
		track_changes_fn: {
			// store the track_changes_fn for each framework in localstorage; save/store archive_date and archive_note along with the fn
			get() {
				let s = this.$store.state.lst.track_changes_fn
				if (s) {
					let o = JSON.parse(s)
					if (o[this.lsdoc_identifier]) {
						this.archive_date = o[this.lsdoc_identifier].archive_date
						this.archive_note = o[this.lsdoc_identifier].archive_note
						return o[this.lsdoc_identifier].fn
					} else {
						this.archive_date = ''
						this.archive_note = ''
						return ''
					}
				}
			},
			set(val) {
				let o = {}
				let s = this.$store.state.lst.track_changes_fn
				if (s) o = JSON.parse(s)
				o[this.lsdoc_identifier] = {
					fn: val,
					archive_date: this.archive_date,
					archive_note: this.archive_note
				}
				this.$store.commit('lst_set', ['track_changes_fn', JSON.stringify(o)])
			}
		},
		track_changes_fields() {
			return this.$store.state.lst.track_changes_fields
		},
		track_changes_legend_showing() {
			return vapp.framework_list_component.$refs.track_changes_legend.showing
		},
		side_by_side_editor_head_identifier: {
			get() {
				return this.$store.state.lst.side_by_side_editor_head_identifier[this.lsdoc_identifier] ?? ''
			},
			set(val) {
				this.$store.commit('lst_set_hash', ['side_by_side_editor_head_identifier', this.lsdoc_identifier, val])
			}
		},
		user_can_view() {
			return vapp.is_granted('view_framework', this.lsdoc_identifier)
		},
		user_can_edit() {
			// no editing or admining or aligning when on a phone!
			if (!this.signed_in || this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm) return false
			return vapp.is_granted('edit_framework', this.lsdoc_identifier)
		},
		user_can_admin() {
			if (!this.signed_in || this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm) return false
			return vapp.is_granted('admin_framework', this.lsdoc_identifier)
		},
		case_version_displayed: {
			get() {
				return this.$store.state.case_version_displayed
			},
			set(val) {
				this.$store.state.case_version_displayed = val
			}
		},
		viewer_mode: {
			get() {
				//always use tree mode in wiki_mode_view
				if (this.wiki_mode_view) return 'tree'

				let mode = this.$store.state.lst.viewer_mode
				if (mode == 'table') {
					if (this.viewer_table_mode == 'items') this.item_table_shown = true
					else this.assoc_table_shown = true
				}
				return mode
			},
			set(val) {
				this.$store.commit('lst_set', ['viewer_mode', val])

				if (val == 'tiles') {
					vapp.set_current_app_help_doc('tileview')
				} else if (val == 'table') {
					if (this.viewer_table_mode == 'items') {
						this.item_table_shown = true
						vapp.set_current_app_help_doc('tableitemsview')
					} else {
						this.assoc_table_shown = true
						vapp.set_current_app_help_doc('tableassociationsview')
					}
				} else {
					vapp.set_current_app_help_doc('treeview')
				}
			}
		},
		viewer_table_mode: {
			get() {
				return this.$store.state.lst.viewer_table_mode
			},
			set(val) {
				this.$store.commit('lst_set', ['viewer_table_mode', val])

				if (val == 'items') {
					this.item_table_shown = true
					vapp.set_current_app_help_doc('tableitemsview')
				} else {
					this.assoc_table_shown = true
					vapp.set_current_app_help_doc('tableassociationsview')
				}
			}
		},
		maximized: {
			// view is maximized by default -- unless 'minimized' is in the query string
			get() {
				return this.minimized_param !== true
			},
			set(val) {
				this.$emit('set_mode', 'minimized', !val)
			}
		},
		enable_powerschool_export() {
			// for now at least make this a site_config; we might later make it available to anyone (like the Canvas export)
			return this.site_config.enable_powerschool_export
		},
		editing_enabled: {
			get() {
				return this.$store.state.editing_enabled[this.CFDocument.identifier]
			},
			set(val) {
				this.$store.dispatch('update_editing_enabled', { frameworkId: this.CFDocument.identifier, value: val })
			}
		},
		show_identifiers_in_tiles: {
			get() {
				return this.$store.state.lst.show_identifiers_in_tiles
			},
			set(val) {
				this.$store.commit('lst_set', ['show_identifiers_in_tiles', val])
			}
		},
		show_color_coded_item_types: {
			get() {
				return this.$store.state.lst.show_color_coded_item_types
			},
			set(val) {
				this.$store.commit('lst_set', ['show_color_coded_item_types', val])
			}
		},
		hide_associations() {
			// there is a satchel-specific document setting for hiding associations for all users except framework reviewers/editors/admins; respect that setting here
			// if you're allowed to review the framework, associations are *not* hidden for you
			if (vapp.is_granted('review_framework', this.lsdoc_identifier)) return false
			return this.framework_record.ss_framework_data.hide_associations
		},
		show_associations: {
			get() {
				// if we don't *have* any associations for the framework, don't show associations, regardless of whether or not the show_associations flag is on
				if (!this.has_associations) return false

				// also don't show if hide_associations evaluates to true
				if (this.hide_associations) return false

				return this.$store.state.lst.show_associations
			},
			set(val) {
				this.$store.commit('lst_set', ['show_associations', val])
			}
		},
		wrap_item_text: {
			get() {
				return this.$store.state.lst.wrap_item_text
			},
			set(val) {
				this.$store.commit('lst_set', ['wrap_item_text', val])
			}
		},
		show_comments: {
			get() {
				// no commenting currently allowed in embedded_mode
				if (this.embedded_mode) return false
				return this.$store.state.lst.show_comments
			},
			set(val) {
				this.$store.commit('lst_set', ['show_comments', val])
			}
		},
		viewer_width: {
			get() {
				let width = this.$store.state.case_tree_viewer_width
				if (empty(width)) {
					width = Math.round($(window).width() / 2)
					if (width > 900) width = 900 // max 900
					if (width < 600) width = 600 // min 600
				}
				if (width > $(window).width()) width = $(window).width()
				return width
			},
			set(val) {
				this.$store.commit('set', ['case_tree_viewer_width', val])
			}
		},
		viewer_height: {
			get() {
				// on small screen, full height
				if (this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm) return $(window).height()

				let height = this.$store.state.case_tree_viewer_height
				if (empty(height)) {
					height = $(window).height() - 110
				}
				return height
			},
			set(val) {
				this.$store.commit('set', ['case_tree_viewer_height', val])
			}
		},
		class_name() {
			let s = 'k-case-tree-outer-wrapper '

			const framework_color = U.framework_color(this.framework_record.json.CFDocument.identifier)
			if (!isNaN(framework_color)) s += 'k-framework-color-' + framework_color

			if (this.embedded_mode) s += ' k-case-tree-outer-wrapper-embedded'

			if (this.wiki_mode) {
				s += ' k-case-tree-outer-wrapper-wiki-mode'
				if (this.wiki_mode_view) s += ' k-case-tree-outer-wrapper-wiki-mode-view'
			}

			if (this.viewer_mode == 'table') {
				s += ' k-case-tree-outer-wrapper--table-mode'
			}

			if (this.viewing_archive || this.tracking_changes) s += ' k-case-tree-outer-wrapper-archive-showing'

			if (this.maximized) {
				if (
					this.framework_record.pinned_items.length > 0 &&
          !this.$vuetify.breakpoint.xs &&
          !this.$vuetify.breakpoint.sm
				) {
					s += ' k-case-tree-outer-wrapper--pinned-items-showing'
				}
			} else {
				s += ' k-case-tree-outer-wrapper--minimized elevation-8'
			}
			return s
		},
		case_tree_top_style() {
			return U.framework_color_object(this.framework_record.json.CFDocument.identifier)
		},
		case_tree_main_style() {
			return U.framework_color_object(this.framework_record.json.CFDocument.identifier, 'lighten-5')
		},
		doc_title_css() {
			// reduce size of title when necessary to fit it in
			let title = this.framework_record.json.CFDocument.title
			if (title?.length > 85) return 'k-case-tree-title-inner-xsmall'
			else if (title?.length > 70) return 'k-case-tree-title-inner-small'
			return ''
		},
		// cfdocument_subject() {
		// 	// this is supposed to always come in as an array, but sometimes it's a string
		// 	if (empty(this.cfdocument.subject)) return ''
		// 	if ($.isArray(this.cfdocument.subject)) return this.cfdocument.subject.join(', ')
		// 	return this.cfdocument.subject
		// },

		framework_record() {
			let o = this.framework_records.find(x => x.lsdoc_identifier == this.lsdoc_identifier)
			if (empty(o)) return {}
			else return o
		},

		// this will be true if this is a framework designed to house resource metadata
		is_resource_framework() {
			return U.is_resource_framework(this.framework_record)
		},

		cfo() {
			if (empty(this.framework_record.cfo)) return {}
			else return this.framework_record.cfo
		},
		case_tree_title() {
			if (empty(this.framework_record.json.CFDocument)) return ''

			// in wiki_mode_view, show this
			if (this.wiki_mode_view) {
				// start with specified banner_title
				let s = this.wiki_variables['banner_title']?.val || 'NO banner_title SPECIFIED'

				// add icon if provided
				if (this.wiki_variables['banner_title_icon'])
					s = `<i class="${this.wiki_variables['banner_title_icon'].val}" style="margin-right: 6px; font-size: 1.2em;"></i>${s}`

				return s
			}

			return this.framework_record.json.CFDocument.title
		},
		cftree() {
			return empty(this.cfo.cftree) ? {} : this.cfo.cftree
		},
		cfdocument() {
			return empty(this.cftree) || empty(this.cftree.cfitem) ? {} : this.cftree.cfitem
		},
		cfitems() {
			return empty(this.cfo.cfitems) ? {} : this.cfo.cfitems
		},
		CFDocument() {
			return empty(this.framework_record.json) ? {} : this.framework_record.json.CFDocument
		},
		has_items() {
			return !empty(this.cftree.children) && this.cftree.children.length > 0
		},
		is_mirror() {
			return this.framework_record.ss_framework_data.is_mirror === 'yes'
		},
		is_derivative() {
			return !empty(this.cfdocument.extensions?.sourceFrameworkIdentifier)
		},
		derivative_original_framework_record() {
			if (!this.is_derivative) return null
			return this.framework_records.find(
				x => x.lsdoc_identifier == this.cfdocument.extensions.sourceFrameworkIdentifier
			)
		},
		derivative_original_json() {
			if (!this.derivative_original_framework_record?.framework_json_loaded) return null
			return this.derivative_original_framework_record.json
		},
		is_sandbox() {
			return !empty(this.framework_record.ss_framework_data.sandboxOfIdentifier)
		},
		is_private() {
			return U.framework_is_private(this.framework_record)
		},
		sandbox_creation_date() {
			if (!this.is_sandbox) return ''
			return this.framework_record.ss_framework_data.sandboxSyncDateTime
		},
		user_is_admin() {
			return this.user_info.system_role == 'admin'
		},
		fw_changes() {
			return this.framework_record.fw_changes
		},
		active_node() {
			return this.framework_record.active_node
		},
		open_nodes() {
			return this.framework_record.open_nodes
		},
		open_node_count() {
			return Object.keys(this.open_nodes).length
		},
		image_src() {
			return U.framework_image_src(this.framework_record)
		},
		viewing_archive() {
			// we're currently viewing an archive if this.view_archive_fn is set to something
			return !empty(this.view_archive_fn)
		},
		tracking_changes() {
			return (
				!empty(this.track_changes_fn) &&
        !empty(this.track_changes_framework_record) &&
        !empty(this.track_changes_framework_record.cfo)
			)
		},
		public_review_on() {
			return this.framework_record.ss_framework_data.public_review_on
		},

		font_size: {
			get() {
				if (this.$vuetify.breakpoint.xs) return this.$store.state.lst.font_size_xs
				else if (this.$vuetify.breakpoint.sm) return this.$store.state.lst.font_size_sm
				else return this.$store.state.lst.font_size
			},
			set(val) {
				if (this.$vuetify.breakpoint.xs) this.$store.commit('lst_set', ['font_size_xs', val])
				else if (this.$vuetify.breakpoint.sm) this.$store.commit('lst_set', ['font_size_sm', val])
				else this.$store.commit('lst_set', ['font_size', val])
			}
			// note that font_size gets expressed in CASETree.top_css_class
		},

		// responsivity
		show_framework_title() {
			// if not on a small screen, always show the title
			if (!this.$vuetify.breakpoint.xs && !this.$vuetify.breakpoint.sm) return true
			// else we toggle between showing the search bar and the title
			return this.title_or_search == 'title'
		},

		show_search_bar() {
			// don't show the search bar when we're in crosswalking mode
			// if (this.showing_crosswalk) return false

			// if not on a small screen, always show the search bar
			if (!this.$vuetify.breakpoint.xs && !this.$vuetify.breakpoint.sm) return true
			// else we toggle between showing the search bar and the title
			return this.title_or_search == 'search'
		},

		has_associations() {
			// note that we used to calculate this right here using a recursive fn, but that proved to slow things down a lot for big frameworks
			return this.framework_record.cfo.displayed_association_counts_hash[this.lsdoc_identifier] > 0
		},

		selected_items() {
			return this.framework_record.selected_items
		},
		candidate_items() {
			return this.framework_record.candidate_items
		},
		selected_or_candidate_items() {
			if (!this.selected_items && !this.candidate_items) return null
			return [...new Set([...(this.selected_items || []), ...(this.candidate_items || [])])]
		},
		selected_nodes() {
			// console.log('computing selected_nodes')
			// we actually look at both selected and candidate items for this purpose
			if (!this.selected_or_candidate_items) return null

			let arr = []

			// selected_items and candidate_items are identifiers, and could be aliased in multiple places.
			// if a selected_items_ancestor is specified, only select nodes that are descendents of it (if there are multiple nodes for an item)

			for (let identifier of this.selected_or_candidate_items) {
				let cfitem = this.cfitems[identifier]
				// note that selected_items may include items from other frameworks; skip them
				if (!cfitem) continue

				// for each selected item, if there is a single node for the item (which will usually be the case), we always show it
				if (cfitem.tree_nodes.length == 1) {
					arr.push(cfitem.tree_nodes[0])
					continue
				}

				// if we don't have a selected_items_ancestor, always show all nodes
				if (!this.framework_record.selected_items_ancestor) {
					for (let node of cfitem.tree_nodes) arr.push(node)
					continue
				}

				// else go through all nodes for this item
				let node_pushed = false
				for (let node of cfitem.tree_nodes) {
					// traverse up from the node; if we find selected_items_ancestor, push the node
					this.temp_node = node
					while (temp_node) {
						if (node.cfitem.identifier == this.framework_record.selected_items_ancestor) {
							arr.push(node)
							node_pushed = true
							break
						}
						temp_node = node.parent_node
					}
				}
				// if we didn't push at least one node above, push the first node for the item
				if (!node_pushed) {
					arr.push(cfitem.tree_nodes[0])
				}
			}

			// if open_selected_nodes_when_calculated was set to true...
			// console.log('open_selected_nodes_when_calculated: ' + this.framework_record.open_selected_nodes_when_calculated)
			if (this.framework_record.open_selected_nodes_when_calculated) {
				// open all the selected nodes' ancestors, so that the nodes are showing -- but don't open (show the children of) the selected node itself
				// but we don't here reset all open_nodes -- that may be done in PostMessageMixin, if appropriate
				for (let node of arr) {
					let open_it = false
					while (node.tree_key > 1) {
						if (open_it) this.$store.commit('set', [this.framework_record.open_nodes, node.tree_key + '', true])
						node = node.parent_node
						open_it = true
					}
				}

				if (this.limited_to_selected_items == 'false_after_open') {
					this.$store.commit('set', [this.framework_record, 'limit_to_selected_items', false])
				}

				// always reset open_selected_nodes_when_calculated to false after we've shown the selected nodes
				this.$store.commit('set', [this.framework_record, 'open_selected_nodes_when_calculated', false])
			}

			return arr
		},
		tree_keyboard_enabled() {
			if (this.viewer_mode != 'tree') return false
			if (this.show_checkbox_fn) return false
			if (this.show_move_fn) return false
			if (this.edited_node || this.editing_document) return false
			if (this.showing_crosswalk) return false
			if (this.association_framework_tree_identifier) return false
			return true
		},
		limit_to_selected_items() {
			return this.framework_record.limit_to_selected_items
		},

		framework_type_profile() {
			// Note: this may eventually determine quite a lot of things about how the framework is edited and rendered
			
			// Find the key that matches normalized frameworkType
			let framework_type = U.normalize_to_snake_case(this.CFDocument.frameworkType)
			let actual_key = Object.keys(this.$store.state.framework_type_profiles).find(key => { return U.normalize_to_snake_case(key) === framework_type })

			return this.$store.state.framework_type_profiles[actual_key] ?? {}
		},

		show_rosetta_toggle() {
			// allow the user to toggle to rosetta if the search bar is showing (meaning we have enough space)
			if (!this.show_search_bar) return false
			// and this is a resource set framework
			if (!this.is_resource_framework) return false
			// and the user has edit priviledges (might need to update this later...)
			if (!this.user_can_edit) return false
			return true
		},
		frameworkTitleText() {
			return this.plainText(this.case_tree_title) || 'framework'
		},
		frameworkImageAlt() {
			return `${this.frameworkTitleText} image`
		}
	},
	created() {
		// vapp.case_tree_component will always point to the currently-open viewer, so we can shortcut to current viewer properties with this
		// e.g. vapp.case_tree_component.user_can_edit
		vapp.case_tree_component = this

		// whenever we mount this component, call set_embedded_mode_chooser_fn to set up the embedded mode chooser_fn if necessary
		vapp.set_embedded_mode_chooser_fn()

		// set a placeholder for the title on creation so that refreshing or navigating directly to a framework from url will display "Framework." for screenreaders 
		// once the JSON is loaded, it will get updated with a watcher
	},
	mounted() {
		/* the "normal" sequence we follow when a framework is loaded is:
			- call async refresh_lsdoc, which:
				- awaits the framework's JSON to be loaded from the server (store.dispatch('get_lsdoc'))
				- awaits initialize_tree, which:
					- awaits the framework's cfo to be built (build_cfo, which calls U.build_cfo)
					- waits a tick, then finishes setting things up for the tree

			In certain circumstances, though, the framework's json may already be retrieved, in which case we skip right to initialize_tree
			Furthermore, the cfo may already be built when we get here, in which initialize_tree will not rebuild the cfo
		*/

		// if the framework json has already been loaded, just call initialize_tree; otherwise call refresh_lsdoc to load the json
		if (this.framework_record.framework_json_loaded) {
			this.initialize_tree()
		} else {
			if (!this.framework_record.json) {
				this.$alert(
					sr(
						'The framework specified in this URL ($1) is not available on this instance of $2.',
						this.lsdoc_identifier,
						this.$store.state.site_config.app_name
					)
				)
				vapp.go_to_route('')
				return
			}

			// before loading, make sure the user has access to the framework. This prevents, e.g., viewing of a private framework by someone who doesn't have access to the framework
			if (!this.user_can_view) {
				this.$confirm({
					text: sr(
						'The framework specified in this URL ($1) is not publicly available at this time. Would you like to sign in?',
						this.case_tree_title
					),
					acceptText: 'Sign In',
					cancelText: 'View Public Frameworks',
					dialogMaxWidth: 600
				}).then(y => {
					vapp.sign_in()
				}).catch(n => {
					vapp.go_to_route('')
				}).finally(f => {})
				return
			}

			this.refresh_lsdoc()
		}
		this.last_stamped_framework_timestamp = this.framework_record.json.CFDocument.lastChangeDateTime
		// this.build_assoc_type_menu_options()

		this.$nextTick(() => {
			this.update_page_title()
		})

		document.addEventListener('keydown', this.handle_tree_card_document_keydown, true)
		document.addEventListener('focusin', this.handle_tree_card_document_focusin, true)

	},
	beforeDestroy() {
		document.removeEventListener('keydown', this.handle_tree_card_document_keydown, true)
		document.removeEventListener('focusin', this.handle_tree_card_document_focusin, true)

		if (this.editing_enabled && U.has_sandbox(this.framework_record.lsdoc_identifier)) {
			this.check_in_for_editing()
		}
	},
	watch: {
		tree_keyboard_enabled: {
			immediate: true,
			handler(enabled) {
				if (!enabled) {
					this.tree_card_focus_trap_active = false
					return
				}
				this.$nextTick(() => {
					this.sync_focused_tree_key()
				})
			}
		},
		active_node: {
			immediate: true,
			handler(tree_key) {
				if (!this.tree_keyboard_enabled || this.tree_card_focus_trap_active) return
				if (tree_key) this.focused_tree_key = tree_key + ''
			}
		},
		starting_lsitem_identifier: {
			immediate: true,
			handler() {
				// whenever starting_lsitem_identifier changes, if search results aren't showing and search_limit_to_item is false,...
				if (!this.search_results_panel_showing && !this.search_limit_to_item) {
					// ...clear search_item_for_limit
					this.search_item_for_limit = ''
				}

				// starting_tree_key could be old and invalid; if so, we don't want to use it
				let tree_key = this.validate_tree_key(this.starting_tree_key, this.starting_lsitem_identifier)

				if (!this.$store.state.case_tree_suppress_watcher) {
					// if we have a previous_starting_tree_key, close it
					if (this.previous_starting_tree_key) {
						this.$store.commit('set', [this.open_nodes, this.previous_starting_tree_key + '', '*DELETE_FROM_STORE*'])
					}

					if (!empty(this.starting_lsitem_identifier)) {
						this.$nextTick(x => {
							// clear last_clicked_node -- if we don't do this here, when the user clicks "back" the previously-last-clicked item is still highlighted
							this.$store.commit('set', [this.framework_record, 'last_clicked_node', ''])

							this.make_item_parents_open(this.starting_lsitem_identifier, tree_key)
							this.make_item_open(this.starting_lsitem_identifier, tree_key)
							this.make_item_active(this.starting_lsitem_identifier, tree_key)
						})
					}
				}

				vapp.pm_send('selected_item_changed', {
					framework_identifier: this.lsdoc_identifier,
					item_identifier: this.starting_lsitem_identifier
				})
				this.previous_starting_tree_key = tree_key
			}
		},
		show_comments() {
			// when user clicks to show comments, retrieve comments (and comment groups) from the DB
			if (this.show_comments) {
				this.get_comments_for_framework()
			}
		},
		'framework_record.last_clicked_node'() {
			if (this.$refs.associated_item_tree && this.$refs.associated_item_tree.remove_connecting_line) {
				this.$refs.associated_item_tree.remove_connecting_line()
			}
			this.last_clicked_association_identifier = null
			this.last_clicked_association_item_tree_key = null
			// this.hide_associated_item()
			// this.association_framework_tree_associationType = ''
			// this.association_line_from_tree_key = ''
			// this.association_framework_tree_identifier = null
			// this.association_framework_tree_item_identifier = null
		},

		showing_crosswalk() {
			this.update_associations_to_show()
		},

		case_tree_title() {
			this.update_page_title();
		},

		open_nodes: {immediate: true, handler() {
			// console.warn('open nodes changed', object_copy(this.open_nodes))
			// send a message to the chooser host (if we're in embedded mode) that the framework's open nodes have been changed
			vapp.pm_send('framework_open_nodes_changed', {
				framework_identifier: this.lsdoc_identifier,
				open_nodes: object_copy(this.framework_record.open_nodes)
			})
		}},

	},
	methods: {
		announceA11y(message) {
			this.a11y_status_message = ''
			this.$nextTick(() => {
				this.a11y_status_message = message
			})
		},
		plainText(value) {
			return $('<div>').html(value || '').text().trim()
		},
		searchResultCopyLabel(o) {
			const item = this.framework_record.cfo.tree_nodes_hash[o.tree_key]?.cfitem
			const labelSource = item?.humanCodingScheme || this.plainText(this.search_result_panel_copy_val(o))
			return `Copy search result for ${labelSource || 'item'}`
		},
		//since we want an archive reminder on CSV import click whether there are changes or not, add a force_show param
		async check_for_stamp_reminder_dialog(force_show = false) {
			const framework_has_changed =
				this.framework_record.json.CFDocument.lastChangeDateTime !== this.last_stamped_framework_timestamp
			if (!force_show && !framework_has_changed) return // no change AND no force_show, so no prompt

			try {
				const description = await this.$prompt({
					title: 'Save an Archive?',
					text: '<p>Would you like to archive the framework as it currently exists (after your edits)? If so, enter a description for the archive, then click STAMP ARCHIVE below.</p>',
					initialValue: '',
					promptType: 'textarea',
					acceptIcon: 'fas fa-stamp',
					acceptText: 'Stamp Archive',
					cancelText: 'Don’t Archive',
					dialogMaxWidth: 620
				}).catch(() => {
					// This catch handles the case where the user cancels the prompt
					// Early return to stop the rest of the code
					return null
				})

				// Whether the user chooses to stamp or not, we update last_stamped_framework_timestamp to avoid repeated prompts
				this.last_stamped_framework_timestamp = this.framework_record.json.CFDocument.lastChangeDateTime

				// If the user cancels, description will be null, so we return early
				if (description === null) {
					return
				}

				if (!description.trim()) {
					// user didn't enter a description, so don't create an archive
					await this.$alert('No description entered. Archive not created.')
					return
				}

				const payload = {
					lsdoc_identifier: this.CFDocument.identifier,
					phrase: description.trim(),
					cf_item_count: this.framework_record.json.CFItems.length,
					cf_association_count: this.framework_record.json.CFAssociations.length
				}

				await this.$store.dispatch('archive_lsdoc', payload)
				await this.$alert('Archive created and stamped successfully!')
				vapp.reset_framework_update_report_archives(this.CFDocument.identifier)
			} catch (error) {
				console.error('Error creating archive:', error)
				this.$alert('Error creating archive. Please try again.')
			}
		},
		handle_check_out_for_editing(toggle = false) {
			if (U.has_sandbox(this.framework_record.lsdoc_identifier) && !this.editing_enabled) {
				this.$confirm({
					title: 'Are you sure?',
					text: "This framework currently has a Sandbox open, so if you make edits to this framework directly, those edits may make the Sandbox unusable. The recommended practice is to make edits in the Sandbox, then apply those edits from the Sandbox to the main framework (then delete the Sandbox if you're no longer planning to use it to make further edits).<br><br>Are you sure you want to enable edit mode for this framework?",
					acceptText: 'I understand: Enable edit mode',
					acceptIcon: 'fas fa-triangle-exclamation',
					acceptColor: 'red darken-4',
					dialogMaxWidth: 600,
					focusBtn: true // focus on the accept btn when dialog is rendered
				}).then(() => {
					this.check_out_for_editing()
				}).catch(() => {
					// No action needed - dialog cancel action clicked
				})
			} else {
				if (toggle) {
					this.toggle_editing()
				} else {
					this.check_out_for_editing()
				}
			}
		},
		async refresh_lsdoc(archive_filename, callback_fn) {
			// cancel stats mode when we refresh
			this.stats_mode = false

			// caller can optionally specify an archive_filename to load
			let payload = {
				lsdoc_identifier: this.lsdoc_identifier,
				archive_filename: archive_filename
			}

			// if we're loading the framework for the first time (initialized is false), record the access of the framework
			if (!this.initialized) {
				payload.record_access = true
			}

			// console.log('CASEFrameworkViewer: refresh_lsdoc')
			U.loading_start('Loading framework…', 'refresh_lsdoc')

			try {
				await this.$store.dispatch('get_lsdoc', payload)
			} catch (e) {
				console.log(e)
				U.loading_stop('refresh_lsdoc')
				this.$alert('An error occurred when loading the competency framework.').then(x => this.hide_tree())
				return
			}

			U.loading_stop('refresh_lsdoc')

			// set this.framework_record.framework_json_loaded to ensure we re-initialize the tree
			this.$store.commit('set', [this.framework_record, 'framework_json_loaded', false])

			// if we're embedded, the parent app may have sent in item identifiers for selected_items and/or the starting item that refer to items from another framework that have been duplicated in the current framework. if so, the code below will translate to the copied items’ identifiers
			U.process_framework_record_sourceItemIdentifiers(this.framework_record)
			if (this.starting_lsitem_identifier) {
				let original_identifier = U.get_originals_from_sourceItemIdentifiers(
					this.starting_lsitem_identifier,
					this.framework_record
				)
				if (original_identifier != this.starting_lsitem_identifier) {
					setTimeout(x => this.set_starting_lsitem_identifier(original_identifier))
				}
			}

			await this.initialize_tree()

			// if we just loaded an archive, leave framework_json_loaded at false so that if we leave and come back to this framework, we'll reload the current framework;
			// otherwise set framework_json_loaded to true now
			if (empty(archive_filename)) this.$store.commit('set', [this.framework_record, 'framework_json_loaded', true])

			// if callback_fn specified, call it
			if (typeof callback_fn == 'function') callback_fn()
		},

		async build_cfo() {
			// console.log('load_framework/build_cfo original: ' + this.framework_record.json.CFDocument.identifier)
			let cfo
			try {
				cfo = await U.build_cfo(this.$worker, this.framework_record.json)
				// console.log(cfo)
			} catch (e) {
				// shouldn't happen
				console.warn('cfo couldn’t be built', e)
				return
			}

			// if show_fw_changes is on, add
			if (this.show_fw_changes && !empty(this.fw_changes)) {
				this.add_changes_to_cfo(cfo.cftree)
			}

			this.$store.commit('set', [this.framework_record, 'cfo', cfo])

			// restore pinned items from localstorage
			let s = this.$store.state.lst.pinned_items
			if (s) {
				let o = JSON.parse(s)
				if (o[this.framework_record.lsdoc_identifier]) {
					this.$store.commit('set', [this.framework_record, 'pinned_items', o[this.framework_record.lsdoc_identifier]])
				}
			}

			// if we received a start_string in the URL query string (this is set in App.vue/initialize)...
			if (!empty(this.start_string)) {
				// if we received an item identifier in start_string_identifiers (from initialize service), use it
				if (this.$store.state.start_string_identifiers && this.$store.state.start_string_identifiers.identifier) {
					console.log('here: ' + this.$store.state.start_string_identifiers.identifier)
					this.$store.commit('set', ['start_identifier', this.$store.state.start_string_identifiers.identifier])

					// else if start_string is a GUID, it *is* the identifier
				} else if (U.is_uuid(this.start_string)) {
					this.$store.commit('set', ['start_identifier', this.start_string])
				} else {
					// else start_string can be a humanCodingScheme value
					let hcs = this.start_string

					// allow for regexp(s) on the start_string value
					for (let a of this.$store.state.start_string_regexps) {
						hcs = hcs.replace(new RegExp(a[0]), a[1])
					}
					console.log(sr('start_string: $1 - $2', this.start_string, hcs))

					for (let identifier in this.framework_record.cfo.cfitems) {
						let item = this.framework_record.cfo.cfitems[identifier]
						// use indexOf instead of == so that "27.014" matches "27.01400"
						if (item.humanCodingScheme && item.humanCodingScheme.indexOf(hcs) == 0) {
							console.log('found start_string: ' + identifier)
							this.$store.commit('set', ['start_identifier', identifier])
							break
						}
					}
				}

				// if we got a start_identifier, set_starting_lsitem_identifier, so that we'll show the item below
				if (!empty(this.start_identifier)) {
					this.$emit('set_starting_lsitem_identifier', this.start_identifier)
				} else {
					console.log('couldn’t find identifier for start_string ' + this.start_string)
				}
			}

			// if we received a starting_lsitem_identifier, show it here
			if (!empty(this.starting_lsitem_identifier)) {
				this.$store.commit('set', ['case_tree_suppress_watcher', true])
				setTimeout(x => this.$store.commit('set', ['case_tree_suppress_watcher', false]), 10)

				// starting_tree_key could be old and invalid; if so, we don't want to use it
				let tree_key = this.validate_tree_key(this.starting_tree_key, this.starting_lsitem_identifier)
				this.make_item_parents_open(this.starting_lsitem_identifier, tree_key)
				this.make_item_open(this.starting_lsitem_identifier, tree_key)
				this.make_item_active(this.starting_lsitem_identifier, tree_key)
			}
		},

		show_help() {
			vapp.show_help()
		},
		show_framework_switcher() {
			vapp.show_framework_switcher()
		},

		set_framework_from_switcher(selected_framework_identifier) {
			// if this framework is already showing, don't do anything
			if (this.lsdoc_identifier == selected_framework_identifier) {
				return
			}

			// switching, so set store.lst value if we're in embedded mode
			if (this.embedded_mode)
				this.$store.commit('lst_set', ['embedded_framework_identifier', selected_framework_identifier])

			// also store the new framework as recently-accessed
			U.add_to_recent_frameworks(selected_framework_identifier)

			// we have to first remove the CASEFrameworkViewer component, then re-open it a tick later, to force the new framework to load
			this.hide_tree()
			setTimeout(() => {
				vapp.framework_list_component.view_framework([selected_framework_identifier])
				this.$emit('framework_selected')
			}, 10)
		},

		set_starting_lsitem_identifier(item_identifier, tree_key) {
			this.$emit('set_starting_lsitem_identifier', item_identifier, tree_key)
			// use this hackish method to stop the watcher in CASEFrameworkViewer from firing right now; if it fired it would just try to re-activate what we've already activated above
			// TODO: clean this up??
			this.$store.commit('set', ['case_tree_suppress_watcher', true])
			setTimeout(x => this.$store.commit('set', ['case_tree_suppress_watcher', false]), 10)
		},

		// this fn can be called by includers to first hide any open items, then show a single item, then scroll to the item
		show_item(identifier, flag) {
			if (flag != 'leave_open_nodes') this.$store.commit('set', [this.framework_record, 'open_nodes', {}])
			this.$store.commit('set', [this.framework_record, 'active_node', ''])
			this.$store.commit('set', [this.framework_record, 'last_clicked_node', ''])

			this.make_item_parents_open(identifier)
			this.make_item_open(identifier)
			this.make_item_active(identifier)

			// not sure what this is doing here...
			// setTimeout(x=>{
			// 	let target = $(sr('[data-tree-key=$1]', this.active_node))
			// 	if (target.length == 0) return
			// }, 0)
		},

		show_rubric(item) {
			this.rubric_identifier_showing = item.identifier
			this.initial_rubric_component_identifier_showing = null
		},

		// add change data to the cfo tree, to efficiently show changed items in the tree. Note that we only do this when directed to show change data
		add_changes_to_cfo(node) {
			let change = this.fw_changes.find(o => o.identifier == node.cfitem.identifier)
			// see UpdatesDocTable for fw_changes calculateion and format
			if (change && change.change_type != 'Date change only' && change.change_type != 'Unknown') {
				node.cfitem.stats.change = change
			}
			for (let i = 0; i < node.children.length; ++i) {
				this.add_changes_to_cfo(node.children[i])
			}
		},

		async initialize_tree() {
			// this is stuff that has to be done *after* the document json has loaded

			// build the cfo if it doesn't already exist, or if framework_json_loaded is set to false
			if (empty(this.framework_record.cfo) || !this.framework_record.framework_json_loaded) {
				await this.build_cfo()
			}

			// now do the things we have to do after build_cfo is complete

			// if editing is enabled and the user is allowed to edit this document...
			if (this.editing_enabled && this.user_can_edit) {
				this.check_out_for_editing()
			}

			// if show_comments is on, call get_comments_for_framework
			if (this.show_comments) {
				this.get_comments_for_framework()
			}

			// if we have a stored track_changes_fn, start tracking
			if (this.track_changes_fn) {
				this.track_changes(this.track_changes_fn, this.archive_date, this.archive_note)
			}

			this.initialized = true

			// if directing to an actual framework with '?bp', show the bp report right away
			// if ?bp was in the search string and we went right to a framework, show the bp report for the framework
			if (vapp.bp_requested_in_url) {
				setTimeout(x=>{
					vapp.case_tree_component.show_best_practices()
				}, 1000)
				vapp.bp_requested_in_url = false
			}

			// wait one more tick to make sure everything is set up, then...
			this.$nextTick(() => {
				// if this is a derivative and we don't have the original json, load it (before running update_frameworks_with_associations)
				if (this.is_derivative) {
					if (!this.derivative_original_json) {
						let payload = {
							lsdoc_identifier: this.cfdocument.extensions.sourceFrameworkIdentifier
						}

						console.log('load_framework derivative: ' + payload.lsdoc_identifier)
						U.loading_start('Loading original framework…', 'refresh_lsdoc')
						this.$store
							.dispatch('get_lsdoc', payload)
							.then(() => {
								U.loading_stop('refresh_lsdoc')
								let fr = this.framework_records.find(
									x => x.lsdoc_identifier == this.cfdocument.extensions.sourceFrameworkIdentifier
								)
								// if we didn't get an fr here, probably the original JSON could not be found
								if (!fr) {
									// we probably shouldn't be here though, because get_lsdoc should actually reject()
									console.log(
										sr(
											'Derivative framework’s ($1) original framewwork ($2) could not be found [1]',
											this.cfdocument.identifier,
											this.cfdocument.extensions.sourceFrameworkIdentifier
										)
									)
								} else {
									// then build the cfo for the original framework; we will need it to show associations
									U.build_cfo(this.$worker, fr.json)
										.then(cfo => {
											this.$store.commit('set', [fr, 'cfo', cfo])
											this.$store.commit('set', [fr, 'framework_json_loading', false])

											// when we're done loading the original, pull associations from the original, then update_frameworks_with_associations
											U.pull_associations_from_original_of_derivative(this.framework_record, fr)
											this.update_frameworks_with_associations()
										}).catch(e => {
											this.$store.commit('set', [fr, 'framework_json_load_failed', true])
											console.log(e)
										})
								}
							}).catch(e => {
								// if we get to here, the original doc has disappeared
								U.loading_stop('refresh_lsdoc')
								console.log(e)
								console.log(
									sr(
										'Derivative framework’s ($1) original framewwork ($2) could not be found [2]',
										this.cfdocument.identifier,
										this.cfdocument.extensions.sourceFrameworkIdentifier
									)
								)
							})
					} else {
						// if we already have the original, pull its associations now, then update_frameworks_with_associations
						U.pull_associations_from_original_of_derivative(
							this.framework_record,
							this.derivative_original_framework_record
						)
						this.update_frameworks_with_associations()
					}
				} else {
					// not a derivative, so run update_frameworks_with_associations now
					this.update_frameworks_with_associations()
				}

				// start automatically calling the tree_scrolled fn
				clearTimeout(window.auto_tree_scrolled_timeout) // this clears out previous timeout when we're in dev mode and we update the app
				this.auto_tree_scrolled()

				// if we have a viewer_post_load_execute_fn, execute it
				if (this.$store.state.viewer_post_load_execute_fn) {
					this.$store.state.viewer_post_load_execute_fn()
					this.$store.commit('set', ['viewer_post_load_execute_fn', null])
				}

				// send a message to the chooser host (if we're in embedded mode) that a framework has been loaded
				vapp.pm_send('framework_loaded', {
					framework_identifier: this.lsdoc_identifier,
					item_identifier: this.starting_lsitem_identifier,
					source: 1
				})
			})
		},

		active_item_position() {
			let tile_jq = $('.k-case-tree-active-item-tile-wrapper')

			// wiki_mode_view: always position at the top
			if (this.wiki_mode_view) return [tile_jq, 0, 0]

			// get position of node in tree, relative to the window
			let offset = $(sr('[data-tree-key=$1]', this.active_node)).offset()
			let item_top = offset.top

			let tile_top = item_top
			// if not inline, the tile will be positioned relative to tile_wrapper_top, which should be ~112px
			let tile_wrapper_jq = $('.k-case-tree-active-item-wrapper')
			if (tile_wrapper_jq.length > 0) {
				// so start with tile_top at item_top - tile_wrapper_top
				tile_top -= $('.k-case-tree-active-item-wrapper').offset().top
			}

			// if the tile would be too high, start by positioning at the top of the tile wrapper
			if (tile_top < 0) {
				tile_top = 0
			}

			// get tile_height and window height
			let tile_height = tile_jq.height()
			let window_height = $(window).height()
			// console.log('tile_height: ' + tile_height)

			// calculate where the bottom of the tile would be, relative to the window
			let tile_bottom = item_top + tile_height

			// if that's too low, move it up so it'll be at the bottom of the screen
			let tile_bottom_max = this.tracking_changes ? window_height - 42 : window_height - 6
			if (tile_bottom > tile_bottom_max) {
				tile_top = tile_top - (tile_bottom - tile_bottom_max)
			}

			return [tile_jq, item_top, tile_top]
		},

		auto_tree_scrolled() {
			// run the fn that keeps the tile appearing in the right place every 100 ms, as long as the tree is showing
			// note that this fn is launched as soon as the tree is initialized; tree_scrolled is also attached to the scroll event of the tree
			if ($('[data-case-tree-top-identifier=' + this.lsdoc_identifier + ']').length == 0) {
				// console.log("CANCELLING")
			} else {
				this.tree_scrolled()
				window.auto_tree_scrolled_timeout = setTimeout(x => this.auto_tree_scrolled(), 100)
			}
		},

		tree_scrolled() {
			// don't need to do anything in wiki_mode_view
			if (this.wiki_mode_view) return

			// position toolbar
			let $scroll_wrapper = $(this.$el).find('.k-case-tree-scroll-wrapper')
			// top: at the top of the tree area; yoke to scroll to make it look nice
			let st = $scroll_wrapper.scrollTop()
			let $toolbar = $(this.$el).find('.k-cfv-toolbar')
			let tbt
			if (this.$vuetify.breakpoint.xs) {
				tbt = $(this.$el).find('.k-case-tree-top').outerHeight()
			} else {
				tbt = 8 - st
				if (tbt < 0) tbt = 0
			}
			// width: the same width as the scroll wrapper
			let tbw = $scroll_wrapper.width()
			// left: yoked to the position of the tree area
			let tbl = $(this.$el).find('.k-case-tree').offset()?.left
			$toolbar.css({ top: tbt + 'px', width: tbw + 'px', left: tbl + 'px' })

			if (
				!this.maximized ||
        this.$vuetify.breakpoint.xs ||
        !this.active_node ||
        $(sr('[data-tree-key=$1]', this.active_node)).length == 0
			)
				return
			// when we scroll the tree, position the active tile to try to make it even with the node in the tree
			// see companion watcher function in CASETree.vue

			let [tile_jq, item_top, tile_top] = this.active_item_position()

			// console.log('tile_top: ' + tile_top + ' / current top: ' + tile_jq.css('top'))
			tile_jq.css('top', tile_top + 'px')
		},

		scroll_to_item(node_tree_key, scroll_time = 50) {
			if (empty(node_tree_key)) node_tree_key = this.active_node
			if (empty(node_tree_key)) return

			// note the complicated way we need to get to the scrollable div here; this could need to be updated if the div structure of CASEFrameworkViewer / CASETree changes in the future
			let tree_jq = $(this.$refs.main_viewer_tree.$el).parents('.k-case-tree-inner-wrapper').first()
			let node_jq = tree_jq.find(sr('[data-tree-key=$1]', node_tree_key))
			if (node_jq.length == 0) return

			let node_top = node_jq.position().top
			let node_bottom = node_top + node_jq.height()
			let tree_height = tree_jq.height()
			let current_scroll_top = tree_jq.scrollTop()

			let scroll_to
			if (node_top < 64) {
				// move up (so subtract from scollTop) 64 - node_top
				scroll_to = current_scroll_top - (64 - node_top)
			} else if (node_bottom > tree_height - 64) {
				// move down (so add to scrollTop)
				scroll_to = current_scroll_top + (node_bottom - tree_height + 64)
			}

			if (!empty(scroll_to)) tree_jq.animate({ scrollTop: scroll_to + 'px' }, scroll_time)
		},
		get_tree_item_elements() {
			const tree_root = this.$refs.main_viewer_tree?.$el
			if (!tree_root) return []

			return Array.from(tree_root.querySelectorAll('[data-case-tree-treeitem="true"]')).filter(element => {
				if (!element) return false
				const styles = window.getComputedStyle(element)
				if (styles.display === 'none' || styles.visibility === 'hidden') return false
				return element.getClientRects().length > 0
			})
		},
		get_visible_tree_keys() {
			return this.get_tree_item_elements()
				.map(element => element.getAttribute('data-case-tree-item-tree-key'))
				.filter(Boolean)
		},
		sync_focused_tree_key(preferred_tree_key = '') {
			if (!this.tree_keyboard_enabled) return ''

			const visible_tree_keys = this.get_visible_tree_keys()
			if (!visible_tree_keys.length) {
				this.focused_tree_key = ''
				return ''
			}

			const candidate_tree_key = (preferred_tree_key || this.focused_tree_key || this.active_node || this.cftree?.tree_key || visible_tree_keys[0]) + ''
			this.focused_tree_key = visible_tree_keys.includes(candidate_tree_key) ? candidate_tree_key : visible_tree_keys[0]
			return this.focused_tree_key
		},
		get_tree_item_tabindex(tree_key) {
			if (!this.tree_keyboard_enabled) return undefined
			const active_tree_key = (this.focused_tree_key || this.active_node || this.cftree?.tree_key || '') + ''
			return active_tree_key == (tree_key + '') ? 0 : -1
		},
		focus_tree_item(tree_key, options = {}) {
			if (!this.tree_keyboard_enabled || empty(tree_key)) return

			const resolved_tree_key = this.sync_focused_tree_key(tree_key + '')
			this.focused_tree_key = resolved_tree_key
			this.make_node_parents_open(resolved_tree_key)
			this.make_node_active(resolved_tree_key, false)

			this.$nextTick(() => {
				if (options.scroll !== false) this.scroll_to_item(resolved_tree_key)
				const tree_root = this.$refs.main_viewer_tree?.$el
				const tree_item = tree_root?.querySelector(sr('[data-case-tree-item-tree-key="$1"]', resolved_tree_key))
				tree_item?.focus?.()
			})
		},
		focus_first_tree_item() {
			const visible_tree_keys = this.get_visible_tree_keys()
			if (visible_tree_keys.length) this.focus_tree_item(visible_tree_keys[0], { scroll: false })
		},
		focus_last_tree_item() {
			const visible_tree_keys = this.get_visible_tree_keys()
			if (visible_tree_keys.length) this.focus_tree_item(visible_tree_keys[visible_tree_keys.length - 1])
		},
		move_tree_focus_by(delta) {
			if (!this.tree_keyboard_enabled) return

			const visible_tree_keys = this.get_visible_tree_keys()
			if (!visible_tree_keys.length) return

			const current_tree_key = this.sync_focused_tree_key()
			let current_index = visible_tree_keys.indexOf(current_tree_key)
			if (current_index < 0) current_index = 0

			let next_index = current_index + delta
			if (next_index < 0) next_index = 0
			if (next_index > visible_tree_keys.length - 1) next_index = visible_tree_keys.length - 1

			this.focus_tree_item(visible_tree_keys[next_index])
		},
		handle_tree_item_focus(tree_key) {
			if (!this.tree_keyboard_enabled || empty(tree_key)) return
			this.focused_tree_key = tree_key + ''
			this.make_node_parents_open(tree_key)
			if (this.active_node != tree_key) this.make_node_active(tree_key, false)
		},
		get_active_item_card_container() {
			return this.$refs.main_viewer_tree?.$refs.active_item_wrapper || null
		},
		get_active_item_card_focus_targets() {
			const container = this.get_active_item_card_container()
			if (!container) return []

			const focus_targets = Array.from(container.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')).filter(element => {
				if (!element) return false
				if (element.disabled) return false
				if (element.getAttribute('aria-hidden') === 'true') return false
				const styles = window.getComputedStyle(element)
				if (styles.display === 'none' || styles.visibility === 'hidden') return false
				return element.getClientRects().length > 0
			})

			if (focus_targets.length) return focus_targets
			return [container]
		},
		enter_active_item_card(origin_tree_key = '') {
			const resolved_tree_key = origin_tree_key || this.focused_tree_key || this.active_node
			if (empty(resolved_tree_key)) return

			this.tree_card_focus_origin_key = resolved_tree_key + ''
			this.focused_tree_key = resolved_tree_key + ''
			this.make_node_parents_open(resolved_tree_key)
			this.make_node_active(resolved_tree_key, false)

			this.$nextTick(() => {
				window.setTimeout(() => {
					const container = this.get_active_item_card_container()
					if (!container) return
					this.tree_card_focus_trap_active = true
					const focus_targets = this.get_active_item_card_focus_targets()
					const first_focus_target = focus_targets.find(element => element !== container) || focus_targets[0] || container
					first_focus_target?.focus?.()
				}, 40)
			})
		},
		exit_active_item_card() {
			const focus_origin_key = this.tree_card_focus_origin_key || this.focused_tree_key || this.active_node
			this.tree_card_focus_trap_active = false
			this.tree_card_focus_origin_key = ''
			if (!empty(focus_origin_key)) this.focus_tree_item(focus_origin_key)
		},
		handle_tree_card_document_keydown(event) {
			if (!this.tree_card_focus_trap_active) return

			const container = this.get_active_item_card_container()
			if (!container) {
				this.tree_card_focus_trap_active = false
				return
			}

			if (event.key === 'Escape' || event.key === 'Esc') {
				event.preventDefault()
				event.stopPropagation()
				this.exit_active_item_card()
				return
			}

			if (event.key !== 'Tab') return

			this.tree_card_last_tab_direction = event.shiftKey ? -1 : 1

			const focus_targets = this.get_active_item_card_focus_targets()
			if (!focus_targets.length) {
				event.preventDefault()
				container.focus?.()
				return
			}

			const active_element = document.activeElement
			const first_focus_target = focus_targets[0]
			const last_focus_target = focus_targets[focus_targets.length - 1]
			const focus_is_inside = container.contains(active_element)

			if (event.shiftKey) {
				if (!focus_is_inside || active_element === first_focus_target) {
					event.preventDefault()
					last_focus_target.focus()
				}
				return
			}

			if (!focus_is_inside || active_element === last_focus_target) {
				event.preventDefault()
				first_focus_target.focus()
			}
		},
		handle_tree_card_document_focusin(event) {
			if (!this.tree_card_focus_trap_active) return

			const container = this.get_active_item_card_container()
			if (!container) {
				this.tree_card_focus_trap_active = false
				return
			}

			if (container.contains(event.target)) return

			const focus_targets = this.get_active_item_card_focus_targets()
			if (!focus_targets.length) {
				container.focus?.()
				return
			}

			const target = this.tree_card_last_tab_direction < 0 ? focus_targets[focus_targets.length - 1] : focus_targets[0]
			target?.focus?.()
		},

		adjust_font_size(delta) {
			this.font_size += delta
			if (this.font_size < -2) this.font_size = -2
			if (this.font_size > 2) this.font_size = 2
		},

		set_show_chooser_fn(val) {
			// this can be used to set the show_chooser_fn to a function, then set it back to false when it's done
			this.show_chooser_fn = val
		},

		find_cfitem_from_key(node, key) {
			if (empty(node)) return null
			if (node.tree_key == key) return node.cfitem
			if (empty(node.children) || node.children.length == 0) return null
			for (let child of node.children) {
				let r = this.find_cfitem_from_key(child, key)
				if (!empty(r)) return r
			}
			return null
		},

		clear_last_clicked_node() {
			this.search_panel_to_back()

			// if we're in wiki_mode_view, something is always selected, so don't clear here (if you click on a different node, that one will be selected)
			if (this.wiki_mode_view) return

			this.$store.commit('set', [this.framework_record, 'last_clicked_node', ''])
		},

		show_expanded_item(item, item_framework_record) {
			// note that "item" here must be a tree node (not a cfitem)
			if (!item_framework_record) item_framework_record = this.framework_record
			this.$refs.expanded_tile.show_item(item, item_framework_record)
		},

		hide_expanded_item() {
			this.$refs.expanded_tile.hide()
		},

		// validate that the given tree_key corresponds to the given item_identifier
		validate_tree_key(tree_key, item_identifier) {
			// return empty string if not valid, or the received tree_key if valid
			let cfitem = this.cfitems[item_identifier]
			if (empty(cfitem)) return ''

			for (let tree_node of cfitem.tree_nodes) {
				if (tree_node.tree_key + '' == tree_key + '') {
					// found the key; return it
					return tree_key
				}
			}
			// key not found, so return empty string
			return ''
		},

		// the 'make_item_xxx' fns handle cases where work on the basis of an identifier; the 'make_node_xxx' fns handle cases where we want to do something with a single node
		make_item_open(identifier, tree_key) {
			let cfitem = this.cfitems[identifier]
			if (empty(cfitem)) return

			// the tree_key param can be 'show_all' to show all nodes with the given identifier;
			// or it could specify a specific tree_key;
			// or it could be empty, in which case we show the first node for the identifier
			let show_all = false
			if (tree_key == 'show_all') {
				show_all = true
				tree_key = ''
			}

			// open this item
			for (let tree_node of cfitem.tree_nodes) {
				// if we're looking for a specific tree_key and this isn't it, continue
				if (!empty(tree_key) && tree_node.tree_key + '' != tree_key + '') continue

				// don't open nodes that have no children
				if (tree_node.children.length == 0) continue

				if (!this.open_nodes[tree_node.tree_key]) {
					this.$store.commit('set', [this.open_nodes, tree_node.tree_key + '', true])
				}

				// if we're not showing all nodes, break after we show one
				if (!show_all) break
			}
		},

		make_item_active(identifier, tree_key) {
			let cfitem = identifier == this.cfdocument.identifier ? this.cfdocument : this.cfitems[identifier]
			if (empty(cfitem)) return

			// activate this item the first place it appears in the case_tree, or at tree_key of provided
			for (let node of cfitem.tree_nodes) {
				// if a tree_key was provided, continue until we find the node with that tree_key
				if (!empty(tree_key) && node.tree_key + '' != tree_key + '') continue
				this.$store.commit('set', [this.framework_record, 'active_node', node.tree_key])
				break
			}
		},

		make_item_parents_open(identifier, tree_key) {
			let cfitem = this.cfitems[identifier]
			if (empty(cfitem)) return
			// see logic around tree_key for make_item_open above

			let show_all = false
			if (tree_key == 'show_all') {
				show_all = true
				tree_key = ''
			}

			// open the item's ancestors
			for (let node of cfitem.tree_nodes) {
				if (!empty(tree_key) && node.tree_key + '' != tree_key + '') continue

				let temp_node = node
				while (!empty(temp_node.parent_node)) {
					this.$store.commit('set', [this.open_nodes, temp_node.parent_node.tree_key + '', true])
					temp_node = temp_node.parent_node
				}

				if (!show_all) break
			}
		},

		// the 'make_item_xxx' fns handle cases where work on the basis of an identifier; the 'make_node_xxx' fns handle cases where we want to do something with a single node
		make_node_open(tree_key) {
			let tree_node = this.cfo.tree_nodes_hash[tree_key]
			if (empty(tree_node)) return

			// don't open nodes that have no children
			if (tree_node.children.length == 0) return

			if (!this.open_nodes[tree_node.tree_key]) {
				this.$store.commit('set', [this.open_nodes, tree_node.tree_key + '', true])
			}
		},

		make_node_active(tree_key, and_set_url) {
			// if tree_key is empty string, or this tree_key doesn't exist, clear the active_node
			let tree_node = this.cfo.tree_nodes_hash[tree_key]
			let identifier

			if (empty(tree_node)) {
				tree_key = ''
				identifier = ''
			} else {
				identifier = tree_node.cfitem.identifier
			}

			// activate this node (or set to empty string)
			this.$store.commit('set', [this.framework_record, 'active_node', tree_key])

			// if and_set_url is true, also set the url
			if (and_set_url === true) {
				this.set_starting_lsitem_identifier(identifier)
			}
		},

		make_node_parents_open(tree_key) {
			let tree_node = this.cfo.tree_nodes_hash[tree_key]
			if (empty(tree_node)) return

			// open the node's ancestors
			while (!empty(tree_node.parent_node)) {
				this.$store.commit('set', [this.open_nodes, tree_node.parent_node.tree_key + '', true])
				tree_node = tree_node.parent_node
			}

			// console.log('make_node_parents_open', JSON.stringify(this.open_nodes))
		},

		collapse_all(callback) {
			// putting this and the expand_all code in the worker shell makes it so that the loading indicator shows while vue is showing things
			U.loading_start('Collapsing all items…')
			this.$worker
				.run(() => {
					return
				}, []) // this is where we pass the original data into the fn
				.then(() => {
					U.loading_stop()
					this.$store.commit('set', [this.framework_record, 'open_nodes', {}])
					this.$store.commit('set', [this.framework_record, 'active_node', ''])
					this.$store.commit('set', [this.framework_record, 'last_clicked_node', ''])

					// reset item identifier from url
					this.set_starting_lsitem_identifier('')

					if (typeof callback == 'function') callback()
				}).catch(() => {
					U.loading_stop()
					console.error // logs any possible error
				})
		},

		expand_all() {
			// not currently accessible from the UI
			U.loading_start('Expanding all items…')
			this.$store.commit('set', [this.framework_record, 'last_clicked_node', ''])
			this.$worker
				.run(
					cfo => {
						let obj = {}
						for (let identifier in cfo.cfitems) {
							for (let i = 0; i < cfo.cfitems[identifier].tree_nodes.length; ++i) {
								if (cfo.cfitems[identifier].tree_nodes[i].children.length > 0) {
									obj[cfo.cfitems[identifier].tree_nodes[i].tree_key] = true
								}
							}
						}
						return obj
					},
					[this.cfo]
				) // this is where we pass the original data into the fn
				.then(obj => {
					U.loading_stop()
					this.$store.commit('set', [this.framework_record, 'open_nodes', obj])
					// reset item identifier from url
					this.set_starting_lsitem_identifier('')
				}).catch(() => {
					U.loading_stop()
					console.error // logs any possible error
				})
		},

		hide_tree() {
			// when this framework's tree is hidden, clear start_string
			// console.log('hide_tree')
			this.$store.commit('set', ['start_string', ''])
			this.$store.commit('set', ['start_identifier', ''])
			this.$store.commit('set', ['start_string_identifiers', ''])
			this.$emit('hide_tree')
		},

		more_info(item, flag) {
			this.more_info_item = {}

			if (item == 'document' || empty(item.parent_node)) {
				this.more_info_item = this.framework_record.json.CFDocument
				// let cfdocument
				// if (item == 'document') cfdocument = this.cfo.cfdocument
				// else cfdocument = item.cfitem
				// for (let key in cfdocument) {
				// 	if (key == 'tree_nodes' || key == 'stats') continue
				// 	this.more_info_item[key] = cfdocument[key]
				// }
				this.more_info_type = 'document'
			} else {
				// CF item
				this.more_info_item = this.framework_record.json.CFItems.find(x => x.identifier == item.cfitem.identifier)
				// for (let key in item.cfitem) {
				// 	if (key == 'tree_nodes' || key == 'stats') continue
				// 	this.more_info_item[key] = item.cfitem[key]
				// }
				this.more_info_type = 'item'

				// removed 3/4/2023
				// if (item.cfitem.stats.change) this.more_info_item.change = item.cfitem.stats.change
			}

			if (flag == 'show_history') this.more_info_show_delta = true
			else this.more_info_show_delta = false
		},

		print_view(item) {
			this.print_node = item
			this.show_print_dialog = true
		},

		framework_viewer_clicked() {
			this.clear_last_clicked_node()

			// if not maximized, when framework viewer is clicked, make sure it's in front of the editor
			if (this.maximized) return
			$('.k-case-tree-outer-wrapper').css('z-index', 101)
			$('.k-case-item-editor-outer').css('z-index', 100)
		},

		toggle_move_mode(flag, param) {
			if (this.show_move_fn) {
				this.show_move_fn = false
				this.move_node_shift_right = null
			} else {
				// set the show_move_fn; this will both toggle the move interface to show, and toggle the tree to show move handles
				this.show_move_fn = evt => {
					this.$refs.move_editor.item_move_drag_complete(evt)
				}
				// cancel document/item edit if open
				this.cancel_edit_document()
				this.cancel_item_edit()
			}

			// set move_mode_flag and param -- e.g. SideBySideEditor calls this with a flag so that when the user finishes moving we re-open the SideBySideEditor
			this.move_mode_flag = flag
			this.move_mode_param = param
		},

		start_move_node_conversion() {
			this.move_node_shift_right = null
			this.show_move_node_conversion = true
		},

		finish_move_node_conversion(tree_key) {
			if (tree_key) this.move_node_shift_right = tree_key
			this.show_move_node_conversion = false
		},

		sort_children(parent, confirmed) {
			if (!confirmed) {
				this.$confirm({
					text: 'Are you sure you want to sort the child items of this node?<br><b>This is not undoable.</b>',
					acceptIconAfter: 'fas fa-circle-arrow-right',
					acceptText: 'Sort'
				}).then(y => {
					this.sort_children(parent, true)
				}).catch(n => {
					console.log(n)
				}).finally(f => {})
				return
			}

			// sort by hcs/fs -- have to do this for all places where the parent exists in the tree
			for (let parent_node of parent.cfitem.tree_nodes) {
				parent_node.children.sort((a, b) => {
					// sort by hcs if either or both have one
					if (a.cfitem.humanCodingScheme && !b.cfitem.humanCodingScheme) return -1
					if (!a.cfitem.humanCodingScheme && b.cfitem.humanCodingScheme) return 1
					if (a.cfitem.humanCodingScheme && b.cfitem.humanCodingScheme) {
						return U.natural_sort(a.cfitem.humanCodingScheme, b.cfitem.humanCodingScheme)
					}

					// else sort by fs
					return U.natural_sort(a.cfitem.fullStatement, b.cfitem.fullStatement)
				})
			}

			// construct data for update
			let data = {
				lsdoc_identifier: this.lsdoc_identifier,
				CFAssociations: []
			}

			let data_updated = false
			for (let parent_node of parent.cfitem.tree_nodes) {
				let arr = parent_node.children
				for (let i = 0; i < arr.length; ++i) {
					let child = arr[i]
					// console.log(sr('$1: $2', child.cfitem.humanCodingScheme, child.cfitem.fullStatement))

					// for forms' sake, update the sequence in the node, although we really only use these node.sequence values when the tree is originally created
					this.$store.commit('set', [child, 'sequence', i + 1])

					// update the sequenceNumber in the framework_record, and add to data
					if (!data_updated) {
						// find the association for this item
						let index = U.find_cfassociation_index(
							this.framework_record.json.CFAssociations,
							child.cfitem.identifier,
							parent.cfitem.identifier
						)
						let target_association = this.framework_record.json.CFAssociations[index]

						// set lastChangeDateTime to *NOW* so server will update to the correct timestamp
						this.$store.commit('set', [target_association, 'lastChangeDateTime', '*NOW*'])
						this.$store.commit('set', [target_association, 'sequenceNumber', i + 1])
						data.CFAssociations.push(new CFAssociation(target_association))
					}
				}

				// set data_updated to true here so that we only add CFAssociations to data when we process the first parent
				data_updated = true
			}

			// console.log(data)

			// save the updated cfassociations
			data.show_spinner = true
			this.$store.dispatch('save_framework_data', data).then(() => {
				// when done, update the CFAssociations' lastChangeDateTime
				for (let cfa of data.CFAssociations) {
					if (cfa.lastChangeDateTime == '*NOW*') {
						let json_CFA = this.framework_record.json.CFAssociations.find(x => x.identifier == cfa.identifier)
						this.$store.commit('set', [json_CFA, 'lastChangeDateTime', this.$store.state.framework_lastChangeDateTime])
					}
				}
			})
		},

		toggle_batch_edit_mode() {
			if (this.show_checkbox_fn) {
				this.show_checkbox_fn = false
			} else {
				// set the show_checkbox_fn; this will both toggle the batch editor interface to show, and toggle the tree to show checkboxes
				this.show_checkbox_fn = (identifier_clicked, tree_key_clicked, val, evt) => {
					this.$refs.batch_editor.checkbox_clicked(identifier_clicked, tree_key_clicked, val, evt)
				}
				// cancel document/item edit if open
				this.cancel_edit_document()
				this.cancel_item_edit()
			}
		},

		toggle_make_associations(node, restoreFocusElement = null) {
			// if on, turn off
			if (this.show_make_associations_interface) {
				this.show_make_associations_interface = false
				this.make_associations_restore_focus_element = null
			} else {
				// else turn on; if node is provided, chose that node on open
				this.make_associations_restore_focus_element = restoreFocusElement
				this.show_make_associations_interface = true
				if (node && node.tree_key) {
					setTimeout(x => {
						this.current_editor.item_chosen_on_left(node)
					}, 100)
				}
				// make sure associations are showing when this goes on
				this.show_associations = true

				// cancel document/item edit if open
				this.cancel_edit_document()
				this.cancel_item_edit()
			}
		},

		toggle_show_comments() {
			if (!this.signed_in && !this.public_review_on) {
				this.$alert('Please sign in to create or view comments.')
			} else {
				this.show_comments = !this.show_comments
			}
		},

		archive_framework() {
			if (this.is_mirror) {
				this.mirror_update_by_user(this.framework_record)
				return
			}

			this.$prompt({
				title: 'Archive Framework',
				text: '<p>Enter a short description to identify the archive. (The archive’s record will always include the current date and time, so there’s no need to specify the date/time in your description.)</p>',
				promptType: 'textarea',
				initialValue: '',
				acceptText: 'Save Archive',
				dialogMaxWidth: 620
			}).then(phrase => {
				if (empty(phrase)) {
					this.$alert('You must enter a description of the archive.')
					return
				}

				let payload = {
					lsdoc_identifier: this.CFDocument.identifier,
					phrase: $.trim(phrase),
					cf_item_count: this.framework_record.json.CFItems.length,
					cf_association_count: this.framework_record.json.CFAssociations.length
				}
				this.$store.dispatch('archive_lsdoc', payload).then(archive_time => {
					archive_time = archive_time.replace(/(\d\d\d\d-\d\d-\d\d)--(\d\d)-(\d\d)-(\d\d)/, '$1 $2:$3:$4')
					archive_time = date.format(U.convert_to_local_date(archive_time, 0), 'M/D/YYYY h:mm A') // Jan 1, 2019 3:12 PM

					// Update last_stamped_framework_timestamp to prevent repeated prompts
					this.last_stamped_framework_timestamp = this.framework_record.json.CFDocument.lastChangeDateTime

					this.$alert(sr('Framework archived at $1 (local time).', archive_time)).then(x => {
						// trigger a reload of the framework's archives
						vapp.reset_framework_update_report_archives(this.CFDocument.identifier)
					})
				})
			}).catch(n => {
				console.log(n)
			}).finally(f => {})
		},

		delete_framework() {
			this.$confirm({
				title: 'Delete Framework',
				text: 'Are you sure you want to delete this framework? The framework will be moved to the archives, and will no longer appear in the framework list.',
				acceptText: 'Delete',
				acceptColor: 'red'
			}).then(y => {
				this.$store.dispatch('delete_lsdoc', this.CFDocument.identifier).then(() => {
					this.hide_tree()
				})
			}).catch(n => {
				console.log(n)
			}).finally(f => {})
		},

		check_out_for_editing() {
			// mirrored framworks do not need an edit lock and abbreviated options under edit menus
			if (this.is_mirror) {
				this.editing_enabled = true
				return
			}

			let payload = {
				framework_identifier: this.CFDocument.identifier,
				edit_action: 'framework_checkout',
				cf_item_count: this.framework_record.json.CFItems.length,
				cf_association_count: this.framework_record.json.CFAssociations.length
			}

			this.$store
				.dispatch('manage_edit_lock', payload)
				.then(result => {
					console.log('edit request: ' + result.status)

					this.editing_enabled = true
				}).catch(e => {
					// if this doesn't work, set editing_enabled to false
					this.editing_enabled = false
					console.log(e)
				})
		},

		async check_in_for_editing(showPrompt = false) {
			// Only prompt to stamp when explicitly requested (e.g., user clicks "Stop editing")
			if (showPrompt) {
				await this.check_for_stamp_reminder_dialog()
			}
			let payload = {
				framework_identifier: this.CFDocument.identifier,
				edit_action: 'framework_checkin'
			}
			this.$store.dispatch('manage_edit_lock', payload)
			this.editing_enabled = false
		},

		toggle_editing() {
			if (this.editing_enabled) this.check_in_for_editing(true)
			else this.check_out_for_editing()
		},

		edit_document(action) {
			this.cancel_item_edit()
			this.editing_document = true
			if (action) {
				this.$nextTick(x => this.current_editor[action]())
			}
		},

		cancel_edit_document() {
			this.editing_document = false
		},

		edit_item(item, action) {
			this.cancel_edit_document()
			this.edited_node = null
			this.$nextTick(x => {
				this.edited_node = item
				// do action
				if (action) {
					// for actions 'suggestions_only' or 'apply_suggestions', on nextTick (after the editor is showing...)
					if (action == 'suggestions_only') {
						this.$nextTick(x => {
							// call the 'enter_suggestions_only_mode' fn in the editor, passing in arguments[2], which can include an object with previous suggested edits
							this.current_editor.enter_suggestions_only_mode(arguments[2])
						})
					} else if (action == 'apply_suggestions') {
						this.$nextTick(x => {
							// call the 'apply_suggestions' fn in the editor, passing in arguments[2], which will include an object with previous suggested edits
							this.current_editor.apply_suggestions(arguments[2])
						})
					} else {
						// else execute the fn designated by action
						this.$nextTick(x => this.current_editor[action]())
					}
				}
			})
		},

		cancel_item_edit() {
			this.edited_node = null
		},

		item_edit_suggestion_saved(CFItem) {
			// cancel the item editor; then the tile will reappear with the comment editor still open
			this.cancel_item_edit()

			// then after a tick to let things settle down, pass the edited CFItem back to the comment editor
			setTimeout(x => {
				if (this.comment_editor) {
					this.comment_editor.item_edits_suggested(CFItem)
				}
			}, 100)
		},

		item_edit_suggestion_applied(CFItem) {
			// cancel the item editor; then the tile will reappear with the comment editor still open
			this.cancel_item_edit()

			// after a tick to let the editor close and the comment viewer show, pass the edited CFItem back to this.comment_editor (which will actually be the comment viewer)
			setTimeout(x => {
				if (this.comment_editor) {
					this.comment_editor.suggested_edits_applied(CFItem)
				}
			}, 100)
		},

		edit_new_child(new_child_node) {
			this.edited_node = null
			this.$nextTick(() => {
				this.edited_node = new_child_node
				// this.make_node_active(new_child_node)
			})
		},

		association_removable(assoc) {
			if (!this.editing_enabled) return false

			// the "fake" alias and copy associations aren't deletable
			if (
				assoc.associationType == 'copiedFromSource' ||
        assoc.associationType == 'aliasOf' ||
        assoc.associationType == 'aliasOfExternal'
			)
				return false

			// if we're in a mirror, the assoc is removable IFF the assoc is in a crosswalk framework
			if (this.is_mirror) {
				// so if the assoc is in the viewer framework's CFAssociations, it's *not* removable
				if (this.framework_record.json.CFAssociations.find(x => x.identifier == assoc.identifier)) {
				} else {
					// look for the other framework's identifier in origin and destination nodes' titles
					let other_framework_identifier = U.get_framework_identifier_from_node_uri_title(
						assoc.originNodeURI.title
					)
					if (!other_framework_identifier || other_framework_identifier == this.framework_record.lsdoc_identifier) {
						other_framework_identifier = U.get_framework_identifier_from_node_uri_title(
							assoc.destinationNodeURI.title
						)
					}

					if (other_framework_identifier) {
						// if we found another framework and we have a crosswalk framework for that other framework
						let crosswalk_framework_record = U.get_crosswalk_framework_record(
							this.framework_record.lsdoc_identifier,
							other_framework_identifier
						)
						if (crosswalk_framework_record) {
							// then if the assoc is in that crosswalk framework, removable!
							if (crosswalk_framework_record.json.CFAssociations.find(x => x.identifier == assoc.identifier)) {
								return true
							}
						}
					}
				}
				// if we fall through to here, the assoc is not in a crosswalk framework, so it's not editable here.
				return false
			}

			return true
		},

		// called from an item tile, AssociationsTable, or MakeAssociationsInterface
		remove_association(assoc) {
			return new Promise((resolve, reject) => {
				if (this.remove_association_confirmed) {
					this.remove_association_finish(assoc)
					resolve()
				} else {
					this.$confirm({
						title: 'Delete Association',
						text: 'Are you sure you want to delete this association? (You can always re-create the association later if you wish.)',
						acceptText: 'Delete Association',
						acceptColor: 'red'
					}).then(y => {
						this.remove_association_finish(assoc)
						this.remove_association_confirmed = true
						resolve()
					}).catch(n => {
						console.log(n)
					}).finally(f => {})
				}
			})
		},

		remove_association_finish(assoc, flags) {
			// associations can be housed either in the "home" framework or a dedicated crosswalk framework
			if (this.framework_record.json.CFAssociations.find(x => x.identifier == assoc.identifier)) {
				console.warn('delete from home framework')
				// note that delete_associations will call the fn to update association counts
				let payload = {
					framework_record: this.framework_record,
					associations_to_delete: [assoc],
				}
				if (!empty(flags)) {
					for (let key in flags) payload[key] = flags[key]
				}

				this.$store.dispatch('delete_associations', payload).then(x => {
					// clear cat value for the destination node (used in MakeAssociationsInterface)
					let right_node = this.framework_record.cfo.cfitems[assoc.destinationNodeURI.identifier]?.tree_nodes[0]
					if (right_node) this.$store.commit('set', [right_node, 'cat', null])
				})
			
			} else {
				// look for the other framework's identifier in origin and destination nodes' titles
				let other_side = 'originNodeURI'
				let other_framework_identifier = U.get_framework_identifier_from_node_uri_title(
					assoc.originNodeURI.title
				)
				if (!other_framework_identifier || other_framework_identifier == this.framework_record.lsdoc_identifier) {
					other_side = 'destinationNodeURI'
					other_framework_identifier = U.get_framework_identifier_from_node_uri_title(
						assoc.destinationNodeURI.title
					)
				}

				if (other_framework_identifier) {
					let crosswalk_framework_record = U.get_crosswalk_framework_record(
						this.framework_record.lsdoc_identifier,
						other_framework_identifier
					)
					if (crosswalk_framework_record) {
						console.warn('delete from other framework ' + other_framework_identifier)
						let payload = {
							framework_record: crosswalk_framework_record,
							associations_to_delete: [assoc],
							check_out_and_in: 'yes'
						}
						if (!empty(flags)) {
							for (let key in flags) payload[key] = flags[key]
						}

						this.$store.dispatch('delete_associations', payload).then(x => {
							// clear cat value for the destination node (used in MakeAssociationsInterface)
							let right_framework = this.framework_records.find(
								x => x.lsdoc_identifier == other_framework_identifier
							)
							if (!empty(right_framework)) {
								let right_node = right_framework.cfo.cfitems[assoc[other_side].identifier]?.tree_nodes[0]
								if (right_node) this.$store.commit('set', [right_node, 'cat', null])
							}
						})
						return
					}
				}
				// if we get to here the delete didn't work
				this.$alert(
					'We couldn’t find the framework where the association “lives”, so the association couldn’t be deleted.'
				)
			}
		},

		// note that we may or may not specify an item_tree_key
		toggle_associated_item(associationType, node1, framework_identifier2, item_identifier2, item_tree_key2) {
			if (
				this.last_clicked_association_identifier &&
				this.association_framework_tree_identifier == framework_identifier2 &&
				this.association_framework_tree_item_identifier == item_identifier2 &&
				this.association_framework_tree_item_tree_key == item_tree_key2 &&
				this.association_framework_tree_associationType == associationType
			) {
				this.hide_associated_item()
			} else {
				// do this nextTick thing so that this re-shows the association line when the user has clicked off of the originally-shown item, then clicks back in and wants to re-show the line
				this.hide_associated_item()
				this.$nextTick(x =>
					this.show_associated_item(associationType, node1, framework_identifier2, item_identifier2, item_tree_key2)
				)
			}
		},

		show_associated_item(associationType, node1, framework_identifier2, item_identifier2, item_tree_key2) {
			this.association_framework_tree_left_node = node1
			this.association_framework_tree_associationType = associationType
			this.association_framework_tree_identifier = framework_identifier2
			this.association_framework_tree_item_identifier = item_identifier2
			this.association_framework_tree_item_tree_key = item_tree_key2

			// this causes this association to be highlighted (bolded) in CASEItemAssociations.vue
			this.last_clicked_association_identifier = item_identifier2
			this.last_clicked_association_item_tree_key = item_tree_key2

			this.$nextTick(x => {
				this.association_line_from_tree_key = node1.tree_key
			})
		},

		// when a track-changes icon is clicked on the left, show the corresponding item on the right
		show_archived_item(node1) {
			this.hide_associated_item()
			this.$nextTick(x => {
				this.association_framework_tree_left_node = node1
				this.association_framework_tree_associationType = 'archive'
				this.association_framework_tree_identifier = this.lsdoc_identifier
				this.association_framework_tree_item_identifier = node1.cfitem.identifier

				this.make_node_active(node1.tree_key)
				this.make_node_open(node1.tree_key) // make the node open when we show its archived equivalent
				this.$store.commit('set', [this.framework_record, 'last_clicked_node', node1.tree_key])

				this.$nextTick(x => {
					this.association_line_from_tree_key = node1.tree_key
				})
			})
		},

		// when an archived item's track-changes icon is clicked on the right, show the corresponding item on the left
		show_current_item_from_archive(archive_node) {
			let item_identifier = archive_node.cfitem.identifier
			let cfitem = this.framework_record.cfo.cfitems[item_identifier]
			// if item doesn't exist in the current tree, return
			if (!cfitem) return

			// clear the line that's currently showing; then select the item on the left and draw a new line
			this.association_line_from_tree_key = ''
			this.$nextTick(x => {
				this.association_framework_tree_left_node = current_node
				this.association_framework_tree_associationType = 'archive'
				this.association_framework_tree_identifier = this.lsdoc_identifier
				this.association_framework_tree_item_identifier = item_identifier
				this.association_framework_tree_item_tree_key = archive_node.tree_key

				let current_node = cfitem.tree_nodes[0]
				this.make_node_parents_open(current_node.tree_key)
				this.make_node_active(current_node.tree_key)
				this.make_node_open(current_node.tree_key) // make the node open when we show its archived equivalent
				this.$store.commit('set', [this.framework_record, 'last_clicked_node', current_node.tree_key])

				this.$nextTick(x => {
					this.association_line_from_tree_key = current_node.tree_key
				})
			})
		},

		switch_associated_item(framework_identifier2, item_identifier2, item_tree_key2) {
			// this is for a pretty specific purpose: if you're viewing an item on the left, and that item has associated items,
			// and you click to view an associated item on the right, and you click to expand the associated item's tile,
			// and that associated item itself has associations, and you click on one of the associated item's associated items...
			// we want to switch to showing the associated item's associated item on the right,
			// then immediately expand the tile of the associated item's associated item
			this.hide_associated_item()

			// do this on next tick so that the open_item fn will be called in CASEAssociatedItemTree -- this opens the item's parents
			this.$nextTick(x => {
				this.association_framework_tree_left_node = null // ??
				this.association_framework_tree_associationType = '' // ??
				this.association_framework_tree_identifier = framework_identifier2
				this.association_framework_tree_item_identifier = item_identifier2
				this.association_framework_tree_item_tree_key = item_tree_key2

				// this causes this association to be highlighted in CASEItemAssociations.vue
				this.last_clicked_association_identifier = item_identifier2
				this.last_clicked_association_item_tree_key = item_tree_key2

				// then after another brief delay, simulate clicking the "expand" button on the newly-revealed item
				setTimeout(x => {
					if (
						vapp.last_clicked_item_component &&
            vapp.last_clicked_item_component.item &&
            vapp.last_clicked_item_component.item.cfitem.identifier == this.association_framework_tree_item_identifier
					) {
						vapp.last_clicked_item_component.show_expanded_tile()
					}
				}, 10)
			})
		},

		hide_associated_item() {
			this.association_framework_tree_left_node = null
			this.association_framework_tree_associationType = ''
			this.association_framework_tree_identifier = null
			this.association_framework_tree_item_identifier = null
			this.association_framework_tree_item_tree_key = null
			this.association_line_from_tree_key = ''
			this.last_clicked_association_identifier = null
			this.last_clicked_association_item_tree_key = null
		},

		// if window is resized, save width and height to store
		tree_resized(x, y, width, height) {
			this.$store.commit('set', ['case_tree_viewer_width', width])
			this.$store.commit('set', ['case_tree_viewer_height', width])
		},

		toggle_maximize_tree(maximized_val) {
			if (typeof maximized_val != 'boolean') maximized_val = !this.maximized
			this.maximized = maximized_val
		},

		// view the original item that a duplicated node was copied from
		// NOTE: view_original_item and view_item_copies aren't currently used, but might be useful in the future
		view_original_item(copied_node) {
			// return if the copied item isn't a duplicate (?)
			if (
				!(
					copied_node.cfitem.extensions.sourceItemIdentifier &&
          copied_node.cfitem.extensions.sourceItemIdentifier != copied_node.cfitem.identifier
				)
			)
				return

			let sourceItemIdentifier = copied_node.cfitem.extensions.sourceItemIdentifier

			// get the original's lsdoc_identifier (which might be the same framework we're viewing)
			U.get_lsdoc_identifier_from_item_identifier(sourceItemIdentifier)
				.then(lsdoc_identifier => {
					// found it -- so show the original in the association tree (the associated item tree component will load the framework's cfo if necessary)
					this.show_associated_item('', copied_node, lsdoc_identifier, sourceItemIdentifier)
				}).catch(e => {
					console.log(e)
					this.$alert('Couldn’t identify the source of the original item.')
				})
		},

		// view copies of an node, which could be an alias (look up other items with the same identifier)
		// and/or a duplicate (look up the item whose identifier matches copied_node's sourceItemIdentifier)
		view_item_copies(copied_node) {
			// let identifier = this.item_is_copy ? this.item.cfitem.extensions.sourceItemIdentifier : this.item.cfitem.identifier
			// see if the copied item is a duplicate
			let copied_node_identifier = copied_node.cfitem.identifier
			let sourceItemIdentifier = copied_node.cfitem.extensions.sourceItemIdentifier
			let copied_node_is_duplicate = sourceItemIdentifier && sourceItemIdentifier != copied_node_identifier

			// collapse all items, then open copies; by definition we will re-open the current item
			this.collapse_all(x => {
				// start by opening all aliases of copied_node, and setting count to the number of nodes we open here
				this.make_item_parents_open(copied_node_identifier, 'show_all')
				let count = copied_node.cfitem.tree_nodes.length

				// now find duplicates of the item or the item's sourceItemIdentifier
				let found_original = false
				for (let cfitem of this.framework_record.json.CFItems) {
					if (cfitem.identifier == copied_node_identifier) continue

					let open_it = false
					// the original that copied_node was duplicated from
					if (cfitem.identifier == sourceItemIdentifier) {
						open_it = true
						found_original = true

						// a duplicate of copied_node
					} else if (cfitem.extensions.sourceItemIdentifier == copied_node_identifier) {
						open_it = true

						// another duplicate of copied_node's original
					} else if (sourceItemIdentifier && cfitem.extensions.sourceItemIdentifier == sourceItemIdentifier) {
						open_it = true
					}

					if (open_it) {
						this.make_item_parents_open(cfitem.identifier, 'show_all')
						++count
					}
				}
				// don't make items open -- if the items are folders with lots of children this makes things confusing
				// this.make_item_open(identifier, tree_key)

				// make the current item active, and clear last_clicked_node
				this.make_item_active(copied_node_identifier, copied_node.tree_key)
				this.$store.commit('set', [this.framework_record, 'last_clicked_node', ''])

				// do a snackbar; if the user clicks the btn on the snackbar, view the original item
				let options = {
					text: '',
					color: 'amber darken-4',
					snackbarTimeout: 5000,
					callback_fn: () => {
						this.view_original_item(copied_node)
					}
				}

				if (copied_node_is_duplicate) options.closeText = 'View Original'

				// if we didn't find a copy in this framework
				if (count == 0) {
					if (copied_node_is_duplicate) options.text = 'This item was duplicated from an item in another framework.'
					else
						options.text =
							'No copies of the item were found in this framework. The item may have been copied into a different framework.'

					// else we *did* find a copy in this framework
				} else {
					if (copied_node_is_duplicate) {
						if (found_original) options.text = 'This item was duplicated from another item.'
						else options.text = 'This item was duplicated from an item in another framework.'
					} else options.text = 'Copies are marked with amber triangles.'
				}

				this.$inform(options)
			})
		},

		//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
		// "Statistics" mode

		toggle_stats_mode() {
			if (this.stats_mode) {
				this.stats_mode = false
			} else {
				U.process_cfo_stats(this.$worker, this.cfo)
					.then(new_cfo => {
						U.loading_stop()
						this.$store.commit('set', [this.framework_record, 'cfo', new_cfo])
						this.stats_mode = true
					}).catch(() => {
						U.loading_stop()
						console.error // logs any possible error
					})
			}
		},

		//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
		// MISC FUNCTIONS

		// toggle whether "selected_items" are showing only -- used for embed mode
		toggle_show_selected_items(toggle_on) {
			if (typeof toggle_on != 'boolean') toggle_on = !this.framework_record.limit_to_selected_items
			this.$store.commit('set', [this.framework_record, 'limit_to_selected_items', toggle_on])

			// if we're toggling on, set open_selected_nodes_when_calculated to true so that we open the selected items' ancestors in the selected_nodes computed
			if (toggle_on) this.$store.commit('set', [this.framework_record, 'open_selected_nodes_when_calculated', true])
		},

		// split into multiple frameworks
		split_framework(evt) {
			let export_files = evt && evt.metaKey === true
			let export_files_text = ''
			if (export_files)
				export_files_text =
					'<div class="mt-2"><b>Since you were holding down the COMMAND/CTRL key when you clicked the command, the split files will be exported, rather than being saved to the server.</b></div>'

			let msg = sr(
				'This operation will split the current framework into $1 separate frameworks, one for each top-level item:<ul class="mt-2">',
				this.cftree.children.length
			)
			for (let child of this.cftree.children) {
				msg += sr('<li>$1</li>', child.cfitem.fullStatement)
			}
			msg += sr(
				'</ul><div class="mt-2">Each new document will take as its document identifier (GUID) the identifier of the top-level item in the current framework.</div><div class="mt-2">The current framework will remain unchanged.</div>$1<div class="mt-2">Do you want to proceed?</div>',
				export_files_text
			)
			this.$confirm({
				title: 'Split Into Multiple Frameworks',
				text: msg,
				acceptText: 'Create Split Frameworks',
				dialogMaxWidth: 800
			}).then(y => {
				this.split_framework_process(export_files)
			}).catch(n => {
				console.log(n)
			}).finally(f => {})
		},

		split_framework_process(export_files) {
			let ojson = this.framework_record.json
			let parent_ss_framework_data = this.framework_record.ss_framework_data

			U.loading_start('Creating frameworks…')
			let files_to_process = this.cftree.children.length

			// for each top-level item...
			for (let tc_node of this.cftree.children) {
				// get top-child identifier, then get the item JSON from ojson for this identifier
				let tc_identifier = tc_node.cfitem.identifier
				let tc_item = ojson.CFItems.find(x => x.identifier == tc_identifier)

				// copy document; add title of tc_node; use tc_node as identifier; server will update lastChangeDateTime
				let nf = {}
				nf.CFDocument = $.extend(true, {}, ojson.CFDocument)
				nf.CFDocument.title += ': ' + tc_item.fullStatement
				nf.CFDocument.identifier = tc_identifier

				// create regular expression for replacing old document identifier with new identifier; use it on the CFDocument uri
				let doc_id_re = new RegExp(ojson.CFDocument.identifier, 'gi')
				nf.CFDocument.uri = nf.CFDocument.uri.replace(doc_id_re, tc_identifier)

				// copy in definitions, then add shell for items and associations
				nf.CFDefinitions = $.extend(true, {}, ojson.CFDefinitions)
				nf.CFItems = []
				nf.CFAssociations = []

				// copy all items and associations that are children of this child
				this.$worker
					.run(
						(ojson, nf, tc_node) => {
							function build_framework(node) {
								let identifier = node.cfitem.identifier

								// skip the document's item (but not associations to the document)
								if (identifier != nf.CFDocument.identifier) {
									// add the item, if it doesn't already exist
									if (nf.CFItems.findIndex(x => x.identifier == identifier) == -1) {
										nf.CFItems.push(ojson.CFItems.find(x => x.identifier == identifier))
									}
								}

								// all non-isChildOf associations where the originNodeURI or destinationNodeURI is this identifier
								let assocs = ojson.CFAssociations.filter(
									o =>
										o.associationType != 'isChildOf' &&
                    (o.originNodeURI.identifier == identifier || o.destinationNodeURI.identifier == identifier)
								)
								for (let j = 0; j < assocs.length; ++j) {
									// make sure we only add each assoc once
									if (nf.CFAssociations.findIndex(x => x.identifier == assocs[j].identifier) == -1) {
										nf.CFAssociations.push(assocs[j])
									}
								}

								// all isChildOf associations where the destinationNodeURI is this identifier -- that is, children of this item
								// (we *don't* do all isChildOf associations where the originNodeURI is this identifier, because if the item appears in multiple locations, we might get extra children)
								assocs = ojson.CFAssociations.filter(
									o => o.associationType == 'isChildOf' && o.destinationNodeURI.identifier == identifier
								)
								for (let k = 0; k < assocs.length; ++k) {
									// make sure we only add each assoc once
									if (nf.CFAssociations.findIndex(x => x.identifier == assocs[k].identifier) == -1) {
										nf.CFAssociations.push(assocs[k])
									}
								}

								// recursively process children
								for (let i = 0; i < node.children.length; ++i) {
									let cn = node.children[i]
									build_framework(cn)
								}
							}
							build_framework(tc_node)

							let s = JSON.stringify(nf)
							console.log(
								'finished ' +
                  nf.CFDocument.title +
                  ' / found document identifier? (should be -1): ' +
                  s.indexOf(ojson.CFDocument.identifier)
							)

							return nf
						},
						[ojson, nf, tc_node]
					) // this is where we pass the original data into the fn
					.then(json => {
						// clean the generated json using reduce_case_json
						U.reduce_case_json(this.$worker, json, ['remove_empty_properties', 'remove_CFDocumentURIs']).then(
							clean_json => {
								// reduce_case_json returns an object with json and fn_return_vals; we just need the json
								clean_json = clean_json.json

								// if user held down cmd key when they clicked the command, export files rather than saving them to the server
								if (export_files) {
									this.export_json(clean_json, clean_json.CFDocument.identifier + '.json')
									// update files_to_process, and if we're done stop the loading indicator
									files_to_process -= 1
									if (files_to_process == 0) {
										U.loading_stop()
									}
								} else {
									// set lsdoc_identifier in the json, then save using the same save_framework_data fn that we use for creating new frameworks and saving changes to frameworks
									clean_json.lsdoc_identifier = clean_json.CFDocument.identifier

									// copy some things from parent_ss_framework_data
									if (!clean_json.ss_framework_data) clean_json.ss_framework_data = {}
									clean_json.ss_framework_data.color = parent_ss_framework_data.color
									clean_json.ss_framework_data.image = parent_ss_framework_data.image
									clean_json.ss_framework_data.category = parent_ss_framework_data.category
									clean_json.ss_framework_data.render_latex = parent_ss_framework_data.render_latex
									clean_json.ss_framework_data.exemplar_label = parent_ss_framework_data.exemplar_label

									// NOTE: we are not dealing with updating CFItemTypeURIs

									this.$store.dispatch('save_framework_data', clean_json).then(result => {
										// update this.CFDocument's lastChangeDateTime from result
										clean_json.CFDocument.lastChangeDateTime = result.document_lastChangeDateTime

										let cfd_json = new CFDocument(clean_json.CFDocument).to_json() // it's probably overkill to do these transformation, but it shouldn't hurt

										let index = this.framework_records.findIndex(x => x.lsdoc_identifier == cfd_json.identifier)
										if (index == -1) {
											let framework_record = U.create_framework_record(cfd_json.identifier, { CFDocument: cfd_json })
											this.$store.commit('set', [this.framework_records, 'PUSH', framework_record])
										} else {
											// if we're "re-importing" an existing framework, create a new framework_record object, copying in old_framework's ss_framework_data; then when we open it we'll reload the new json from the server
											// note that if this is the case we won't have updated the ss_framework_data record in the DB, so we should get these values back when we reload
											let framework_record = U.create_framework_record(
												cfd_json.identifier,
												{ CFDocument: cfd_json },
												$.extend(true, {}, this.framework_records[index].ss_framework_data)
											)
											this.$store.commit('set', [this.framework_records, 'SPLICE', index, framework_record])
										}

										// update files_to_process, and if we're done stop the loading indicator
										files_to_process -= 1
										if (files_to_process == 0) {
											U.loading_stop()
										}
									})
								}
							}
						)
					}).catch(() => {
						U.loading_stop()
						this.$alert('An error occurred when attempting to split the framework.')
						console.error // logs any possible error
					})
			}
		},

		export_canvas_spreadsheet() {
			this.kebab_menu_showing = false
			if (vapp.signed_in_only('export the framework in Canvas spreadsheet format', this.lsdoc_identifier)) return
			this.show_canvas_import_dialog = true
		},

		export_for_powerschool() {
			this.kebab_menu_showing = false
			if (vapp.signed_in_only('export the framework in PowerSchool format', this.lsdoc_identifier)) return
			this.show_powerschool_import_dialog = true
		},

		export_framework_json() {
			if (vapp.signed_in_only('export CASE JSON', this.lsdoc_identifier)) return
			this.export_framework('Export Framework as JSON', 'json', 'export_json')
		},

		export_framework(prompt_title, ext, fn) {
			// ask the user what format they want to export
			let select_options = [
				{ value: 'v1p0', text: 'CASE 1.0' },
				{ value: 'v1p1', text: 'CASE 1.1' },
				{ value: 'vext', text: 'Extended CASE' }
			]
			let text = `Select a format for your export:<ul class="mb-2"><li><b>CASE 1.0</b> includes only items and item fields that are part of the official CASE 1.0 specification.</li><li><b>CASE 1.1</b> includes additional item fields that are part of the official CASE 1.1 specification.</li><li><b>Extended CASE</b> may include items and item fields that are editable/viewable in ${this.$store.state.site_config.app_name}, but are not part of the official CASE spec, or are not conformant with the official CASE best practices.</li>`

			// use a config for this
			if (this.site_config.use_sanitized_options === 'true') {
				text += `<li><b>“Normalized”</b> exports modify JSON if necessary to conform to CASE best practices (see the User Guide).</li>`
				select_options = [
					...select_options,
					{ value: 'sv1p0', text: '“Normalized” CASE 1.0' },
					{ value: 'sv1p1', text: '“Normalized” CASE 1.1' },
					{ value: 'svext', text: '“Normalized” Extended CASE' }
				]
			}

			text += '</ul>'

			this.$prompt({
				title: prompt_title,
				text: text,
				promptType: 'select',
				selectOptions: select_options,
				initialValue: 'v1p1',
				acceptText: 'Select Format',
				dialogMaxWidth: 620
			}).then(format => {
				// let the user choose the filename
				this.$prompt({
					title: prompt_title,
					text: 'Enter a title for the exported file:',
					initialValue: 'CASE-' + this.cfdocument.title + '.' + ext,
					acceptText: 'Export',
					hideCancel: false, // set to true to hide the cancel button
					dialogMaxWidth: 620
				}).then(filename => {
					U.ajax(
						'sync_case_api',
						{ framework_identifiers: [this.framework_record.lsdoc_identifier], case_version: format },
						result => {
							// record_framework_download here, just before downloading
							vapp.record_framework_download(this.lsdoc_identifier)

							// MC: Fetch from filestore instead
							this[fn](JSON.parse(result.jsons[0]), filename)
						}
					)
				})
			})
			this.kebab_menu_showing = false
		},

		export_json(json, filename) {
			// slightly hacky solution from here: https://stackoverflow.com/questions/19721439/download-json-object-as-a-file-from-browser
			var dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(json))
			var downloadAnchorNode = document.createElement('a')
			downloadAnchorNode.setAttribute('href', dataStr)
			downloadAnchorNode.setAttribute('download', filename)
			document.body.appendChild(downloadAnchorNode) // required for firefox
			downloadAnchorNode.click()
			downloadAnchorNode.remove()
		},

		export_ap_word() {
			let html = `<h1 style="margin-bottom:16px">${this.framework_record.cfo.cftree.cfitem.title}</h1>`

			let last_level = -1
			let process_children = (node, level) => {
				let cfitem = node.cfitem

				let style = `margin-left:${level * 24}px; `
				if (last_level != level) style += `border-top:1px solid #ccc; padding-top:16px;`

				let line = `<div style="${style}">`

				let s = ''
				if (cfitem.humanCodingScheme) s += `**${cfitem.humanCodingScheme}**  `
				s += cfitem.fullStatement

				if (level == 0) line += `<h3 style="margin-bottom:16px">${s}</h3>`
				else line += marked(s)

				line += `</div>`

				html += line

				last_level = level

				for (let child_node of node.children) {
					process_children(child_node, level + 1)
				}
			}

			// for each top-level item...
			for (let top_node of this.framework_record.cfo.cftree.children) {
				if (top_node.cfitem.fullStatement != 'Additional Metadata') {
					process_children(top_node, 0)
				}
			}

			html = `<!DOCTYPE html><html><head></head><body><div style="font-size:14px; line-height:18px;">\n${html}\n</div></body></html>`

			this.$confirm({
				// title: 'AP Export for Word',
				text: html,
				acceptText: 'Done',
				acceptIcon: 'fas fa-check',
				cancelText: 'Save HTML',
				cancelIcon: 'fas fa-save',
				cancelColor: 'green darken-4',
				// set rejectCloseOnClick to false so clicking the cancel ("Copy to Clipboard") button *doesn't* close the dialog
				rejectCloseOnClick: false,
				fullscreen: true,
				dialogMaxWidth: '95vw'
			}).then(y => {}).catch(n => {
				var dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(html)
				var downloadAnchorNode = document.createElement('a')
				let filename = $.trim(this.framework_record.cfo.cftree.cfitem.title) + '.html'
				downloadAnchorNode.setAttribute('href', dataStr)
				downloadAnchorNode.setAttribute('download', filename)
				document.body.appendChild(downloadAnchorNode) // required for firefox
				downloadAnchorNode.click()
				downloadAnchorNode.remove()
			}).finally(f => {})
		},

		export_ap_spreadsheet() {
			// retrieve all-course metadata framework if we don't already have it
			let acm_framework_record = this.framework_records.find(
				x => x.lsdoc_identifier == this.$store.state.all_course_metadata_framework_identifier
			)
			if (acm_framework_record.framework_json_loaded) {
				this.export_ap_spreadsheet_finish(acm_framework_record)
			} else {
				U.loading_start('Loading metadata…')
				this.$store
					.dispatch('get_lsdoc', this.$store.state.all_course_metadata_framework_identifier)
					.then(() => {
						U.loading_stop()

						// then build the cfo for the framework
						U.build_cfo(this.$worker, acm_framework_record.json)
							.then(cfo => {
								this.$store.commit('set', [acm_framework_record, 'cfo', cfo])
								this.$store.commit('set', [acm_framework_record, 'framework_json_loading', false])
								U.loading_stop()

								this.export_ap_spreadsheet_finish(acm_framework_record)
							}).catch(e => {
								this.$store.commit('set', [acm_framework_record, 'framework_json_load_failed', true])
								U.loading_stop()
								console.log(e)
								this.$alert('Could not load the all-course metadata')
							})
					}).catch(e => {
						console.log(e)
						U.loading_stop()
						this.$alert('Could not load the all-course metadata')
					})
			}
		},

		export_ap_spreadsheet_finish(acm_framework_record) {
			// currently it doesn't really matter if we've extracted the vext fields for this format
			let all_lines = ['Category'] // placeholder for 'Category...' line
			let lines = []
			let max_level
			let max_max_level = 0
			let flat_lines = []
			let flat_columns = []
			let content_item_types = []
			let header_row_indexes = [0]

			// get renested item types
			let renested_item_types = {}
			for (let identifier in this.framework_record.cfo.cfitems) {
				let nitem = this.framework_record.cfo.cfitems[identifier]
				if (U.item_type_string(nitem) == 'Metadata Category') {
					let nit = nitem.extensions?.nestForAPSpreadsheetExport
					if (nit) {
						renested_item_types[nitem.fullStatement] = nit
					}
				}
			}

			let item_line = function (cfitem) {
				let line = ''
				// if the line has a hcs, add it
				if (cfitem.humanCodingScheme) {
					// strip out initial words/abbreviations if there? not doing this for now
					// line += cfitem.humanCodingScheme.replace(/^.* /, '') + ': '
					line += cfitem.humanCodingScheme + ': '
				}

				// add the fullStatement
				line += cfitem.fullStatement

				// for all item types other than 'Learning Object' and 'Essential Knowledge' (this list might grow), strip all markdown and dollar sign delimiters for latex
				if (!['Learning Objective', 'Essential Knowledge'].includes(cfitem.CFItemType)) {
					line = `[${line}]`
					line = line.replace(/([\s({\[>])\$(\S([^$]*?\S)?)\$([\s)}\].,;:%!?<])/g, ($0, $1, $2, $3, $4) => {
						return `${$1}${$2}${$4}`
					})
					line = line.replace(/^\[([\s\S]*)\]$/, '$1')

					// bulletted lists -- convert to bullet character? (hopefully there won't be many of these)
					line = line.replace(/\n\s*[*-]\s+(.*)/g, '• $1')

					// bold/italics/both
					line = line.replace(/\*\*\*(\S.*?)\*\*\*/g, '$1')
					line = line.replace(/\*\*(\S.*?)\*\*/g, '$1')
					line = line.replace(/\*(\S.*?)\*/g, '$1')

					// replace newlines and tabs with spaces
					line = line.replace(/\s/g, ' ')
					line = line.replace(/\s+/g, ' ')

					// else for the item types listed above...
				} else {
					// convert lower-case roman numerals to lists -- from extensions of marked in utilities.js (requested 9/22/2023)
					line = line.replace(/\n([ivx]+)\.\s(.*)/g, ($0, $1, $2) => {
						let start = [
							'',
							'i',
							'ii',
							'iii',
							'iv',
							'v',
							'vi',
							'vii',
							'viii',
							'ix',
							'x',
							'xi',
							'xii',
							'xiii',
							'xiv',
							'xv',
							'xvi',
							'xvii',
							'xviii',
							'xix',
							'xx',
							'xi',
							'xii',
							'xiii',
							'xiv',
							'xv'
						].indexOf($1)
						return `<ol type="i" start="${start}"><li>${$2}</li></ol>`
						// AP requested this to use <ul style="list-style-type: lower-roman;"> as the outer container, but that causes problems...
						// return `<ul style="list-style-type: lower-roman;" start="${start}"><li>${$2}</li></ul>`
					})

					// also convert bulleted lists to HTML
					while (line.search(/\n\s*[*-]\s+([\s\S]+?)((<ul>)|(\n\n)|(\n\s*[*-])|$)/) > -1) {
						// console.log(RegExp.$1, RegExp.$2)
						line = line.replace(/\n\s*[*-]\s+([\s\S]+?)((<ul>)|(\n\n)|(\n\s*[*-])|$)/, '<ul><li>$1</li></ul>$2')
					}
					// while (line.search(/\n\s*\*\s+([\s\S]+?)((\n\n)|$)/) > -1) {
					// 	console.log(RegExp.$1, RegExp.$2)
					// 	line = line.replace(/\n\s*\*\s+([\s\S]+?)((\n\n)|$)/, '<ul><li>$1</li></ul>\n\n')
					// }
					// line = line.replace(/\n\s*\*\s+([\s\S]+?)((\n\n)|$)/g, '<ul><li>$1</li></ul>\n\n')

					// now, after doing the above transform, change double-line-breaks to <br>'s, and line breaks to spaces
					line = line.replace(/\n\n/g, '<br>')
					line = line.replace(/\n/g, ' ')

					// 1.1.A.3: Distance and speed are examples of scalar quantities, while position, displacement, velocity, and acceleration are examples of vector quantities. i. Vectors are notated with an arrow above the symbol for that quantity. Relevant equation: \(\vec{v}={{\vec{v}}_{0}}+\vec{a}t\) ii. Vector notation is not required for vector components along an axis. In one dimension, the sign of the component completely describes the direction of that component. Derived equation: \({{v}_{x}}={{v}_{x0}}+{{a}_{x}}t\)

					// Convert back ticks to LaTeX -- \texttt -- or (hopefully this works too) <code>tags</code>
					line = line.replace(/`(\S.*?)`/g, ($0, code) => {
						// AP wants latex within backticks blocks to be *outside* the code blocks...
						// we're going to hope/assume that we don't have "raw" $'s inside code blocks (that is, we assume that every $ is a latex marker)
						let s = ''
						let chunks = code.split('$')
						// `a $\leftarrow$ 1` => ['a ', '$\leftarrow$', ' 1']
						for (let i = 0; i < chunks.length; ++i) {
							// skip empties; that could happen if there's a latex at the start or end of the code block
							if (empty(chunks[i])) continue

							// even chunks are text that we need in code blocks; odd chunks are latex
							if (i % 2 == 0) s += `<code>${chunks[i]}</code>`
							else s += `$${chunks[i]}$`
						}
						return s
					})

					// 8/18/2023: convert LaTeX delimiters from $xxx$ to \(xxx\) -- see U.render_latex for explanation of regex
					line = `[${line}]`
					line = line.replace(/([\s({\[>])\$(\S([^$]*?\S)?)\$([\s)}\].,;:%!?<])/g, ($0, $1, $2, $3, $4) => {
						let s = `\\(${$2}\\)`

						// also insert clearspeak span as alt text
						if (cfitem.extensions && cfitem.extensions.latex_alt_text && $2 in cfitem.extensions.latex_alt_text) {
							s = U.clearspeak_span($2, s, '', cfitem.extensions.latex_alt_text[$2])
						} else {
							s = U.clearspeak_span($2, s)
						}

						return `${$1}${s}${$4}`
					})
					line = line.replace(/^\[([\s\S]*)\]$/, '$1')

					// convert italics and bolds from markdown to LaTeX
					// TODO: we could have a version that doesn't do this conversion...
					// latex: $\textbf{bold}$ and $\textit{italics}$ and $\textbf{\textit{both}}$ here
					line = line.replace(/\*\*\*(\S.*?)\*\*\*/g, '\\(\\textbf{\\textit{$1}}\\)')
					line = line.replace(/\*\*(\S.*?)\*\*/g, '\\(\\textbf{$1}\\)')
					line = line.replace(/\*(\S.*?)\*/g, '\\(\\textit{$1}\\)')
				}

				return line
			}

			let last_line_for_level = [] // see below

			let process_children = (node, level, flag, flag2) => {
				let cfitem = node.cfitem
				let item_type = U.item_type_string(cfitem)

				// if flag isn't 'processing_nested_children' and the item type is one of the renested_item_types, skip it
				if (flag != 'processing_nested_children' && !empty(renested_item_types[item_type])) {
					console.log('skipping item type ' + item_type)
					return
				}

				// start the line with the appropriate number of tabs
				let line = ''
				for (let i = 0; i < level; ++i) line += '\t'

				line += item_line(cfitem)

				// push to flat_lines, removing one leading tab
				flat_lines.push(line.replace(/^\t/, ''))

				// save list of content_item_types, in the order we encounter them in the tree
				if (flag2 != 'dont_process_item_types') {
					// unless told not process item types
					if (item_type && !content_item_types.includes(item_type)) content_item_types.push(item_type)
				}

				// if we've reached a new level...
				if (level > max_level) {
					// increase max_level and add this item's type to the types line for this category
					// BUT NOT if the item's type is one of the types we've identified as not being included in the "subject" part of the spreadsheet
					if (!excluded_subject_item_types.includes(item_type)) {
						max_level = level
						lines[0] += '\t' + U.item_type_string(cfitem)
						if (max_level > max_max_level) {
							all_lines[0] += '\t' + 'Level ' + max_level
							max_max_level = max_level
						}
					}
				}

				// push to lines
				// BUT NOT if the item's type is one of the types we've identified as not being included in the "subject" part of the spreadsheet
				// 		-- we will add these as flat fields only below
				if (!excluded_subject_item_types.includes(item_type)) {
					// also don't push lines with empty item_type values, except for level 0 (e.g. "Skill Category")
					if (item_type || level == 0) {
						// and don't push the same line for the same level twice in a row -- see CoGoPo PAU-1.D
						if (line == last_line_for_level[level]) {
							console.log(`skipping duplicate line for level ${level}: ${line}`)
						} else {
							lines.push(line)
							last_line_for_level[level] = line
						}
					}
				}

				// process children
				for (let child_node of node.children) {
					process_children(child_node, level + 1, flag, flag2)
				}

				// if this item's type is one of the targets of a renested_item_types type,
				if (flag != 'processing_nested_children' && node.cfitem.humanCodingScheme)
					for (let child_type in renested_item_types) {
						if ((renested_item_types[child_type] = item_type)) {
							// then go through all items and find items of child_type whose hcs match this item's code; add those items, and their children, to the spreadsheet here
							let re = new RegExp(`^${node.cfitem.humanCodingScheme}\\.[^\.]+$`)
							let arr = []
							for (let identifier in this.framework_record.cfo.cfitems) {
								let nitem = this.framework_record.cfo.cfitems[identifier]
								if (U.item_type_string(nitem) == child_type) {
									if (nitem.humanCodingScheme && nitem.humanCodingScheme.search(re) == 0) {
										// console.log(`found child of ${item_type} ${node.cfitem.humanCodingScheme}: ${nitem.humanCodingScheme}`)
										arr.push(nitem.tree_nodes[0])
									}
								}
							}

							// sort the found nodes by the code
							arr.sort((a, b) => U.natural_sort(a.cfitem.humanCodingScheme, b.cfitem.humanCodingScheme))

							// then process each node (and its children)
							for (let node of arr) {
								process_children(node, level + 1, 'processing_nested_children', flag2)
							}
						}
					}
			}

			let process_metadata_node = node => {
				let flat_column_index = flat_columns.length
				// start this flat_column with the item's fullStatement
				flat_columns[flat_column_index] = [node.cfitem.fullStatement]
				// now for each child...
				for (let child_node of node.children) {
					// add the child's fullStatement to this flat_column
					flat_columns[flat_column_index].push(child_node.cfitem.fullStatement)
					if (child_node.children.length > 0) {
						// if the child has children, we create another flat column for the child and its children
						process_metadata_node(child_node)
					} else if (U.item_type_string(child_node.cfitem) == 'Metadata Category') {
						// else if the child is itself a MetadataCategory item, and its fullStatement matches an item type, that means it represents an item type, and gets its own flat column.
						if (this.framework_record.cfo.item_types.includes(child_node.cfitem.fullStatement)) {
							flat_columns[flat_columns.length] = [child_node.cfitem.fullStatement]
						}
					}
				}
			}

			// get list of item types that we want to exclude from the "subject" (tree-formatted) part of the spreadsheet...
			let excluded_subject_item_types = []
			let additional_metadata_parent_node = this.framework_record.cfo.cftree.children.find(
				x => x.cfitem.fullStatement == 'Additional Metadata'
			)
			if (additional_metadata_parent_node) {
				for (let top_am_node of additional_metadata_parent_node.children) {
					// ... top-level "Metadata Category" items that *don't* have children
					let item_type = U.item_type_string(top_am_node.cfitem)
					if (item_type == 'Metadata Category') {
						if (top_am_node.children.length == 0) {
							excluded_subject_item_types.push(top_am_node.cfitem.fullStatement)
						}
					}
				}
			}
			console.log('excluded_subject_item_types:', excluded_subject_item_types)

			// for each top-level item...
			for (let top_node of this.framework_record.cfo.cftree.children) {
				if (top_node.cfitem.fullStatement != 'Additional Metadata') {
					max_level = 0
					lines = [''] // placeholder for this category's types line

					process_children(top_node, 0, '', '')

					// only put the top-level item in the 'top' section of the spreadsheet if it has more than one level
					if (max_level > 1) {
						header_row_indexes.push(all_lines.length)
						all_lines.push('') // blank line between categories, including before the first category
						all_lines = all_lines.concat(lines)
					}

					// when we get to the Additional Metadata item, process its children as flat lines only
				} else {
					for (let child_node of top_node.children) {
						process_metadata_node(child_node)
					}

					// then process the top-level children of the all-course metadata file
					// BUT NOT if the "Additional Metadata" node has the string "no_all_course_metadata" in the notes field
					if (empty(top_node.cfitem.notes) || !top_node.cfitem.notes.includes('no_all_course_metadata')) {
						for (let child_node of acm_framework_record.cfo.cftree.children) {
							process_metadata_node(child_node)

							// exception: "Assessment Type" is added as hierarchical, with some edits
							if (child_node.cfitem.fullStatement == 'Assessment Type') {
								console.warn('***found Assessment Type')
								max_level = 0
								lines = [''] // placeholder for this category's types line

								process_children(child_node, 0, '', 'dont_process_item_types')

								// total hack: they want the header lines like this:
								lines[0] = `	Assessment Type	Source	Practice Exam Form`
								lines[1] = `Assessment Purpose and Source			`

								// only put the top-level item in the 'top' section of the spreadsheet if it has more than one level
								if (max_level > 1) {
									// I think we don't want to do this...
									// header_row_indexes.push(all_lines.length)
									all_lines = all_lines.concat(lines)
									all_lines.push('') // blank line between categories
								}
							}
						}
					}
				}
			}

			// add another blank line before flat fields
			all_lines.push('')

			let process_flat_item_type_column = (col, item_type, node) => {
				if (node.cfitem && U.item_type_string(node.cfitem) == item_type) col.push(item_line(node.cfitem))
				for (let child of node.children) process_flat_item_type_column(col, item_type, child)
			}

			// add a "flat" column for the values for each content_item_type that is specified in the metadata area
			let type_warnings = []
			for (let i = content_item_types.length - 1; i >= 0; --i) {
				let item_type = content_item_types[i]
				let index = flat_columns.findIndex(x => x[0] == item_type)
				if (index == -1) {
					type_warnings.push(
						`Not processing ${item_type} items because the type isn't specified  in the medatadata area`
					)
					continue
				}

				let arr = [item_type]
				process_flat_item_type_column(arr, item_type, this.framework_record.cfo.cftree)

				if (arr.length == 0) {
					type_warnings.push(`No items found for item type ${item_type}`)
				} else {
					flat_columns[index] = arr
				}
			}

			for (let fc of flat_columns) {
				if (fc.length == 1) type_warnings.push(`No values found for metadata category ${fc[0]}`)
			}

			// collapse flat_columns into rows and add to the bottom
			if (flat_columns.length > 0) {
				all_lines.push('')
				all_lines.push('FLAT METADATA')
				all_lines.push('')
				header_row_indexes.push(all_lines.length)
				for (let i = 0; i < 10000; ++i) {
					let line = ''
					let found_value = false
					for (let fc of flat_columns) {
						if (fc[i]) {
							line += fc[i]
							found_value = true
						}
						line += '\t'
					}
					if (!found_value) break
					all_lines.push(line)
				}
			}

			let t = ''
			if (type_warnings.length > 0) {
				t += `<div class="red--text text--darken-3 mb-3"><b>WARNINGS:</b><ul><li>${type_warnings.join(
					'</li><li>'
				)}</li></ul></div>`
			}

			t += '<div class="mb-2"><a href="#k_flat_metadata"><b>Jump to Flat Metadata</b></a></div>'

			t += '<table class="k-ap-spreadsheet-export">'
			for (let j = 0; j < all_lines.length; ++j) {
				let tag = header_row_indexes.includes(j) ? 'th' : 'td'
				let arr = all_lines[j].split(/\t/)
				if (arr[0] == 'FLAT METADATA') t += `<tr id="k_flat_metadata">`
				else t += `<tr>`
				for (let i = 0; i < flat_columns.length; ++i) {
					t += `<${tag}>${arr[i] ?? ''}</${tag}>`
				}
				t += `</tr>`
			}
			t += '</table>'

			this.$confirm({
				title: 'AP-Formatted Spreadsheet Data',
				text: t,
				// text: '<pre style="font-size:12px; line-height:15px;">' + all_lines.join('\n') + '</pre>',
				acceptText: 'Done',
				acceptIcon: 'fas fa-check',
				cancelText: 'Copy to Clipboard',
				cancelIcon: 'fas fa-clipboard',
				cancelColor: 'green darken-4',
				// set rejectCloseOnClick to false so clicking the cancel ("Copy to Clipboard") button *doesn't* close the dialog
				rejectCloseOnClick: false,
				fullscreen: true,
				dialogMaxWidth: '95vw'
			}).then(y => {}).catch(n => {
				U.copy_to_clipboard(all_lines.join('\n'))
				this.$inform('Spreadsheet data copied to clipboard')
			}).finally(f => {})
		},

		show_framework_json() {
			if (vapp.signed_in_only('show the entire framework’s CASE JSON', this.lsdoc_identifier)) return
			// record_framework_download here, just before showing the json
			vapp.record_framework_download(this.lsdoc_identifier)

			let html = `<textarea autofocus readonly aria-label="Framework JSON" spellcheck="false" style="height: calc(100vh - 220px); width: 1000px; font-size: 12px; line-height: 14px; font-family: monospace;">${JSON.stringify(this.framework_record.json, null, 4)}</textarea>`
			this.$alert({ title: 'Framework JSON', text: html, dialogMaxWidth: 1050 })
		},

		reduce_file_size() {
			this.show_reduce_file_size_dialog = true
		},

		clean_line_breaks() {
			this.$confirm({
				title: 'Clean Line Breaks',
				text: 'This process removes extraneous line breaks from item fullStatements and notes (while preserving markup formatting). Line breaks can be introduced when copying and pasting from PDFs to create items. Click “CHECK FOR LINE BREAKS” to run the process and see how many, if any, items would be affected by the process. You can then decide if you want to proceed',
				acceptText: 'CHECK FOR LINE BREAKS'
			}).then(y => {
				let updated_items = []
				let updates = ''
				for (let item of this.framework_record.json.CFItems) {
					item = $.extend(true, {}, item)
					let original = JSON.stringify(item)
					item.fullStatement = U.remove_line_breaks_from_text_field(item.fullStatement)
					item.notes = U.remove_line_breaks_from_text_field(item.notes)
					if (JSON.stringify(item) != original) {
						// set lastChangeDateTime = '*NOW*' so that server will generate a new date
						item.lastChangeDateTime = '*NOW*'
						updated_items.push(item)
						updates += sr('<li>$1 ($2)</li>', U.generate_cfassociation_node_uri_title(item, true), item.identifier)
					}
				}
				updates = sr('<ul class="mt-2">$1</ul>', updates)

				if (updated_items.length == 0) {
					this.$alert({ title: 'Clean Line Breaks', text: 'No items need cleaning!' })
					return
				}

				this.$confirm({
					title: 'Clean Line Breaks',
					text: updated_items.length + ' item(s) have line breaks that can be removed from fullStatements and/or notes (listed below). Click “REMOVE LINE BREAKS” to remove them and save the framework.' + updates,
					acceptText: 'REMOVE LINE BREAKS',
					dialogMaxWidth: 800
				}).then(y => {
					let data = {
						lsdoc_identifier: this.lsdoc_identifier,
						CFItems: updated_items
					}

					// save the updated items
					U.loading_start()
					this.$store.dispatch('save_framework_data', data).then(() => {
						// rather than messing around with trying to preserve things in the data structure, just reload
						this.$alert({ title: 'Clean Line Breaks', text: 'Done! Please reload the framework now.' }).then(y =>
							vapp.reload_window()
						)
					}).catch(n => {
						console.log(n)
					}).finally(f => {
						U.loading_stop()
					})
				}).catch(n => {
					console.log(n)
				}).finally(f => {})
			}).catch(n => {
				console.log(n)
			}).finally(f => {})
		},

		process_duplicate_items() {
			if (!this.stats_mode) {
				this.$alert('You must run the “Calculate/show framework statistics” command before running this command.')
				return
			}

			this.$confirm({
				title: 'Process Duplicates',
				text: 'This experimental process does advanced JSON cleaning by removing duplicates and some other things. <b>Running this process will not (currently) update the framework’s JSON file on the server.</b> Rather, it will download a copy of the processed JSON to your local computer.',
				acceptText: 'Do It!'
			}).then(y => {
				U.process_duplicates(this.$worker, this.framework_record.json, this.cfo.cfitems, U.case_current_time_string())
					.then(nf => {
						U.loading_stop()
						// Done!
						console.log('done!', nf)

						// TODO: save the file instead of exporting
						// export the file
						this.export_json(nf, 'CASE-DUPLICATES-REMOVED-' + nf.CFDocument.title)

						// set the json to the new version, re-build the cfo, and cancel stats_mode
						this.$store.commit('set', [this.framework_record, 'json', nf])
						this.build_cfo()
						this.stats_mode = false
					}).catch(e => {
						U.loading_stop()
						console.log(e)
					})
			}).catch(n => {
				console.log(n)
			}).finally(f => {})
		},

		edit_framework_json(original_json) {
			// if there's a basic parsing error, the original_json value will be what the user entered previously
			if (typeof(original_json) != 'string') {
				original_json = JSON.stringify(this.framework_record.json, null, 4)
			}

			this.$prompt({
				title: 'Edit Framework JSON',
				promptType: 'textarea',		// default is 'text'
				initialValue: original_json,
				acceptText: 'Save Changes',
				acceptIcon: 'fas fa-save',
				dialogMaxWidth: 1200,
			}).then(val => {
				// NOTE: keep this in synch with import_framework from FrameworkList.vue and re_import_framework below

				let json
				try {
					json = JSON.parse(val)
				} catch (e) {
					console.log(e)
					this.$alert('Your edited JSON could not be parsed (i.e. you did not enter valid JSON).').then(x=>{
						this.edit_framework_json(val)
					})
					return
				}

				// if no CFDocument, it's not valid json
				if (!json.CFDocument) {
					this.$alert('Your JSON did not include a CFDocument property, so it is not a valid CASE framework file.').then(x=>{
						this.edit_framework_json(val)
					})
					return
				}

				// if the uploaded document doesn't match this document's identifier, error
				if (json.CFDocument.identifier != this.CFDocument.identifier) {
					this.$alert('Your JSON’s document identifier does not match this document’s identifier.').then(x=>{
						this.edit_framework_json(val)
					})
					return
				}

				this.$confirm({
					title: 'Are you a sure?',
					text: 'Are you sure you want to bulk-edit the framework JSON?<br><br><b>No checks will be performed to determine if you’re saving malformed CASE data!</b>',
					acceptText: 'I accept this risk',
					acceptColor: 'red darken-3',
					acceptIcon: 'fas fa-arrow-right',
				}).then(y => {
					// call complete_import_framework from FrameworkList
					vapp.framework_list_component.complete_import_framework(json)

				}).catch(n=>{console.log(n)}).finally(f=>{})
			}).catch(n=>{console.log(n)}).finally(f=>{})

			// apply custom styles for textarea
			setTimeout(x=>{
				$('.simple-dialog-textarea').find('textarea').css({
					'overflow': 'auto',
					'height': 'calc(100vh - 220px)',
					'width': '1000px',
					'font-size': '12px',
					'line-height': '14px',
					'font-family': 'monospace',
				})
			}, 500)
		},

		re_import_framework() {
			this.$prompt({
				title: 'Re-Import Framework',
				text: 'Choose a CASE JSON file from your computer as the imported framework source.',
				promptType: 'file', // default is 'text'
				acceptText: 'Import'
			}).then(file => {
				if (empty(file) || empty(file.name)) return
				// we receive a file from dialog-promise-pwet here; create a FileReader
				let reader = new FileReader()
				reader.onload = e => {
					// NOTE: keep this in synch with import_framework from FrameworkList.vue and edit_framework_json above
					// currently we only support CASE JSON imports

					// parse json (it will be stringified by save_framework_data)
					let json
					try {
						json = JSON.parse(e.target.result)
					} catch (e) {
						console.log(e)
						this.$alert('The uploaded file was not valid JSON (an error occurred when attempting to parse the file).')
						return
					}

					// if no CFDocument, it's not valid json
					// TODO: do more validation...
					if (!json.CFDocument) {
						this.$alert('The uploaded JSON did not include a CFDocument property, so it is not a valid CASE framework file.')
						return
					}

					// if the uploaded document doesn't match this document's identifier, error
					if (json.CFDocument.identifier != this.CFDocument.identifier) {
						this.$alert('The uploaded JSON’s document identifier does not match this document’s identifier.')
						return
					}

					this.$confirm({
						title: 'Overwrite Existing Framework',
						text: sr('Are you sure you want to overwrite this framework’s CASE JSON with the newly-imported JSON?<br><br><b>No checks will be performed to determine if you’re saving malformed CASE data!</b>'),
						acceptText: 'Re-Import CASE JSON',
						dialogMaxWidth: 600
					}).then(y => {
						// call complete_import_framework from FrameworkList
						vapp.framework_list_component.complete_import_framework(json)
					}).catch(n => {
						console.log(n)
					}).finally(f => {})
				}
				// trigger the FileReader to load the text of the file
				reader.readAsText(file)
			}).catch(n => {
				console.log(n)
			}).finally(f => {})
		},

		// A sandbox is a copy of framework with new lsdoc identifier and private adoption status
		create_sandbox() {
			let payload = {
				user_id: this.user_info.user_id,
				framework_identifier: this.cfdocument.identifier,
				sandbox_identifier: U.new_uuid()
			}

			// returns result.sandbox_json
			// TODO we may only need CFDocument and sandbox meta data here, if so don't send whole frameowrk back to client here
			U.loading_start()
			U.ajax('save_sandbox', payload, result => {
				U.loading_stop()
				if (result.status != 'ok') {
					console.log(sr('Error in save_sandbox ajax call: $1', result.status))
					return
				}
				console.log(
					sr(
						'Sandbox $1 created for framework $2',
						result.sandbox_json.CFDocument.identifier,
						this.cfdocument.identifier
					)
				)

				let cfd_json = new CFDocument(result.sandbox_json.CFDocument).to_json()

				// this is a new framework created by sandbox_create service
				// we do want to copy in the ss_framework_data from source framework and add sandboxOfIdentifier
				let sandbox_framework_record = U.create_framework_record(
					cfd_json.identifier,
					{ CFDocument: cfd_json },
					$.extend(true, {}, this.framework_record.ss_framework_data)
				)
				sandbox_framework_record.ss_framework_data.sandboxOfIdentifier = this.cfdocument.identifier
				// add the sandboxSyncDateTime too, it's the lastChangeDateTime
				sandbox_framework_record.ss_framework_data.sandboxSyncDateTime = this.cfdocument.lastChangeDateTime
				/**
         *  By default, sandbox frameworks are private, otherwise they would be
         *  set as the save visibility of the source framework
         */
				sandbox_framework_record.ss_framework_data.is_private = true

				// add sandbox to framework_records
				this.$store.commit('set', [this.framework_records, 'PUSH', sandbox_framework_record])

				// open viewer into sandbox if user chooses to do so
				this.$confirm({
					text: 'Sandbox created. Would you like to open the sandbox now?',
					acceptText: 'Open Sandbox',
					cancelText: 'Remain in Original',
					dialogMaxWidth: 520
				}).then(y => {
					// we need to reload once changes have been applied, in case we created new crosswalk frameworks
					window.location = `/${cfd_json.identifier}`
				}).catch(n => {
					// we need to reload once changes have been applied, in case we created new crosswalk frameworks
					window.location = `/${this.cfdocument.identifier}`
				}).finally(f => {})
			})
		},

		// apply sandbox changes to original framework and then delete the sandbox
		// see ApplySandbox.vue component
		apply_sandbox() {
			this.show_apply_sandbox = true
		},

		open_original_of_sandbox() {
			let source_lsdoc_identifier = this.framework_record.ss_framework_data.sandboxOfIdentifier
			setTimeout(() => vapp.framework_list_component.view_framework(source_lsdoc_identifier), 100)
			this.hide_tree()
		},

		open_original_of_derivative() {
			let source_lsdoc_identifier = this.cfdocument.extensions.sourceFrameworkIdentifier
			setTimeout(() => vapp.framework_list_component.view_framework(source_lsdoc_identifier), 100)
			this.hide_tree()
		},

		// A derivative is a copy of framework with a new document and associations, but aliases of all items from the source
		create_derivative() {
			let payload = {
				user_id: this.user_info.user_id,
				framework_identifier: this.cfdocument.identifier,
				derivative_identifier: U.new_uuid()
			}

			// returns result.derivative_fr
			U.loading_start()
			U.ajax('save_derivative', payload, result => {
				U.loading_stop()
				if (result.status != 'ok') {
					console.log(sr('Error in save_derivative ajax call: $1', result.status))
					return
				}
				console.log(
					sr('Derivative $1 created for framework $2', payload.derivative_identifier, this.cfdocument.identifier)
				)
				console.log(result)

				// store returned framework record
				let json = JSON.parse(result.derivative_fr.json)
				let doc = new CFDocument(json.case_document_json)
				delete json.case_document_json // once we delete this, we'll be left with the ss_framework_data
				let derivative_framework_record = U.create_framework_record(doc.identifier, { CFDocument: doc }, json, false)
				this.$store.commit('set', [this.$store.state.framework_records, 'PUSH', derivative_framework_record])

				// open viewer into derivative if user chooses to do so
				this.$confirm({
					text: 'Derivative created. Would you like to open the derivative now?',
					acceptText: 'Open Derivative',
					cancelText: 'Remain in Original',
					dialogMaxWidth: 520
				}).then(y => {
					setTimeout(() => vapp.framework_list_component.view_framework(doc.identifier), 100)
					this.hide_tree()
				}).catch(n => {
					console.log(n)
				}).finally(f => {})
			})
		},

		copy_shortcut_url() {
			let url = window.location.origin + '/' // e.g. `https://case.georgiastandards.org/`
			if (this.framework_record.ss_framework_data.shortcuts.length == 0) {
				url += this.lsdoc_identifier
			} else {
				url += this.framework_record.ss_framework_data.shortcuts[0]
			}

			U.copy_to_clipboard(url)
			this.$inform('Link copied to clipboard')
			this.kebab_menu_showing = false
		},

		copy_api_url() {
			if (vapp.signed_in_only('copy this framework’s CASE API URL', this.lsdoc_identifier)) return

			// ask the user what format they want to export
			let select_options = [
				{ value: 'v1p0', text: 'CASE 1.0' },
				{ value: 'v1p1', text: 'CASE 1.1' },
				{ value: 'vext', text: 'Extended CASE' }
			]
			let text = `Select a format for the API URL:<ul class="mb-2"><li>The <b>CASE 1.0</b> API includes only items and item fields that are part of the official CASE 1.0 specification.</li><li><b>CASE 1.1</b> includes additional item fields that are part of the official CASE 1.1 specification.</li><li><b>Extended CASE</b> may include items and item fields that are editable/viewable in ${this.$store.state.site_config.app_name}, but are not part of the official CASE spec, or are not conformant with the official CASE best practices.</li>`

			// use a config for this
			if (this.site_config.use_sanitized_options === 'true') {
				text += `<li><b>“Normalized”</b> exports modify JSON if necessary to conform to CASE best practices (see the User Guide).</li>`
				select_options = [
					...select_options,
					{ value: 'sv1p0', text: '“Normalized” CASE 1.0' },
					{ value: 'sv1p1', text: '“Normalized” CASE 1.1' },
					{ value: 'svext', text: '“Normalized” Extended CASE' }
				]
			}

			text += '</ul>'

			// always call sync_case_api before showing the copy API URL dialog
			U.loading_start()
			U.ajax('sync_case_api', { framework_identifiers: [this.framework_record.lsdoc_identifier] }, result => {
				U.loading_stop()
				this.$prompt({
					title: 'Copy CASE package API URL',
					text: text,
					promptType: 'select',
					selectOptions: select_options,
					initialValue: 'v1p1',
					acceptText: 'Copy CASE API URL',
					dialogMaxWidth: 620
				}).then(format => {
					let domain = this.$store.state.framework_domain
					// if we're using a satchel_commons patch, with window.satchel_subdomain set by site_config.php,
					if (!empty(window.satchel_subdomain)) {
						// add the subdomain to the start of the domain for the API URI
						domain = domain.replace(/(https:\/\/)/, `$1${window.satchel_subdomain.toLowerCase()}.`)
					}
					let url = U.case_api_url(this.lsdoc_identifier, null, format, domain)
					U.copy_to_clipboard(url)
					this.$inform('API URL copied to clipboard')
				}).catch(n => {
					console.log(n)
				}).finally(f => {})
			})
			this.kebab_menu_showing = false
		},

		//////////////////////////////////////////////
		// Archives functionality
		set_track_changes_fields(key) {
			this.track_changes_fields[key] = !this.track_changes_fields[key]
			this.$store.commit('lst_set', ['track_changes_fields', this.track_changes_fields])
		},

		copy_archive_link() {
			let url = window.location.toString() + '?'
			if (this.tracking_changes) {
				url += 'track_changes=' + this.track_changes_fn
				url += `&track_changes_fields=${JSON.stringify(this.track_changes_fields)}`
				if (this.side_by_side_editor_head_identifier) {
					url += '&sbshi=' + this.side_by_side_editor_head_identifier
					this.$inform('Side-By-Side Comparison link copied to clipboard')
				} else {
					this.$inform('Track changes link copied to clipboard')
				}
			} else {
				// we currently don't show the link btn when you're just viewing an archive, but we could do so in the future
				url += 'view_archive=' + this.view_archive_fn
				this.$inform('Archive link copied to clipboard')
			}
			U.copy_to_clipboard(url)
		},

		view_archive(archive_filename, archive_date, archive_note, item_identifier) {
			// make sure we're not tracking changes when we're viewing an archive
			this.cancel_track_changes()

			this.archive_item_identifier_to_show = item_identifier ? item_identifier : ''
			this.view_archive_fn = archive_filename

			if (archive_filename == 'current') {
				// if archive_filename is 'current', we're viewing an item from the current framework, but in the context of the archive comparison dialog, so we want the bottom panel to show
				archive_filename = ''
				this.view_archive_ctl_text = 'Viewing item in current framework'
			} else {
				// set reactive values noting what we're showing
				this.view_archive_ctl_text = 'Viewing archive'
				if (archive_date && archive_note) this.view_archive_ctl_text += ` from <b>${archive_date}</b>: “${archive_note}”`

				// note if we need to re-enable editing when we return to the current framework, then turn editing_enabled off because we don't want you editing while you're viewing the archived framework
				this.editing_prior_to_viewing_archive = this.editing_enabled
				this.editing_enabled = false
			}

			// call refresh_lsdoc sending the archive_filename to load the archived json
			this.refresh_lsdoc(archive_filename, x => {
				setTimeout(x => this.show_item(item_identifier), 100)
			})
		},

		track_changes(archive_filename, archive_date, archive_note, item_identifier) {
			// set reactive values noting what we're showing; this also stores these values in lst
			this.archive_date = archive_date ?? ''
			this.archive_note = archive_note ?? ''
			this.track_changes_fn = archive_filename

			this.view_archive_ctl_text = archive_date
				? sr('Tracking changes from <b>$1</b>: “$2”', archive_date, archive_note)
				: 'Tracking changes'

			// load the archive's file (if we haven't already), then continue
			if (this.$store.state.archive_json[this.track_changes_fn]) {
				this.track_changes_post_file_load(item_identifier)
			} else {
				let filepath = sr('framework_archives/$1', this.track_changes_fn)
				U.get_json_file(filepath, archive_json => {
					if (typeof json == 'string') {
						console.log('Error in get_json_file', archive_json)
						return
					}

					if (archive_json.CFDocument.identifier !== this.lsdoc_identifier) {
						// discovered on 10-13-2023 that this can happen with a "Sandbox X changes applied to original" archive; I don't *think* it causes a problem with the track changes algorithm...
						console.log(' Mismatch CFDocument identifier archive id: [' + archive_json.CFDocument.identifier + '] and framework id: [' + this.lsdoc_identifier + ']')
						// return
					}

					// store archive json in store
					this.$store.commit('set', ['archive_json', this.track_changes_fn, archive_json])

					// if we didn't get an archive_date (probably because we entered track changes via a direct link), set archive_date here based on the archive_json
					if (!this.archive_date) {
						this.archive_date = archive_json.CFDocument.lastChangeDateTime.replace(/T.*/, '')
						this.view_archive_ctl_text = sr('Tracking changes from $1', this.archive_date)
					}

					this.track_changes_post_file_load(item_identifier)
				})
			}
		},

		track_changes_post_file_load(item_identifier) {
			let case_json = this.$store.state.archive_json[this.track_changes_fn]
			this.track_changes_framework_record = U.create_framework_record(
				case_json.CFDocument.identifier,
				case_json,
				this.framework_record.ss_framework_data,
				true
			)
			// note that this uses the U.build_cfo fn, not this.build_cfo
			U.build_cfo(this.$worker, this.track_changes_framework_record.json)
				.then(cfo => {
					this.track_changes_framework_record.cfo = cfo
					if (item_identifier) this.show_item(item_identifier)
					U.loading_stop()
				}).catch(e => {
					U.loading_stop()
					console.log(e)
				})
		},

		show_archive_table_view() {
			vapp.show_framework_update_report('framework_archive_compare')
		},

		cancel_track_changes() {
			this.track_changes_fn = ''
			this.track_changes_framework_record = null
			this.view_archive_ctl_text = ''
		},

		toggle_track_changes_legend() {
			vapp.framework_list_component.$refs.track_changes_legend.toggle_legend()
		},

		return_to_current_version(callback_fn) {
			if (this.view_archive_fn != '' && this.view_archive_fn != 'current') {
				// to return to the current version, we call refresh_lsdoc with no arguments
				this.refresh_lsdoc(null, callback_fn)
			} else {
				if (typeof callback_fn == 'function') {
					this.$nextTick(x => callback_fn())
				}
			}
			// (we may have been viewing the current version with the "return to archive" buttons at the bottom)

			// reset the view_archive values
			this.view_archive_fn = ''
			this.view_archive_ctl_text = ''

			// restore value of editing_enabled, if set
			if (this.editing_prior_to_viewing_archive !== null) {
				this.editing_enabled = this.editing_prior_to_viewing_archive
				this.editing_prior_to_viewing_archive = null
			}
		},

		restore_archive(archive_filename) {
			let payload = {
				archive_filename: archive_filename,
				action: 'restore',
				cf_item_count: this.framework_record.json.CFItems.length,
				cf_association_count: this.framework_record.json.CFAssociations.length
			}

			U.loading_start()
			U.ajax('manage_archives', payload, result => {
				U.loading_stop()
				if (result.status != 'ok') {
					console.log('Error in manage_archives ajax call')
					if (result.status == 'lock_conflict' || result.status == 'session_conflict') {
						// user has lost the edit lock due to inactivity and competing checkout
						this.$alert(result.message)

						this.view_archive_fn = 'lost_lock' // set view_archive_fn to something to force reload of current version
						this.return_to_current_version()
						this.editing_enabled = false
					} else {
						this.$alert('There was a problem with restoring the archive to the current framework.')
					}
					return
				}
				if (result.files_identical == 'yes') {
					this.$alert('This archive is identical to the current version, so the restore was canceled.')
					return
				}

				// reset entity_archives so they'll be reloaded when we re-open change history for entities
				this.$store.commit('set', ['entity_archives', {}])

				// call return_to_current_version; the restored archive is now the current version!
				this.view_archive_fn = 'post_restore' // set view_archive_fn to something to force reload of current version
				this.return_to_current_version()
				this.$alert('The framework has been restored to the archive.')
			})
		},

		show_update_report(flag) {
			// when the button is clicked from the bottom banner to show the framework_update_report, go back to the last-shown report
			this.return_to_current_version()
			setTimeout(() => vapp.show_framework_update_report(flag), 0)
		},

		show_progression_table(framework_record, node) {
			this.progression_table_record = framework_record

			// if the provided node has extensions.progressionTableItemIdentifier set, show the progression table for the specified node
			let progressionTableItemIdentifier = node.cfitem.extensions?.progressionTableItemIdentifier
			if (progressionTableItemIdentifier && framework_record.cfo.cfitems[progressionTableItemIdentifier]?.tree_nodes.length > 0) {
				this.progression_table_data = framework_record.cfo.cfitems[progressionTableItemIdentifier].tree_nodes[0]
				return
			}
			
			// if progressionTableItemIdentifier not set (or not found), use the provided node
			this.progression_table_data = node
		},

		cancel_tile_mode(evt) {
			if (!empty(evt?.target)) $(evt.target).closest('button').blur()
			this.$store.commit('set', ['start_string', ''])
			this.$store.commit('set', ['start_identifier', ''])
			this.$store.commit('set', ['start_string_identifiers', ''])
			this.viewer_mode = 'tree'
		},

		enter_tile_mode(evt) {
			if (!empty(evt?.target)) $(evt.target).closest('button').blur()
			this.$store.commit('set', ['start_string', ''])
			this.$store.commit('set', ['start_identifier', ''])
			this.$store.commit('set', ['start_string_identifiers', ''])
			this.viewer_mode = 'tiles'
		},

		run_custom_script() {
			this.$prompt({
				title: 'Run custom script',
				text: 'Enter the script name:',
				initialValue: this.$store.state.lst.last_custom_script_run,
				disableForEmptyValue: true,
				acceptText: 'Run…'
			}).then(custom_script_name => {
				this.$store.commit('lst_set', ['last_custom_script_run', custom_script_name])
				this.custom_script_name = custom_script_name
			}).catch(n => {
				console.log(n)
			}).finally(f => {})
		},
		on_side_by_cancel() {
			this.side_by_side_editor_head_identifier = null
			this.cancel_track_changes()
		},

		go_to_rosetta() {
			vapp.go_to_route(`resources/${this.lsdoc_identifier}`)
		},

		update_page_title() {
			let title = 
				// use case_tree_title if we have it
				this.case_tree_title ||
				// or the CFDocument if we have it
				this.framework_record?.json?.CFDocument?.title ||
				// or just the word 'Framework'
				'Framework'

			// make sure whatever we get is plain-text
			title = U.html_to_text(title)

			// set document.title
			document.title = title
		},

		show_best_practices() {
			if (vapp.signed_in_only('show the Best Practices report for this framework', this.lsdoc_identifier)) return
			this.best_practices_showing = true
		},
	}
}
</script>

<style lang="scss">
// adjust these to fit app; also see initialize_tree above
$k-case-tree-outer-wrapper-top: 60px; // height of permanent banner at the top of the screen (with the product title and product menu)
$k-case-tree-outer-wrapper-height: calc(100vh - 60px);
$k-case-tree-inner-wrapper-height: calc(
  100vh - 107px
); // 100vh - height of permanent banner - height of framework banner (with the search bar and framework menu)
// see also $k-case-tree-height in CASETree

.k-case-tree-outer-wrapper {
  position: fixed;
  top: $k-case-tree-outer-wrapper-top;
  left: 0px;
  height: $k-case-tree-outer-wrapper-height;
  width: 100%;
  z-index: 200;
  background-color: #eee;
  display: flex;
  flex-direction: column;
  text-align: left;
  resize: both;

  .k-case-tree-top {
    background-color: #999;
    // display:flex;
    padding: 4px 4px 0 4px;
    height: 48px;
  }

  .k-case-tree-top-inner {
    // flex:1 1 auto;
    padding-bottom: 5px;
    display: flex;

    .k-case-tree-title {
      font-size: 20px;
      line-height: 24px;
      font-weight: 900;
      flex: 0 1 auto;
      display: flex;
      align-items: center;
      overflow: hidden;
    }

    .k-case-tree-title-inner {
      flex: 0 1 auto;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .k-case-tree-title-inner-small {
      font-size: 16px;
      line-height: 19px;
      white-space: normal;
    }

    .k-case-tree-title-inner-xsmall {
      font-size: 14px;
      line-height: 16px;
      white-space: normal;
    }

    .k-case-tree-search-outer {
      flex: 0 0 auto;
      display: flex;
      text-align: center;
      width: 372px;
      font-weight: normal;
      position: relative;
      pointer-events: auto;

      input {
        font-size: 14px;
      }

      .k-case-tree-search-results {
        position: absolute;
        z-index: 102;
        right: 12px;
        top: 38px;
        background-color: #000;
        opacity: 0.7;
        border-radius: 4px;
        font-size: 14px;
        padding: 0 6px;
      }
    }
  }

  .k-case-tree-main {
    flex: 1 1 auto;
    height: 100%;
    pointer-events: none;
    // z-index:2;
    // padding:5px 0;

    .k-case-tree-inner-wrapper {
      // height:calc(100% - 10px);
      // height:100%;
      height: $k-case-tree-inner-wrapper-height;
      // overflow:auto;
      overflow: hidden;
      // padding-top:8px;
      position: relative; // this is needed so that the item tile shows up in the right place on small screens
    }
  }

  .k-resizable-handle-br,
  .k-resizable-handle-bl,
  .k-resizable-handle-tr,
  .k-resizable-handle-tl,
  .k-resizable-handle-bm,
  .k-resizable-handle-tm,
  .k-resizable-handle-ml,
  .k-resizable-handle-mr {
    display: none !important;
  }
}

.k-case-tree-search-panel {
  position: absolute;
  right: 4px;
  top: 40px;
  border-radius: 6px;
  background-color: #eee;
  color: #000;
  font-size: 14px;
  z-index: 10;
  text-align: left;
  // min-width:calc(100% - 8px);
  max-width: calc(50vw - 50px) !important;
  max-height: calc(100vh - 120px);
  overflow: auto;
}

.k-case-tree-search-panel-screen {
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
  background-color: transparent;
  display: none;
}

.k-case-tree-search-panel-behind {
  opacity: 0.5;
  z-index: 0;
  .k-case-tree-search-panel-screen {
    display: block;
  }
}

.k-case-tree-search-options {
  width: calc(50vw - 50px);
  max-width: 800px;
  padding: 0 8px 1px 8px;

  .k-case-tree-search-option-selectors {
    display: flex;
    flex-wrap: nowrap;
  }

  .v-select__selections {
    font-size: 13px;
    line-height: 13px;
    input[type='text'] {
      display: none !important;
    }
  }
}

.k-case-tree-search-results-panel {
  // position:absolute;
  // left:auto!important;
  // right:4px!important;
  // top:40px!important;
  // border-radius:6px;
  max-width: 800px;
  border-top: 1px solid #666;
  margin-top: 4px;
  background-color: #ddd;

  .v-list {
    background-color: #ddd;
    margin: 0 8px;
    overflow: hidden;
  }

  .v-list-item {
    font-size: 12px;
    min-height: 26px !important;
    text-align: left;
    white-space: nowrap;
    padding: 0 4px;
  }

  .k-advanced-search-sim-score {
    flex: 0 0 40px;
    // width:40px;
    text-align: center;
    // font-size:13px;
    font-weight: bold;
    background-color: #444;
    color: #fff;
    border-radius: 3px;
    text-decoration: none !important;
    margin-right: 8px;

    .v-icon {
      font-size: 11px !important;
      margin-right: 2px;
      // margin-left:2px;
      margin-top: -3px;
    }
  }

  .k-advanced-search-multiple-items {
    flex: 0 0 auto;
    text-align: center;
    font-weight: bold;
    color: $v-green-darken-2;
    margin-right: 8px;
  }

  .k-advanced-search-full-statement {
    overflow: hidden;
  }
}

.k-search-full-statement-tooltip {
  max-width: 360px;
  font-size: 12px;
  line-height: 15px;
}

.k-case-tree-advanced-search-last-clicked-result {
  background-color: $v-yellow-accent-4 !important;
}

.k-case-tree-search-item-tooltip {
  background-color: #eee !important;
  color: #000 !important;
  opacity: 1 !important;
  transition: none !important;
  border: 1px solid #666;
}

.k-case-tree-framework-image-wrapper {
  position: fixed;
  // z-index: -1;
  right: 20px;
  bottom: 20px;
  width: 248px;
  height: 248px;
  padding: 12px;
  border-radius: 8px;
  background-color: #fff;
  // background-color: rgba(255, 255, 255, 0.5);
  // margin: 4px 8px 0 8px;
  opacity: 0.3;
  // z-index:-1; MC: Was there a specific reason for the z-index?
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    max-width: 224px;
    max-height: 224px;
    border-radius: 6px;
    // opacity:0.2;
  }
}

.k-case-tree-top-control {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.k-case-tree-outer-wrapper:hover {
  .k-resizable-handle-br,
  .k-resizable-handle-bl,
  .k-resizable-handle-tr,
  .k-resizable-handle-tl,
  .k-resizable-handle-bm,
  .k-resizable-handle-tm,
  .k-resizable-handle-ml,
  .k-resizable-handle-mr {
    display: block !important;
  }
}

// allow for scrolling in table view
.k-case-tree-outer-wrapper--table-mode {
  .k-case-tree-main {
    .k-case-tree-inner-wrapper {
      overflow: auto;
      pointer-events: auto;
    }
  }
}

// adjustments for minimized view
.k-case-tree-outer-wrapper--minimized {
  left: 10px;
  top: 100px;
  border: 5px solid #999;
  border-radius: 10px;

  .k-case-tree-main {
    .k-case-tree-inner-wrapper {
      height: calc(100% - 100px);
    }
  }

  .k-case-tree-top {
    padding: 0;
  }

  .k-case-tree-framework-image-wrapper {
    display: none;
  }

  .k-resizable-handle-br,
  .k-resizable-handle-bl,
  .k-resizable-handle-tr,
  .k-resizable-handle-tl,
  .k-resizable-handle-bm,
  .k-resizable-handle-tm,
  .k-resizable-handle-ml,
  .k-resizable-handle-mr {
    background-color: #fff;
    border: 1px solid #000;
    position: absolute;
    width: 16px;
    height: 16px;
    // currently not showing handles, even in minimized mode
    display: none !important;
  }

  .k-resizable-handle-br {
    right: -10px;
    bottom: -10px;
    cursor: se-resize;
  }

  .k-resizable-handle-bl {
    left: -10px;
    bottom: -10px;
    cursor: sw-resize;
  }

  .k-resizable-handle-mr {
    right: -10px;
    bottom: calc(50% - 8px);
    cursor: e-resize;
  }

  .k-resizable-handle-ml {
    left: -10px;
    bottom: calc(50% - 8px);
    cursor: w-resize;
  }

  .k-resizable-handle-bm {
    right: calc(50% - 8px);
    bottom: -10px;
    cursor: s-resize;
  }
}

.k-case-tree-main {
  .k-ls-doc-preview {
    font-size: 14px;
    margin: 4px 8px 12px 8px;
    border-radius: 6px;
    color: #222;
    background-color: #fff;
    cursor: default;
    max-width: 750px;
    padding: 10px 12px;
    font-size: 15px;
    li {
      margin-top: 2px;
      margin-bottom: 2px;
    }
    a {
      font-weight: normal !important;
      text-decoration: none;
    }
  }
}

.k-ls-item-preview {
  // position:relative;
  // display:inline-block;
  font-size: 12px;
  font-weight: normal;
  margin-top: 8px;
  margin-bottom: 12px;
  border-radius: 6px;
  background-color: #fff;
  padding: 8px 8px 12px 8px;
  // min-width:600px;
  color: #000;
  cursor: default;
  li {
    margin-top: 2px;
    margin-bottom: 2px;
  }
}

// MC: i will have to refactor all of this next week

// TODO NEXT: COPY THE style_name with these!!!, going to be tough???
///////////////////////////
.k-case-tree-outer-wrapper.k-framework-color-0,
.k-editor-wrapper-outer .k-framework-color-0-editor {
  .primary {
    background-color: $k-dfc-darken-3 !important;
    border-color: $k-dfc-darken-3 !important;
  }
  .primary--text {
    color: $k-dfc-darken-3 !important;
    caret-color: $k-dfc-darken-3 !important;
  }
  border-color: $k-dfc-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$k-dfc-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $k-dfc-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $k-dfc-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $k-dfc-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $k-dfc-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $k-dfc-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $k-dfc-lighten-5;
  } // ; border-color:$k-dfc-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $k-dfc-darken-3;
  }
}
.k-case-item-editor.k-framework-color-0 {
  background-color: $k-dfc-darken-3 !important;
}
.k-framework-color-0-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $k-dfc-darken-3;
  }
}
.k-framework-color-0-svg-line line {
  stroke: $k-dfc-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-1,
.k-editor-wrapper-outer .k-framework-color-1-editor {
  .primary {
    background-color: $v-pink-darken-3 !important;
    border-color: $v-pink-darken-3 !important;
  }
  .primary--text {
    color: $v-pink-darken-3 !important;
    caret-color: $v-pink-darken-3 !important;
  }
  border-color: $v-pink-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-pink-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-pink-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-pink-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-pink-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-pink-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-pink-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-pink-lighten-5;
  } // ; border-color:$v-pink-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-pink-darken-3;
  }
}
.k-case-item-editor.k-framework-color-1 {
  background-color: $v-pink-darken-3 !important;
}
.k-framework-color-1-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-pink-darken-3 !important;
  }
}
.k-framework-color-1-svg-line line {
  stroke: $v-pink-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-2,
.k-editor-wrapper-outer .k-framework-color-2-editor {
  .primary {
    background-color: $v-deep-purple-darken-3 !important;
    border-color: $v-deep-purple-darken-3 !important;
  }
  .primary--text {
    color: $v-deep-purple-darken-3 !important;
    caret-color: $v-deep-purple-darken-3 !important;
  }
  border-color: $v-deep-purple-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-deep-purple-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-deep-purple-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-deep-purple-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-deep-purple-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-deep-purple-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-deep-purple-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-deep-purple-lighten-5;
  } // ; border-color:$v-deep-purple-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-deep-purple-darken-3;
  }
}
.k-case-item-editor.k-framework-color-2 {
  background-color: $v-deep-purple-darken-3 !important;
}
.k-framework-color-2-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-deep-purple-darken-3 !important;
  }
}
.k-framework-color-2-svg-line line {
  stroke: $v-deep-purple-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-3,
.k-editor-wrapper-outer .k-framework-color-3-editor {
  .primary {
    background-color: $v-indigo-darken-3 !important;
    border-color: $v-indigo-darken-3 !important;
  }
  .primary--text {
    color: $v-indigo-darken-3 !important;
    caret-color: $v-indigo-darken-3 !important;
  }
  border-color: $v-indigo-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-indigo-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-indigo-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-indigo-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-indigo-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-indigo-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-indigo-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-indigo-lighten-5;
  } // ; border-color:$v-indigo-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-indigo-darken-3;
  }
}
.k-case-item-editor.k-framework-color-3 {
  background-color: $v-indigo-darken-3 !important;
}
.k-framework-color-3-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-indigo-darken-3 !important;
  }
}
.k-framework-color-3-svg-line line {
  stroke: $v-indigo-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-4,
.k-editor-wrapper-outer .k-framework-color-4-editor {
  .primary {
    background-color: $v-blue-darken-3 !important;
    border-color: $v-blue-darken-3 !important;
  }
  .primary--text {
    color: $v-blue-darken-3 !important;
    caret-color: $v-blue-darken-3 !important;
  }
  border-color: $v-blue-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-blue-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-blue-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-blue-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-blue-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-blue-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-blue-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-blue-lighten-5;
  } // ; border-color:$v-blue-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-blue-darken-3;
  }
}
.k-case-item-editor.k-framework-color-4 {
  background-color: $v-blue-darken-3 !important;
}
.k-framework-color-4-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-blue-darken-3 !important;
  }
}
.k-framework-color-4-svg-line line {
  stroke: $v-blue-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-5,
.k-editor-wrapper-outer .k-framework-color-5-editor {
  .primary {
    background-color: $v-cyan-darken-3 !important;
    border-color: $v-cyan-darken-3 !important;
  }
  .primary--text {
    color: $v-cyan-darken-3 !important;
    caret-color: $v-cyan-darken-3 !important;
  }
  border-color: $v-cyan-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-cyan-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-cyan-darken-3;
  }
  // .k-cfv-toolbar { background-color:$v-cyan-lighten-5; }
  .k-case-tree-item-being-edited {
    border-color: $v-cyan-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-cyan-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-cyan-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-cyan-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-cyan-lighten-5;
  } // ; border-color:$v-cyan-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-cyan-darken-3;
  }
}
.k-case-item-editor.k-framework-color-5 {
  background-color: $v-cyan-darken-3 !important;
}
.k-framework-color-5-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-cyan-darken-3 !important;
  }
}
.k-framework-color-5-svg-line line {
  stroke: $v-cyan-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-6,
.k-editor-wrapper-outer .k-framework-color-6-editor {
  .primary {
    background-color: $v-lime-darken-4 !important;
    border-color: $v-lime-darken-4 !important;
  }
  .primary--text {
    color: $v-lime-darken-4 !important;
    caret-color: $v-lime-darken-4 !important;
  }
  border-color: $v-lime-darken-4;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-lime-darken-4; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-lime-darken-4;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-lime-darken-4 !important;
    .k-case-tree-item-node-btn {
      color: $v-lime-darken-4 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-lime-darken-4;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-lime-darken-4;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-lime-lighten-5;
  } // ; border-color:$v-lime-darken-4; }
  .k-case-tree-item-tile-outer {
    border-color: $v-lime-darken-4;
  }
}
.k-case-item-editor.k-framework-color-6 {
  background-color: $v-lime-darken-4 !important;
}
.k-framework-color-6-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-lime-darken-4 !important;
  }
}
.k-framework-color-6-svg-line line {
  stroke: $v-lime-darken-4 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-7,
.k-editor-wrapper-outer .k-framework-color-7-editor {
  .primary {
    background-color: $v-brown-darken-3 !important;
    border-color: $v-brown-darken-3 !important;
  }
  .primary--text {
    color: $v-brown-darken-3 !important;
    caret-color: $v-brown-darken-3 !important;
  }
  border-color: $v-brown-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-brown-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-brown-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-brown-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-brown-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-brown-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-brown-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-brown-lighten-5;
  } // ; border-color:$v-brown-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-brown-darken-3;
  }
}
.k-case-item-editor.k-framework-color-7 {
  background-color: $v-brown-darken-3 !important;
}
.k-framework-color-7-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-brown-darken-3 !important;
  }
}
.k-framework-color-7-svg-line line {
  stroke: $v-brown-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-8,
.k-editor-wrapper-outer .k-framework-color-8-editor {
  .primary {
    background-color: $v-purple-darken-3 !important;
    border-color: $v-purple-darken-3 !important;
  }
  .primary--text {
    color: $v-purple-darken-3 !important;
    caret-color: $v-purple-darken-3 !important;
  }
  border-color: $v-purple-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-purple-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-purple-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-purple-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-purple-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-purple-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-purple-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-purple-lighten-5;
  } // ; border-color:$v-purple-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-purple-darken-3;
  }
}
.k-case-item-editor.k-framework-color-8 {
  background-color: $v-purple-darken-3 !important;
}
.k-framework-color-8-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-purple-darken-3 !important;
  }
}
.k-framework-color-8-svg-line line {
  stroke: $v-purple-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-9,
.k-editor-wrapper-outer .k-framework-color-9-editor {
  .primary {
    background-color: $v-light-blue-darken-3 !important;
    border-color: $v-light-blue-darken-3 !important;
  }
  .primary--text {
    color: $v-light-blue-darken-3 !important;
    caret-color: $v-light-blue-darken-3 !important;
  }
  border-color: $v-light-blue-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-light-blue-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-light-blue-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-light-blue-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-light-blue-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-light-blue-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-light-blue-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-light-blue-lighten-5;
  } // ; border-color:$v-light-blue-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-light-blue-darken-3;
  }
}
.k-case-item-editor.k-framework-color-9 {
  background-color: $v-light-blue-darken-3 !important;
}
.k-framework-color-9-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-light-blue-darken-3 !important;
  }
}
.k-framework-color-9-svg-line line {
  stroke: $v-light-blue-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-10,
.k-editor-wrapper-outer .k-framework-color-10-editor {
  .primary {
    background-color: $v-teal-darken-3 !important;
    border-color: $v-teal-darken-3 !important;
  }
  .primary--text {
    color: $v-teal-darken-3 !important;
    caret-color: $v-teal-darken-3 !important;
  }
  border-color: $v-teal-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-teal-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-teal-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-teal-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-teal-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-teal-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-teal-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-teal-lighten-5;
  } // ; border-color:$v-teal-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-teal-darken-3;
  }
}
.k-case-item-editor.k-framework-color-10 {
  background-color: $v-teal-darken-3 !important;
}
.k-framework-color-10-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-teal-darken-3 !important;
  }
}
.k-framework-color-10-svg-line line {
  stroke: $v-teal-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-11,
.k-editor-wrapper-outer .k-framework-color-11-editor {
  .primary {
    background-color: $v-green-darken-3 !important;
    border-color: $v-green-darken-3 !important;
  }
  .primary--text {
    color: $v-green-darken-3 !important;
    caret-color: $v-green-darken-3 !important;
  }
  border-color: $v-green-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-green-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-green-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-green-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-green-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-green-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-green-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-green-lighten-5;
  } // ; border-color:$v-green-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-green-darken-3;
  }
}
.k-case-item-editor.k-framework-color-11 {
  background-color: $v-green-darken-3 !important;
}
.k-framework-color-11-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-green-darken-3 !important;
  }
}
.k-framework-color-11-svg-line line {
  stroke: $v-green-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-12,
.k-editor-wrapper-outer .k-framework-color-12-editor {
  .primary {
    background-color: $v-red-darken-3 !important;
    border-color: $v-red-darken-3 !important;
  }
  .primary--text {
    color: $v-red-darken-3 !important;
    caret-color: $v-red-darken-3 !important;
  }
  border-color: $v-red-darken-3;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-red-darken-3; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-red-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-red-darken-3 !important;
    .k-case-tree-item-node-btn {
      color: $v-red-darken-3 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-red-darken-3;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-red-darken-3;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-red-lighten-5;
  } // ; border-color:$v-red-darken-3; }
  .k-case-tree-item-tile-outer {
    border-color: $v-red-darken-3;
  }
}
.k-case-item-editor.k-framework-color-12 {
  background-color: $v-red-darken-3 !important;
}
.k-framework-color-12-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-red-darken-3 !important;
  }
}
.k-framework-color-12-svg-line line {
  stroke: $v-red-darken-3 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-13,
.k-editor-wrapper-outer .k-framework-color-13-editor {
  .primary {
    background-color: $v-orange-darken-4 !important;
    border-color: $v-orange-darken-4 !important;
  }
  .primary--text {
    color: $v-orange-darken-4 !important;
    caret-color: $v-orange-darken-4 !important;
  }
  border-color: $v-orange-darken-4;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-orange-darken-4; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-orange-darken-4;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-orange-darken-4 !important;
    .k-case-tree-item-node-btn {
      color: $v-orange-darken-4 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-orange-darken-4;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-orange-darken-4;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-orange-lighten-5;
  } // ; border-color:$v-orange-darken-4; }
  .k-case-tree-item-tile-outer {
    border-color: $v-orange-darken-4;
  }
}
.k-case-item-editor.k-framework-color-13 {
  background-color: $v-orange-darken-4 !important;
}
.k-framework-color-13-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-orange-darken-4 !important;
  }
}
.k-framework-color-13-svg-line line {
  stroke: $v-orange-darken-4 !important;
}

.k-case-tree-outer-wrapper.k-framework-color-14,
.k-editor-wrapper-outer .k-framework-color-14-editor {
  .primary {
    background-color: $v-grey-darken-4 !important;
    border-color: $v-grey-darken-4 !important;
  }
  .primary--text {
    color: $v-grey-darken-4 !important;
    caret-color: $v-grey-darken-4 !important;
  }
  border-color: $v-grey-darken-4;
  // .k-case-tree-item-descendant-or-ancestor-of-last-clicked { color:$v-grey-darken-4; }
  .k-case-tree-top,
  .k-case-item-editor-title {
    background-color: $v-grey-darken-3;
  }
  .k-case-tree-item-being-edited {
    border-color: $v-grey-darken-4 !important;
    .k-case-tree-item-node-btn {
      color: $v-grey-darken-4 !important;
    }
  }
  .k-case-graphic-tree-node {
    background-color: $v-grey-darken-4;
  }
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-grey-darken-4;
  }
  .k-case-tree-tile-view .k-case-tree-item-last-clicked {
    background-color: $v-grey-lighten-5;
  } // ; border-color:$v-grey-darken-4; }
  .k-case-tree-item-tile-outer {
    border-color: $v-grey-darken-4;
  }
}
.k-case-item-editor.k-framework-color-14 {
  background-color: $v-grey-darken-4 !important;
}
.k-framework-color-14-editor {
  .k-case-tree-item-last-clicked > .k-case-tree-item {
    border-color: $v-grey-darken-4 !important;
  }
}
.k-framework-color-14-svg-line line {
  stroke: $v-grey-darken-4 !important;
}

.k-case-archive-view-ctl {
  background-color: $v-orange-darken-3;
  color: #fff;
  position: fixed !important;
  z-index: 3; // needs to be above the k-case-tree-pinned-items-wrapper
  left: 0;
  bottom: 0;
  width: 100%;
  font-size: 14px;
  line-height: 18px;
  padding: 4px 8px;
  // font-weight:bold;
  display: flex;
  align-items: center;
  // height: 100px;
}

.k-case-archive-view-ctl-current {
  background-color: $v-cyan-darken-3;
}

// adjustments for embedded mode
.k-case-tree-outer-wrapper-embedded {
  .k-case-tree-top {
    padding-right: 12px;
    padding-left: 12px;
  }
}

// adjustments for wiki mode
.k-case-tree-outer-wrapper-wiki-mode {
  .k-case-tree-scroll-wrapper {
    max-width: 600px;
  }

  .k-case-tree-active-item-wrapper {
    left: 608px;
  }

  .k-case-tree-item-tile-outer {
    max-width: calc(100vw - 620px);
    .k-case-tree-item-details-exemplar {
      .fr-view {
        font-size: 15px;
        line-height: 19px;
      }
    }
  }
}

.k-case-tree-outer-wrapper-wiki-mode.k-case-tree-outer-wrapper {
  .k-case-tree-top {
    .k-case-tree-title {
      // the user guide title should always be short; lock font-size at relatively big
      .k-case-tree-title-inner {
        white-space: normal;
        font-size: 18px !important;
        line-height: 18px !important;
        margin-top: 3px;
      }
    }
  }
}

.k-editor-wrapper-outer-wiki-mode {
  left: 608px;
  .k-editor-wrapper {
    max-width: calc(100vw - 616px);
  }
}

// further adjustments when we're in wiki mode and not in edit mode
.k-case-tree-outer-wrapper-wiki-mode-view {
  .k-case-tree-search-results {
    right: 48px !important;
  }

  /////////////////////////////
  // TREE
  .k-case-tree-scroll-wrapper {
    width: 300px;
    padding: 0;
    border-right: 1px solid $v-light-blue-darken-4;
  }
  .k-cfv-toolbar {
    display: none;
  }
  .k-case-tree {
    background-color: $v-light-blue-lighten-5;
    // background-color:transparent!important;
    border-radius: 0;
    box-shadow: none !important;
    padding: 0;
    overflow-x: hidden;
    height: calc(100vh - 108px);
    padding: 4px 4px 4px 0;
  }
  .k-case-tree-top-children-wrapper {
    // margin-left:-22px;
  }
  .k-case-tree-item-document {
    display: none;
  }
  .k-case-tree-title-inner {
    margin-left: 8px;
  }

  .k-case-tree-item-outer {
    background-color: $v-light-blue-lighten-5;
    border-radius: 0px;
    button.grey--text.text--darken-2 {
      color: inherit !important;
    }
    .k-case-tree-item-code-plus-statement {
      color: inherit;
      b.grey--text {
        color: inherit !important;
      }
    }
  }

  .k-case-tree-item {
    border-radius: 4px;
    margin-left: 0px;
    background-color: $v-light-blue-lighten-5;
    padding: 0;
    color: #000 !important;
    .k-case-tree-item-inner {
      font-size: 14px;
      line-height: 17px;
    }
    .k-case-tree-item-node-btn {
      width: 13px !important;
      height: 18px !important;
      margin-top: 0px;
      margin-left: 2px;

      i {
        font-size: 13px !important;
      }
    }
    .k-case-tree-item-hcs {
      display: none;
    }
  }

  .k-case-tree-item-children {
    margin-left: 8px;
  }

  .k-case-tree-framework-image-wrapper {
    display: none;
  }

  /////////////////////////////
  // TILE
  .k-case-tree-active-item-wrapper {
    left: 300px;
    top: 107px;
    height: calc(100vh - 107px);
  }

  .k-case-tree-item-tile-outer-has-supp-notes {
    width: 800px;
  }

  .k-case-tree-item-tile-outer {
    border-left: 0;
    border-top: 0;
    border-bottom: 0;
    border-color: $v-light-blue-darken-4;
    border-radius: 0;
    box-shadow: none !important;
    padding: 0 4px;
    height: calc(100vh - 107px);
    max-width: calc(100vw - 300px);

    .k-copy-component-btn {
      display: none;
    }
    .k-case-tree-item-tile-close-btn {
      display: none;
    }
    .k-case-tree-item-statement-hcs {
      display: none;
    }
    .k-case-tree-item-tile-pin-btn {
      display: none;
    }
    .k-case-tree-item-tile-expand-btn {
      display: none;
    }
    .k-tile-comments-wrapper {
      display: none;
    }
    .k-case-tree-item-details-inner {
      display: none;
    }
    .k-case-tree-item-details-identifier {
      display: none;
    }
    .k-case-tree-item-tile-search-children-div {
      display: none;
    }

    .k-case-tree-item-node-btn {
      width: 20px !important;
      height: 20px !important;
      margin-top: 2px;
      i {
        font-size: 17px !important;
        color: #000 !important;
      }
    }
    .k-case-tree-item-statement {
      font-weight: bold;
      font-size: 18px;
      line-height: 22px;
    }

    .k-case-tree-item-details-exemplar {
      border: 0;
      margin-top: 8px;
      padding: 0;
      .fr-view {
      }
    }

    .k-case-tree-item-details-exemplar-header {
      display: none;
    }
  }

  .k-case-tree-item-tile {
    padding: 0;
    border-radius: 0;
    border: 0;
    max-height: calc(100vh - 107px);
  }

  .k-case-tree-search-panel {
    .k-case-tree-search-options {
      display: none !important;
    }
    .k-copy-component-btn {
      display: none;
    }

    .k-case-tree-search-results-panel {
      width: 380px;
      border-top: 0;
      margin-top: 0;
    }
  }
}

// when wiki is embedded, adjust for lack of top banner
.k-app-embedded-true {
  .k-case-tree-outer-wrapper-wiki-mode-view {
    .k-case-tree {
      height: calc(100vh - 47px);
    }

    .k-case-tree-active-item-wrapper {
      top: 47px;
      height: calc(100vh - 47px);
    }

    .k-case-tree-item-tile-outer {
      height: calc(100vh - 47px);

      .k-case-tree-item-details-exemplar .fr-view {
        font-size: 14px;
        padding-left: 4px;
        padding-right: 4px;
      }
    }

    .k-case-tree-item-tile {
      max-height: calc(100vh - 47px);
    }
  }
}

// when wiki is narrower, reduce width of TOC
@media (max-width: 960px) {
  .k-case-tree-outer-wrapper-wiki-mode-view {
    .k-case-tree-scroll-wrapper {
      max-height: calc(100vh - 47px);
    }

    // also have to adjust for lack of top banner at this point
    .k-case-tree {
      height: calc(100vh - 47px);
    }

    .k-case-tree-item {
      .k-case-tree-item-inner {
        font-size: 12px;
        line-height: 14px;
      }
      .k-case-tree-item-node-btn {
        width: 10px !important;
        height: 16px !important;
        margin-top: -1px;
        margin-left: 2px;

        i {
          font-size: 10px !important;
        }
      }
      .k-case-tree-item-hcs {
        display: none;
      }
    }

    .k-case-tree-active-item-wrapper {
      left: 240px;
      top: 47px;
      height: calc(100vh - 47px);
    }

    .k-case-tree-item-tile-outer {
      height: calc(100vh - 47px);
      max-width: calc(100vw - 240px);
    }

    .k-case-tree-item-tile {
      max-height: calc(100vh - 47px);
    }
    .k-case-tree-scroll-wrapper {
      width: 240px;
    }
  }
}

// when wiki is narrow and embedded, don't show TOC at all
@media (max-width: 600px) {
  .k-case-tree-outer-wrapper-wiki-mode-view {
    .k-case-tree {
      display: none;
    }
    .k-case-tree-active-item-wrapper {
      left: 0 !important;
    }
    .k-case-tree-item-tile-outer {
      max-width: 100vw !important;
      width: 100vw !important;
      left: 0 !important;
      border-right: 0 !important;
    }
  }
}

.k-tile-mode-to-tree-mode-btn {
  position: fixed;
  right: 8px;
  top: 114px;
}

.k-case-tree-inline-btn {
	background: transparent;
	border: 0;
	color: inherit;
	cursor: pointer;
	padding: 0;
	text-decoration: underline;
}

.k-ap-spreadsheet-export {
  border-collapse: collapse;
  padding-right: 16px;
  td,
  th {
    font-size: 12px;
    line-height: 12px;
    padding: 2px;
    border: 1px solid #ccc;
    max-width: 120px;
    min-width: 120px;
    height: 16px;
    white-space: nowrap;
    overflow: auto;
  }
  th {
    font-weight: bold;
    background-color: #333;
    color: #fff;
    text-align: left;
  }
}
</style>
